import { useState, useEffect } from 'react'
import { FiX, FiLoader, FiSave, FiTrash2, FiUpload } from 'react-icons/fi'
import toast from 'react-hot-toast'
import axios from 'axios'

const getLifecycleLabel = (customDomain = {}) => {
  const lifecycleStatus = customDomain.lifecycleStatus || customDomain.status || 'pending_verification'
  const sslStatus = customDomain.sslStatus || 'pending'
  const verified = Boolean(customDomain.verified)

  if (lifecycleStatus === 'active' || (verified && sslStatus === 'active')) {
    return { label: 'Active', tone: 'green' }
  }

  if (lifecycleStatus === 'failed' || sslStatus === 'failed') {
    return { label: 'Failed', tone: 'red' }
  }

  if (lifecycleStatus === 'ssl_provisioning' || lifecycleStatus === 'configuration_valid' || lifecycleStatus === 'ssl_issued') {
    return { label: 'SSL provisioning', tone: 'yellow' }
  }

  if (lifecycleStatus === 'vercel_registered' || lifecycleStatus === 'dns_verified') {
    return { label: 'Waiting for Vercel', tone: 'yellow' }
  }

  if (verified) {
    return { label: 'DNS verified', tone: 'blue' }
  }

  if (customDomain.failureReason) {
    return { label: 'Failed', tone: 'red' }
  }

  return { label: 'Waiting for DNS', tone: 'blue' }
}

const normalizeDomainValue = (value = '') => {
  if (!value || typeof value !== 'string') return ''

  const trimmed = value.trim().toLowerCase()
  if (!trimmed) return ''

  const withoutProtocol = trimmed.replace(/^https?:\/\//, '')
  const withoutPort = withoutProtocol.split(':')[0]
  const withoutTrailingDot = withoutPort.endsWith('.') ? withoutPort.slice(0, -1) : withoutPort

  return withoutTrailingDot
}

const isApexDomain = (value = '') => {
  const normalized = normalizeDomainValue(value)
  if (!normalized) return false
  if (normalized.startsWith('www.')) return false

  const labels = normalized.split('.')
  return labels.length <= 2
}

const getRequiredApexRecords = () => [
  { type: 'A', name: '@', value: '76.76.21.22' },
  { type: 'A', name: '@', value: '76.76.21.21' }
]

export default function WebsiteEditorModal({ isOpen, onClose, onSubmit, website, categories, isSaving, mode, onLogoUpload, onLogoDelete }) {
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    metaTitle: '',
    metaDescription: '',
    metaKeywords: '',
    whatsapp: '',
    instagram: '',
    facebook: '',
    tiktok: '',
    twitter: '',
    primaryColor: '#3B82F6',
    secondaryColor: '#1F2937',
    accentColor: '#F97316',
    primaryFont: 'Inter',
    secondaryFont: 'Inter',
    bodyBackgroundColor: '#FFFFFF',
    bodyHoverBackgroundColor: '#F3F4F6',
    customerAuthEnabled: false,
    loginButtonText: 'Login',
    signupButtonText: 'Sign Up',
    enableForgotPassword: true,
    requireEmailVerification: false,
    allowSocialLogin: false
  })
  const [logoFile, setLogoFile] = useState(null)
  const [logoPreview, setLogoPreview] = useState(null)
  const [uploadingLogo, setUploadingLogo] = useState(false)

   // ✅ Proper local domain state for custom domain section
  const [customDomainInput, setCustomDomainInput] = useState('')
  const [domainAdding, setDomainAdding] = useState(false)
  const [domainVerifying, setDomainVerifying] = useState(false)
  const [domainRemoving, setDomainRemoving] = useState(false)
  const [frontendHostname, setFrontendHostname] = useState('')
  const [seoStatus, setSeoStatus] = useState(null)
  const [loadingSeoStatus, setLoadingSeoStatus] = useState(false)
  const [refreshingSeoStatus, setRefreshingSeoStatus] = useState(false)

  // Helper function to convert relative logo paths to absolute URLs
  const getAbsoluteLogoUrl = (logoUrl) => {
    if (!logoUrl) return null
    
    // If already absolute, return as-is
    if (logoUrl.startsWith('http://') || logoUrl.startsWith('https://')) {
      return logoUrl
    }
    
    // Convert relative path to absolute using backend origin
    const backendOrigin = axios.defaults.baseURL 
      ? axios.defaults.baseURL.replace(/\/api\/?$/, '') 
      : ''
    
    if (!backendOrigin) {
      console.warn('[Logo URL] Cannot determine backend origin, using relative URL:', logoUrl)
      return logoUrl
    }
    
    const pathUrl = logoUrl.startsWith('/') ? logoUrl : `/${logoUrl}`
    const absoluteUrl = `${backendOrigin}${pathUrl}`
    console.log('[Logo URL] Converted relative to absolute:', { original: logoUrl, absolute: absoluteUrl })
    return absoluteUrl
  }

  useEffect(() => {
    if (website && isOpen) {
      setFormData({
        name: website.name || '',
        description: website.description || '',
        metaTitle: website.seo?.metaTitle || '',
        metaDescription: website.seo?.metaDescription || '',
        metaKeywords: website.seo?.metaKeywords || '',
        whatsapp: website.socialLinks?.whatsapp || '',
        instagram: website.socialLinks?.instagram || '',
        facebook: website.socialLinks?.facebook || '',
        tiktok: website.socialLinks?.tiktok || '',
        twitter: website.socialLinks?.twitter || '',
        primaryColor: website.colors?.primary || '#3B82F6',
        secondaryColor: website.colors?.secondary || '#1F2937',
        accentColor: website.colors?.accent || '#F97316',
        primaryFont: website.fonts?.primary || 'Inter',
        secondaryFont: website.fonts?.secondary || 'Inter',
        bodyBackgroundColor: website.bodyStyling?.backgroundColor || '#FFFFFF',
        bodyHoverBackgroundColor: website.bodyStyling?.hoverBackgroundColor || '#F3F4F6',
        customerAuthEnabled: website.customerAuth?.enabled ?? false,
        loginButtonText: website.customerAuth?.loginButtonText || 'Login',
        signupButtonText: website.customerAuth?.signupButtonText || 'Sign Up',
        enableForgotPassword: website.customerAuth?.enableForgotPassword ?? true,
        requireEmailVerification: website.customerAuth?.requireEmailVerification ?? false,
        allowSocialLogin: website.customerAuth?.allowSocialLogin ?? false
      })
      
      // Display the saved/uploaded logo from backend if it exists
      if (website.logo?.url) {
        const absoluteUrl = getAbsoluteLogoUrl(website.logo.url)
        setLogoPreview(absoluteUrl)
        console.log('[WebsiteEditor] Showing saved logo from backend:', absoluteUrl)
      } else {
        setLogoPreview(null)
        console.log('[WebsiteEditor] No saved logo found')
      }
      
      setLogoFile(null)
    }
    // Fetch SEO status when modal opens for the website
    const fetchSeo = async () => {
      if (!website?._id) return
      try {
        setLoadingSeoStatus(true)
        const resp = await axios.get(`/websites/${website._id}/seo-status`)
        setSeoStatus(resp.data?.seoStatus || null)
      } catch (err) {
        console.warn('[SEO] Failed to fetch seo-status', err?.response?.data || err.message)
        setSeoStatus(null)
      } finally {
        setLoadingSeoStatus(false)
      }
    }

    if (isOpen && website?._id) fetchSeo()
  }, [website, isOpen])

  const getSeoStatusText = (field) => {
    if (loadingSeoStatus || refreshingSeoStatus || seoStatus?.checking) return 'Checking...'
    if (!seoStatus || !seoStatus.lastCheckedAt || seoStatus.accessibilityStatus === 'unknown') return 'Not checked'

    if (field === 'online') {
      return seoStatus.accessibilityStatus === 'online' ? '🟢 Online' : '🔴 Offline'
    }

    if (field === 'robots') {
      return seoStatus.robotsStatus === 'available' ? '🟢 Available' : '🔴 Missing'
    }

    if (field === 'sitemap') {
      return seoStatus.sitemapStatus === 'available' ? '🟢 Available' : '🔴 Missing'
    }

    return 'Not checked'
  }

  const lastCheckedLabel = () => {
    if (loadingSeoStatus || refreshingSeoStatus || seoStatus?.checking) return 'Checking...'
    if (!seoStatus?.lastCheckedAt) return 'Not checked'
    return new Date(seoStatus.lastCheckedAt).toLocaleString()
  }

  const refreshSeoStatus = async () => {
    if (!website?._id) return
    setRefreshingSeoStatus(true)
    try {
      const resp = await axios.post(`/websites/${website._id}/seo-status/check-now`)
      const updatedStatus = resp.data?.seoStatus || null
      setSeoStatus(updatedStatus)

      if (updatedStatus?.checking) {
        toast.success('SEO check started. Refresh will update when complete.')
      } else {
        toast.success('SEO status refreshed successfully.')
      }
    } catch (err) {
      const errorMessage = err?.response?.data?.message || err?.message || 'Failed to refresh SEO status'
      toast.error(errorMessage)
      console.warn('[SEO] Failed to refresh seo-status', err?.response?.data || err.message)
    } finally {
      setRefreshingSeoStatus(false)
    }
  }

  // body styling fields
  useEffect(() => {
    if (website && isOpen) {
      setFormData(prev => ({
        ...prev,
        bodyBackgroundColor: website.bodyStyling?.backgroundColor || '#FFFFFF',
        bodyHoverBackgroundColor: website.bodyStyling?.hoverBackgroundColor || '#F3F4F6'
      }))
    }
  }, [website, isOpen])

  const handleInputChange = (e) => {
    const { name, value } = e.target
    setFormData(prev => ({
      ...prev,
      [name]: value
    }))
  }

  const handleColorChange = (colorField, value) => {
    setFormData(prev => ({
      ...prev,
      [colorField]: value
    }))
  }

  const handleLogoChange = (e) => {
    const file = e.target.files?.[0]
    if (!file) return

    if (!file.type.startsWith('image/')) {
      toast.error('Please select an image file')
      return
    }

    if (file.size > 5 * 1024 * 1024) {
      toast.error('File size must be less than 5MB')
      return
    }

    // Store the file for upload - don't show preview until upload completes
    setLogoFile(file)
    console.log('[WebsiteEditor] File selected for upload:', file.name)
  }

  const handleUploadLogo = async () => {
    if (!logoFile || !website?._id) {
      toast.error('No logo file selected')
      return
    }

    try {
      setUploadingLogo(true)
      const formDataUpload = new FormData()
      formDataUpload.append('logo', logoFile)

      console.log('[Logo Upload] Starting upload for website:', website._id)
      console.log('[Logo Upload] File:', logoFile.name, 'Size:', logoFile.size, 'Type:', logoFile.type)

      const response = await axios.post(`/websites/${website._id}/logo`, formDataUpload, {
        headers: {
          'Content-Type': 'multipart/form-data'
        },
        timeout: 30000 // 30 second timeout
      })

      console.log('[Logo Upload] Response received:', response.status)
      console.log('[Logo Upload] Response data:', response.data)
      
      const returnedWebsite = response.data?.website
      const returnedLogoUrl = returnedWebsite?.logo?.url
      
      console.log('[Logo Upload] Returned website:', returnedWebsite ? 'YES' : 'NO')
      console.log('[Logo Upload] Returned logo URL:', returnedLogoUrl)
      
      if (returnedLogoUrl) {
        // Validate the URL format
        if (!returnedLogoUrl.startsWith('/') && !returnedLogoUrl.startsWith('http')) {
          console.warn('[Logo Upload] WARNING: URL does not start with / or http:', returnedLogoUrl)
        }
        
        // Convert relative URL to absolute for image loading in browser
        const absoluteUrl = getAbsoluteLogoUrl(returnedLogoUrl)
        setLogoPreview(absoluteUrl)
        console.log('[Logo Upload] Logo preview set to:', absoluteUrl)
        toast.success('✅ Logo uploaded successfully! It will appear on your public website, replacing the website name.')
        
        // Call parent callback to update website data with the uploaded logo
        if (onLogoUpload && returnedWebsite) {
          try {
            onLogoUpload(returnedWebsite)
            console.log('[Logo Upload] Called onLogoUpload callback successfully')
          } catch (callbackError) {
            console.error('[Logo Upload] Error in onLogoUpload callback:', callbackError)
            // Don't fail the whole operation if callback fails
          }
        } else {
          console.warn('[Logo Upload] onLogoUpload callback not available or returnedWebsite missing')
        }
      } else {
        console.error('[Logo Upload] Logo URL is missing from response')
        console.error('[Logo Upload] Full response:', JSON.stringify(response.data))
        toast.error('Upload succeeded but logo URL is missing from server response')
      }
      
      // Clear file input after successful upload
      setLogoFile(null)
    } catch (error) {
      console.error('[Logo Upload] Error occurred:')
      console.error('[Logo Upload] Error message:', error.message)
      console.error('[Logo Upload] Error code:', error.code)
      console.error('[Logo Upload] Status:', error.response?.status)
      console.error('[Logo Upload] Status text:', error.response?.statusText)
      console.error('[Logo Upload] Response data:', error.response?.data)
      console.error('[Logo Upload] Full error:', error)
      
      let errorMessage = 'Failed to upload logo'
      if (error.response?.data?.message) {
        errorMessage = error.response.data.message
      } else if (error.code === 'ECONNABORTED') {
        errorMessage = 'Upload timeout - please try again'
      } else if (error.message) {
        errorMessage = error.message
      }
      
      toast.error(errorMessage)
    } finally {
      setUploadingLogo(false)
    }
  }

  const handleDeleteLogo = async () => {
    if (!website?._id) return

    if (!window.confirm('Are you sure you want to delete the logo?')) return

    try {
      setUploadingLogo(true)
      console.log('[Logo Delete] Starting delete for website:', website._id)
      
      const response = await axios.delete(`/websites/${website._id}/logo`, {
        timeout: 30000 // 30 second timeout
      })
      
      console.log('[Logo Delete] Response received:', response.status)
      console.log('[Logo Delete] Response data:', response.data)
      
      const updatedWebsite = response.data?.website
      console.log('[Logo Delete] Updated website:', updatedWebsite ? 'YES' : 'NO')
      
      toast.success('Logo deleted successfully')
      setLogoPreview(null)
      setLogoFile(null)
      
      // Call parent callback to update website data after logo deletion
      if (onLogoDelete && updatedWebsite) {
        try {
          onLogoDelete(updatedWebsite)
          console.log('[Logo Delete] Called onLogoDelete callback successfully')
        } catch (callbackError) {
          console.error('[Logo Delete] Error in onLogoDelete callback:', callbackError)
          // Don't fail the whole operation if callback fails
        }
      } else {
        console.warn('[Logo Delete] onLogoDelete callback not available or updatedWebsite missing')
      }
    } catch (error) {
      console.error('[Logo Delete] Error occurred:')
      console.error('[Logo Delete] Error message:', error.message)
      console.error('[Logo Delete] Status:', error.response?.status)
      console.error('[Logo Delete] Response data:', error.response?.data)
      
      let errorMessage = 'Failed to delete logo'
      if (error.response?.data?.message) {
        errorMessage = error.response.data.message
      } else if (error.code === 'ECONNABORTED') {
        errorMessage = 'Delete timeout - please try again'
      }
      
      toast.error(errorMessage)
    } finally {
      setUploadingLogo(false)
    }
  }

  const handleSubmit = (e) => {
    e.preventDefault()
    
    if (!formData.name.trim()) {
      toast.error('Website name is required')
      return
    }

    if (!website || !website._id) {
      toast.error('Invalid website - please select a website to edit')
      return
    }

    const submitData = {
      name: formData.name.trim(),
      description: formData.description,
      seo: {
        metaTitle: formData.metaTitle,
        metaDescription: formData.metaDescription,
        metaKeywords: formData.metaKeywords
      },
      socialLinks: {
        whatsapp: formData.whatsapp,
        instagram: formData.instagram,
        facebook: formData.facebook,
        tiktok: formData.tiktok,
        twitter: formData.twitter
      },
      colors: {
        primary: formData.primaryColor,
        secondary: formData.secondaryColor,
        accent: formData.accentColor
      },
      fonts: {
        primary: formData.primaryFont,
        secondary: formData.secondaryFont
      },
      customerAuth: {
        enabled: formData.customerAuthEnabled,
        loginButtonText: formData.loginButtonText.trim(),
        signupButtonText: formData.signupButtonText.trim(),
        enableForgotPassword: formData.enableForgotPassword,
        requireEmailVerification: formData.requireEmailVerification,
        allowSocialLogin: formData.allowSocialLogin
      }
    }

    // include body styling if present
    if (formData.bodyBackgroundColor !== undefined || formData.bodyHoverBackgroundColor !== undefined) {
      submitData.bodyStyling = {
        backgroundColor: formData.bodyBackgroundColor,
        hoverBackgroundColor: formData.bodyHoverBackgroundColor
      }
    }

    onSubmit(submitData)
  }

  if (!isOpen) return null

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="flex justify-between items-center p-6 border-b border-gray-200 sticky top-0 bg-white">
          <h2 className="text-2xl font-bold text-gray-900">Edit Website</h2>
          <div className="flex items-center gap-4">
            <div className="text-sm text-gray-700 bg-gray-50 p-2 rounded">
              <div className="font-medium">SEO Status</div>
              <div className="flex flex-col gap-2">
                <div>Website Accessibility: {getSeoStatusText('online')}</div>
                <div>robots.txt: {getSeoStatusText('robots')}</div>
                <div>sitemap: {getSeoStatusText('sitemap')}</div>
                <div className="text-xs text-gray-500">Last check: {lastCheckedLabel()}</div>
                <button
                  type="button"
                  onClick={refreshSeoStatus}
                  disabled={refreshingSeoStatus || loadingSeoStatus || seoStatus?.checking}
                  className="inline-flex items-center gap-2 px-3 py-1 text-sm font-medium text-white bg-blue-600 rounded hover:bg-blue-700 disabled:opacity-50"
                >
                  {refreshingSeoStatus ? (
                    <>
                      <FiLoader className="animate-spin" /> Refreshing
                    </>
                  ) : (
                    'Check Now'
                  )}
                </button>
              </div>
            </div>
            <button
              onClick={onClose}
              disabled={isSaving}
              className="p-1 hover:bg-gray-100 rounded-lg transition-colors disabled:opacity-50"
            >
              <FiX className="w-6 h-6" />
            </button>
          </div>
        </div>

        {/* Content */}
        <form onSubmit={handleSubmit} className="p-6 space-y-6">
          {/* Basic Information */}
          <div className="space-y-4">
            <h3 className="text-lg font-semibold text-gray-900">Basic Information</h3>
            
            <div>
              <label htmlFor="website-name" className="block text-sm font-medium text-gray-700 mb-2">
                Website Name *
              </label>
              <input
                id="website-name"
                type="text"
                name="name"
                value={formData.name}
                onChange={handleInputChange}
                placeholder="Enter website name"
                className="input-field w-full"
                required
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Description
              </label>
              <textarea
                name="description"
                value={formData.description}
                onChange={handleInputChange}
                placeholder="Brief description of your website"
                rows="3"
                className="input-field w-full resize-none"
              />
            </div>

            {/* Website Logo */}
            <div className="mt-6 p-4 bg-gray-50 rounded-lg border border-gray-200">
              <label className="block text-sm font-medium text-gray-700 mb-3">
                Website Logo
              </label>
              <p className="text-xs text-gray-600 mb-3">
                Upload a logo to display in the header of your published public website. Once uploaded, the logo will replace your website name. If no logo is uploaded, your website name will be displayed instead. Recommended: square image (PNG, JPG, GIF, WebP, or SVG). Max 5MB.
              </p>

              {/* Logo Preview */}
              {logoPreview && (
                <div className="mb-4">
                  <div className="relative inline-block">
                    <img
                      src={logoPreview}
                      alt="Logo preview"
                      className="max-w-xs max-h-24 rounded-lg border border-gray-300"
                      onLoad={() => console.log('[Logo Preview] Image loaded successfully from:', logoPreview)}
                      onError={(e) => {
                        console.error('[Logo Preview] Image failed to load')
                        console.error('[Logo Preview] Attempted URL:', logoPreview)
                        console.error('[Logo Preview] Backend origin:', axios.defaults.baseURL)
                        console.error('[Logo Preview] Event:', e)
                        toast.error('Logo image failed to load at: ' + logoPreview)
                      }}
                    />
                  </div>
                </div>
              )}

              {/* File Input */}
              <div className="flex gap-2">
                <label className="flex-1">
                  <input
                    type="file"
                    accept="image/*"
                    onChange={handleLogoChange}
                    disabled={uploadingLogo}
                    className="hidden"
                  />
                  <span className="inline-flex items-center gap-2 px-4 py-2 bg-blue-600 text-white hover:bg-blue-700 disabled:bg-gray-400 rounded-lg font-medium transition-colors cursor-pointer w-full justify-center">
                    <FiUpload className="w-4 h-4" />
                    Choose File
                  </span>
                </label>

                {logoFile && !uploadingLogo && (
                  <button
                    type="button"
                    onClick={handleUploadLogo}
                    disabled={uploadingLogo}
                    className="px-4 py-2 bg-green-600 text-white hover:bg-green-700 disabled:bg-gray-400 rounded-lg font-medium transition-colors flex items-center gap-2"
                  >
                    Upload
                  </button>
                )}

                {logoPreview && (
                  <button
                    type="button"
                    onClick={handleDeleteLogo}
                    disabled={uploadingLogo}
                    className="px-4 py-2 bg-red-600 text-white hover:bg-red-700 disabled:bg-gray-400 rounded-lg font-medium transition-colors flex items-center gap-2"
                  >
                    <FiTrash2 className="w-4 h-4" />
                    Remove
                  </button>
                )}

                {uploadingLogo && (
                  <button
                    type="button"
                    disabled
                    className="px-4 py-2 bg-gray-400 text-white rounded-lg font-medium flex items-center gap-2"
                  >
                    <FiLoader className="w-4 h-4 animate-spin" />
                    Uploading...
                  </button>
                )}
              </div>

              {logoFile && (
                <p className="text-xs text-gray-600 mt-2">
                  Selected: {logoFile.name}
                </p>
              )}
            </div>
          </div>

          {/* Body Styling */}
          <div className="pt-4 border-t border-gray-100">
            <h3 className="text-lg font-semibold">Body Styling</h3>
            <div className="grid grid-cols-1 gap-4 mt-3">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Background Color</label>
                <div className="flex gap-2">
                  <input type="color" value={formData.bodyBackgroundColor || '#FFFFFF'} onChange={(e) => setFormData(prev => ({ ...prev, bodyBackgroundColor: e.target.value }))} className="w-12 h-10 border border-gray-300 rounded cursor-pointer" />
                  <input type="text" value={formData.bodyBackgroundColor || ''} onChange={(e) => setFormData(prev => ({ ...prev, bodyBackgroundColor: e.target.value }))} placeholder="#FFFFFF" className="input-field flex-1" />
                </div>
                <p className="text-xs text-gray-500 mt-1">Page background color for the public site</p>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Hover Background Color</label>
                <div className="flex gap-2">
                  <input type="color" value={formData.bodyHoverBackgroundColor || '#F3F4F6'} onChange={(e) => setFormData(prev => ({ ...prev, bodyHoverBackgroundColor: e.target.value }))} className="w-12 h-10 border border-gray-300 rounded cursor-pointer" />
                  <input type="text" value={formData.bodyHoverBackgroundColor || ''} onChange={(e) => setFormData(prev => ({ ...prev, bodyHoverBackgroundColor: e.target.value }))} placeholder="#F3F4F6" className="input-field flex-1" />
                </div>
                <p className="text-xs text-gray-500 mt-1">Hover background color applied when the page is hovered</p>
              </div>
            </div>
          </div>

          {/* SEO Settings */}
          <div className="space-y-4 pt-6 border-t border-gray-200">
            <h3 className="text-lg font-semibold text-gray-900">SEO Settings</h3>
            
            <div>
              <label htmlFor="meta-title" className="block text-sm font-medium text-gray-700 mb-2">
                Meta Title
              </label>
              <input
                id="meta-title"
                type="text"
                name="metaTitle"
                value={formData.metaTitle}
                onChange={handleInputChange}
                placeholder="Enter SEO title (60 characters)"
                maxLength="60"
                className="input-field w-full"
              />
              <p className="text-xs text-gray-500 mt-1">{formData.metaTitle.length}/60</p>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Meta Description
              </label>
              <textarea
                name="metaDescription"
                value={formData.metaDescription}
                onChange={handleInputChange}
                placeholder="Enter SEO description (160 characters)"
                maxLength="160"
                rows="2"
                className="input-field w-full resize-none"
              />
              <p className="text-xs text-gray-500 mt-1">{formData.metaDescription.length}/160</p>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Keywords
              </label>
              <input
                type="text"
                name="metaKeywords"
                value={formData.metaKeywords}
                onChange={handleInputChange}
                placeholder="Comma-separated keywords"
                className="input-field w-full"
              />
            </div>
          </div>

          {/* Social Media Profiles */}
          <div className="space-y-4 pt-6 border-t border-gray-200">
            <h3 className="text-lg font-semibold text-gray-900">Social Media Profiles</h3>
            <p className="text-sm text-gray-600">
              Add links to your public social profiles. These will appear as icons in the footer of your published website.
            </p>

            <div className="grid grid-cols-1 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">WhatsApp URL</label>
                <input
                  type="text"
                  name="whatsapp"
                  value={formData.whatsapp}
                  onChange={handleInputChange}
                  placeholder="https://wa.me/1234567890"
                  className="input-field w-full"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Instagram URL</label>
                <input
                  type="text"
                  name="instagram"
                  value={formData.instagram}
                  onChange={handleInputChange}
                  placeholder="https://instagram.com/yourprofile"
                  className="input-field w-full"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Facebook URL</label>
                <input
                  type="text"
                  name="facebook"
                  value={formData.facebook}
                  onChange={handleInputChange}
                  placeholder="https://facebook.com/yourprofile"
                  className="input-field w-full"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">TikTok URL</label>
                <input
                  type="text"
                  name="tiktok"
                  value={formData.tiktok}
                  onChange={handleInputChange}
                  placeholder="https://tiktok.com/@yourprofile"
                  className="input-field w-full"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Twitter URL</label>
                <input
                  type="text"
                  name="twitter"
                  value={formData.twitter}
                  onChange={handleInputChange}
                  placeholder="https://twitter.com/yourprofile"
                  className="input-field w-full"
                />
              </div>
            </div>
          </div>

          <div className="space-y-4 pt-6 border-t border-gray-200">
            <h3 className="text-lg font-semibold text-gray-900">Customer Authentication</h3>
            <p className="text-sm text-gray-600">
              Enable or configure customer login for this website. These settings control the public website's customer auth experience.
            </p>

            <div className="space-y-4">
              <div className="flex items-center gap-3">
                <input
                  id="customerAuthEnabled"
                  type="checkbox"
                  checked={formData.customerAuthEnabled}
                  onChange={(e) => setFormData(prev => ({ ...prev, customerAuthEnabled: e.target.checked }))}
                  className="h-4 w-4 rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                />
                <label htmlFor="customerAuthEnabled" className="text-sm font-medium text-gray-700">
                  Enable customer authentication
                </label>
              </div>

              <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Login button text</label>
                  <input
                    type="text"
                    name="loginButtonText"
                    value={formData.loginButtonText}
                    onChange={handleInputChange}
                    disabled={!formData.customerAuthEnabled}
                    className="input-field w-full"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Signup button text</label>
                  <input
                    type="text"
                    name="signupButtonText"
                    value={formData.signupButtonText}
                    onChange={handleInputChange}
                    disabled={!formData.customerAuthEnabled}
                    className="input-field w-full"
                  />
                </div>
              </div>

              <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
                <label className="inline-flex items-center gap-2 text-sm text-gray-700">
                  <input
                    type="checkbox"
                    checked={formData.enableForgotPassword}
                    onChange={(e) => setFormData(prev => ({ ...prev, enableForgotPassword: e.target.checked }))}
                    disabled={!formData.customerAuthEnabled}
                    className="h-4 w-4 rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                  />
                  Allow forgot password
                </label>

                <label className="inline-flex items-center gap-2 text-sm text-gray-700">
                  <input
                    type="checkbox"
                    checked={formData.requireEmailVerification}
                    onChange={(e) => setFormData(prev => ({ ...prev, requireEmailVerification: e.target.checked }))}
                    disabled={!formData.customerAuthEnabled}
                    className="h-4 w-4 rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                  />
                  Require email verification
                </label>

                <label className="inline-flex items-center gap-2 text-sm text-gray-700">
                  <input
                    type="checkbox"
                    checked={formData.allowSocialLogin}
                    onChange={(e) => setFormData(prev => ({ ...prev, allowSocialLogin: e.target.checked }))}
                    disabled={!formData.customerAuthEnabled}
                    className="h-4 w-4 rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                  />
                  Allow social login
                </label>
              </div>
            </div>
          </div>

          {/* Custom Domain */}
          <div className="space-y-4 pt-6 border-t border-gray-200">
            <h3 className="text-lg font-semibold text-gray-900">Custom Domain</h3>
            <p className="text-sm text-gray-600">
              Connect your own domain (e.g., example.com or www.example.com). You'll verify ownership using DNS records.
            </p>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <input
                type="text"
                placeholder="Enter custom domain (example.com)"
                value={customDomainInput}
                onChange={(e) => setCustomDomainInput(e.target.value)}
                className="input-field w-full col-span-2"
              />

              <div className="flex gap-2">
                <button
                  type="button"
                  onClick={async () => {
                    if (!website || !website._id)
                      return toast.error('Select a website first')

                    const domain = customDomainInput.trim()
                    if (!domain) return toast.error('Enter a domain')

                    try {
                      setDomainAdding(true)
                      const res = await axios.post(
                        `/websites/${website._id}/custom-domain`,
                        { domain }
                      )

                      if (res?.data?.customDomain) {
                        website.customDomain = res.data.customDomain
                        if (res.data.frontendHostname) {
                          setFrontendHostname(res.data.frontendHostname)
                        }
                        toast.success('✅ Custom domain added! Review DNS instructions below to verify.')
                        setCustomDomainInput('')
                      }
                    } catch (err) {
                      toast.error(
                        err.response?.data?.message ||
                        'Failed to add domain'
                      )
                    } finally {
                      setDomainAdding(false)
                    }
                  }}
                  disabled={domainAdding || domainVerifying || domainRemoving}
                  className={`px-3 py-2 rounded-lg text-white ${
                    domainAdding
                      ? 'bg-gray-400'
                      : 'bg-blue-600 hover:bg-blue-700'
                  }`}
                >
                  {domainAdding ? (
                    <span className="inline-flex items-center gap-2">
                      <FiLoader className="w-4 h-4 animate-spin" />
                      Adding...
                    </span>
                  ) : (
                    'Add Domain'
                  )}
                </button>

                <button
                  type="button"
                  onClick={async () => {
                    if (!website || !website._id) return toast.error('Select a website first')
                    try {
                      setDomainVerifying(true)
                      const res = await axios.post(`/websites/${website._id}/custom-domain/verify`)
                      website.customDomain = res.data.customDomain
                      toast.success('✅ Domain verified and activated successfully!')
                    } catch (err) {
                      console.error('Error verifying domain:', err)
                      const errorMsg = err.response?.data?.message || 'Domain verification failed'
                      const reason = err.response?.data?.reason || err.response?.data?.details?.error || ''
                      if (reason) {
                        toast.error(`${errorMsg}: ${reason}`)
                      } else {
                        toast.error(errorMsg)
                      }
                      if (err.response?.data?.customDomain) {
                        website.customDomain = err.response.data.customDomain
                      }
                    } finally {
                      setDomainVerifying(false)
                    }
                  }}
                  disabled={domainAdding || domainVerifying || domainRemoving || !website?.customDomain?.domain}
                  className={`px-3 py-2 rounded-lg text-white ${domainVerifying ? 'bg-gray-400' : 'bg-green-600 hover:bg-green-700'}`}
                >
                  {domainVerifying ? (
                    <span className="inline-flex items-center gap-2"><FiLoader className="w-4 h-4 animate-spin" />Verifying...</span>
                  ) : 'Verify'}
                </button>

                <button
                  type="button"
                  onClick={async () => {
                    if (!website || !website._id) return toast.error('Select a website first')
                    if (!window.confirm('Remove custom domain?')) return
                    try {
                      setDomainRemoving(true)
                      await axios.delete(`/websites/${website._id}/custom-domain`)
                      website.customDomain = null
                      setFrontendHostname('')
                      setCustomDomainInput('')
                      toast.success('Custom domain removed')
                    } catch (err) {
                      console.error('Error removing domain:', err)
                      toast.error(err.response?.data?.message || 'Failed to remove domain')
                    } finally {
                      setDomainRemoving(false)
                    }
                  }}
                  disabled={domainAdding || domainVerifying || domainRemoving || !website?.customDomain?.domain}
                  className={`px-3 py-2 rounded-lg text-white ${domainRemoving ? 'bg-gray-400' : 'bg-red-600 hover:bg-red-700'}`}
                >
                  {domainRemoving ? (
                    <span className="inline-flex items-center gap-2"><FiLoader className="w-4 h-4 animate-spin" />Removing...</span>
                  ) : 'Remove'}
                </button>
              </div>

              {/* DNS Configuration Instructions */}
              {website?.customDomain?.domain && (
                <div className="col-span-3">
                  <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                    <h4 className="font-semibold text-blue-900 mb-3">📋 DNS Configuration Instructions</h4>
                    
                    {website.customDomain.verified || website.customDomain.lifecycleStatus === 'active' ? (
                      <div className="mb-4 p-3 bg-green-100 border border-green-300 rounded">
                        <p className="text-green-800 font-semibold">✅ Domain Active</p>
                        <p className="text-sm text-green-700 mt-1">Your custom domain is now active and your website is accessible at <code className="bg-white px-2 py-1 rounded">https://{website.customDomain.domain}</code></p>
                      </div>
                    ) : (
                      <div className="space-y-4">
                        <div className="mb-4 flex items-center justify-between rounded-lg border border-gray-200 bg-white px-3 py-2">
                          <div>
                            <p className="text-sm font-semibold text-gray-900">Status: {getLifecycleLabel(website.customDomain).label}</p>
                            <p className="text-xs text-gray-600">{website.customDomain.failureReason || getLifecycleLabel(website.customDomain).label}</p>
                          </div>
                        </div>

                        <p className="text-sm text-gray-700">
                          To activate your custom domain, configure your DNS with one of the following methods:
                        </p>

                        {/* Method 1: CNAME */}
                        <div className="bg-white p-3 rounded border border-gray-200">
                          <p className="font-semibold text-gray-900 text-sm mb-2">Method 1: CNAME Record (Recommended for Subdomains)</p>
                          <p className="text-xs text-gray-600 mb-2">Add a CNAME record in your domain registrar's DNS settings:</p>
                          <div className="bg-gray-50 p-2 rounded font-mono text-xs space-y-1">
                            <p><span className="text-blue-600 font-semibold">Type:</span> CNAME</p>
                            <p><span className="text-blue-600 font-semibold">Name:</span> www (or your subdomain)</p>
                            <p><span className="text-blue-600 font-semibold">Value:</span> {frontendHostname || 'marketbooksolution.com'}</p>
                          </div>
                          <p className="text-xs text-gray-600 mt-2">This tells your domain to point to our frontend platform.</p>
                        </div>

                        {/* Method 2: TXT Verification Token */}
                        <div className="bg-white p-3 rounded border border-gray-200">
                          <p className="font-semibold text-gray-900 text-sm mb-2">Method 2: TXT Record (Domain Ownership Verification)</p>
                          <p className="text-xs text-gray-600 mb-2">Add a TXT record in your domain registrar's DNS settings to prove ownership:</p>
                          <div className="bg-gray-50 p-2 rounded font-mono text-xs space-y-1">
                            <p><span className="text-blue-600 font-semibold">Type:</span> TXT</p>
                            <p><span className="text-blue-600 font-semibold">Name:</span> @ (root domain)</p>
                            <p className="break-all"><span className="text-blue-600 font-semibold">Value:</span> <span className="bg-yellow-100 px-1 py-0.5 rounded select-all">{website.customDomain.verificationToken}</span></p>
                          </div>
                          <p className="text-xs text-gray-600 mt-2">Copy the verification token above and add it as a TXT record. After updating DNS, click the "Verify" button.</p>
                        </div>

                        {isApexDomain(website.customDomain.domain) && (
                          <div className="bg-white p-3 rounded border border-gray-200">
                            <p className="font-semibold text-gray-900 text-sm mb-2">Required A Records for Root Domain</p>
                            <p className="text-xs text-gray-600 mb-2">Add these root-domain A records in addition to the DNS records above.</p>
                            <div className="bg-gray-50 p-2 rounded font-mono text-xs space-y-1">
                              {getRequiredApexRecords().map((record) => (
                                <p key={`${record.type}-${record.value}`}>
                                  <span className="text-blue-600 font-semibold">Type:</span> {record.type}
                                  {' | '}
                                  <span className="text-blue-600 font-semibold">Name:</span> {record.name}
                                  {' | '}
                                  <span className="text-blue-600 font-semibold">Value:</span> {record.value}
                                </p>
                              ))}
                            </div>
                          </div>
                        )}

                        <div className="bg-yellow-50 border border-yellow-200 rounded p-2">
                          <p className="text-xs text-yellow-800">
                            <strong>Important:</strong> In addition to the DNS records generated above (such as CNAME, TXT, or other required records), you must also add the following A records for your root domain.
                          </p>
                          <p className="text-xs text-yellow-800 mt-1">
                            Do <strong>not</strong> replace the generated DNS records. Add these two A records alongside them.
                          </p>
                        </div>

                        <div className="bg-yellow-50 border border-yellow-200 rounded p-2">
                          <p className="text-xs text-yellow-800"><strong>⏱️ Note:</strong> DNS changes can take 15 minutes to several hours to propagate. If verification fails, wait a bit and try again.</p>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Design Settings */}
          <div className="space-y-4 pt-6 border-t border-gray-200">
            <h3 className="text-lg font-semibold text-gray-900">Design Settings</h3>

            {/* Colors */}
            <div className="space-y-4">
              <p className="text-sm text-gray-600">Colors</p>
              <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-2 sm:gap-4">
                <div>
                  <label className="block text-xs font-medium text-gray-700 mb-2">
                    Primary Color
                  </label>
                  <div className="flex items-center gap-2">
                    <input
                      type="color"
                      value={formData.primaryColor}
                      onChange={(e) => handleColorChange('primaryColor', e.target.value)}
                      className="w-12 h-10 rounded cursor-pointer border border-gray-300"
                    />
                    <input
                      type="text"
                      value={formData.primaryColor}
                      onChange={(e) => handleColorChange('primaryColor', e.target.value)}
                      className="input-field flex-1 font-mono text-sm"
                      maxLength="7"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-xs font-medium text-gray-700 mb-2">
                    Secondary Color
                  </label>
                  <div className="flex items-center gap-2">
                    <input
                      type="color"
                      value={formData.secondaryColor}
                      onChange={(e) => handleColorChange('secondaryColor', e.target.value)}
                      className="w-12 h-10 rounded cursor-pointer border border-gray-300"
                    />
                    <input
                      type="text"
                      value={formData.secondaryColor}
                      onChange={(e) => handleColorChange('secondaryColor', e.target.value)}
                      className="input-field flex-1 font-mono text-sm"
                      maxLength="7"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-xs font-medium text-gray-700 mb-2">
                    Accent Color
                  </label>
                  <div className="flex items-center gap-2">
                    <input
                      type="color"
                      value={formData.accentColor}
                      onChange={(e) => handleColorChange('accentColor', e.target.value)}
                      className="w-12 h-10 rounded cursor-pointer border border-gray-300"
                    />
                    <input
                      type="text"
                      value={formData.accentColor}
                      onChange={(e) => handleColorChange('accentColor', e.target.value)}
                      className="input-field flex-1 font-mono text-sm"
                      maxLength="7"
                    />
                  </div>
                </div>
              </div>
            </div>

            {/* Fonts */}
            <div className="space-y-3">
              <p className="text-sm text-gray-600">Fonts</p>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 sm:gap-4">
                <div>
                  <label className="block text-xs font-medium text-gray-700 mb-2">
                    Primary Font
                  </label>
                  <select
                    name="primaryFont"
                    value={formData.primaryFont}
                    onChange={handleInputChange}
                    className="input-field w-full"
                  >
                    <option value="Inter">Inter</option>
                    <option value="Arial">Arial</option>
                    <option value="Georgia">Georgia</option>
                    <option value="Times New Roman">Times New Roman</option>
                    <option value="Verdana">Verdana</option>
                  </select>
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-700 mb-2">
                    Secondary Font
                  </label>
                  <select
                    name="secondaryFont"
                    value={formData.secondaryFont}
                    onChange={handleInputChange}
                    className="input-field w-full"
                  >
                    <option value="Inter">Inter</option>
                    <option value="Arial">Arial</option>
                    <option value="Georgia">Georgia</option>
                    <option value="Times New Roman">Times New Roman</option>
                    <option value="Verdana">Verdana</option>
                  </select>
                </div>
              </div>
            </div>
          </div>

          {/* Action Buttons */}
          <div className="flex gap-3 pt-6 border-t border-gray-200">
            <button
              type="button"
              onClick={onClose}
              disabled={isSaving}
              className="flex-1 py-2 px-4 bg-gray-100 text-gray-700 hover:bg-gray-200 rounded-lg font-medium transition-colors disabled:opacity-50"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={isSaving}
              className="flex-1 py-2 px-4 bg-primary-600 text-white hover:bg-primary-700 disabled:bg-gray-400 rounded-lg font-medium transition-colors flex items-center justify-center gap-2 disabled:cursor-not-allowed"
            >
              {isSaving ? (
                <>
                  <FiLoader className="w-4 h-4 animate-spin" /> Saving...
                </>
              ) : (
                <>
                  <FiSave className="w-4 h-4" /> Save Website
                </>
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  )
}











// import { useState, useEffect } from 'react'
// import { FiX, FiLoader, FiSave, FiTrash2, FiUpload } from 'react-icons/fi'
// import toast from 'react-hot-toast'
// import axios from 'axios'

// export default function WebsiteEditorModal({ isOpen, onClose, onSubmit, website, categories, isSaving, mode, onLogoUpload, onLogoDelete }) {
//   const [formData, setFormData] = useState({
//     name: '',
//     description: '',
//     metaTitle: '',
//     metaDescription: '',
//     metaKeywords: '',
//     primaryColor: '#3B82F6',
//     secondaryColor: '#1F2937',
//     accentColor: '#F97316',
//     primaryFont: 'Inter',
//     secondaryFont: 'Inter'
//   })
//   const [logoFile, setLogoFile] = useState(null)
//   const [logoPreview, setLogoPreview] = useState(null)
//   const [uploadingLogo, setUploadingLogo] = useState(false)

//    // ✅ Proper local domain state for custom domain section
//   const [customDomainInput, setCustomDomainInput] = useState('')
//   const [domainAdding, setDomainAdding] = useState(false)
//   const [domainVerifying, setDomainVerifying] = useState(false)
//   const [domainRemoving, setDomainRemoving] = useState(false)
//   const [frontendHostname, setFrontendHostname] = useState('')

//   // Helper function to convert relative logo paths to absolute URLs
//   const getAbsoluteLogoUrl = (logoUrl) => {
//     if (!logoUrl) return null
    
//     // If already absolute, return as-is
//     if (logoUrl.startsWith('http://') || logoUrl.startsWith('https://')) {
//       return logoUrl
//     }
    
//     // Convert relative path to absolute using backend origin
//     const backendOrigin = axios.defaults.baseURL 
//       ? axios.defaults.baseURL.replace(/\/api\/?$/, '') 
//       : ''
    
//     if (!backendOrigin) {
//       console.warn('[Logo URL] Cannot determine backend origin, using relative URL:', logoUrl)
//       return logoUrl
//     }
    
//     const pathUrl = logoUrl.startsWith('/') ? logoUrl : `/${logoUrl}`
//     const absoluteUrl = `${backendOrigin}${pathUrl}`
//     console.log('[Logo URL] Converted relative to absolute:', { original: logoUrl, absolute: absoluteUrl })
//     return absoluteUrl
//   }

//   useEffect(() => {
//     if (website && isOpen) {
//       setFormData({
//         name: website.name || '',
//         description: website.description || '',
//         metaTitle: website.seo?.metaTitle || '',
//         metaDescription: website.seo?.metaDescription || '',
//         metaKeywords: website.seo?.metaKeywords || '',
//         primaryColor: website.colors?.primary || '#3B82F6',
//         secondaryColor: website.colors?.secondary || '#1F2937',
//         accentColor: website.colors?.accent || '#F97316',
//         primaryFont: website.fonts?.primary || 'Inter',
//         secondaryFont: website.fonts?.secondary || 'Inter'
//       })
      
//       // Display the saved/uploaded logo from backend if it exists
//       if (website.logo?.url) {
//         const absoluteUrl = getAbsoluteLogoUrl(website.logo.url)
//         setLogoPreview(absoluteUrl)
//         console.log('[WebsiteEditor] Showing saved logo from backend:', absoluteUrl)
//       } else {
//         setLogoPreview(null)
//         console.log('[WebsiteEditor] No saved logo found')
//       }
      
//       setLogoFile(null)
//     }
//   }, [website, isOpen])

//   const handleInputChange = (e) => {
//     const { name, value } = e.target
//     setFormData(prev => ({
//       ...prev,
//       [name]: value
//     }))
//   }

//   const handleColorChange = (colorField, value) => {
//     setFormData(prev => ({
//       ...prev,
//       [colorField]: value
//     }))
//   }

//   const handleLogoChange = (e) => {
//     const file = e.target.files?.[0]
//     if (!file) return

//     if (!file.type.startsWith('image/')) {
//       toast.error('Please select an image file')
//       return
//     }

//     if (file.size > 5 * 1024 * 1024) {
//       toast.error('File size must be less than 5MB')
//       return
//     }

//     // Store the file for upload - don't show preview until upload completes
//     setLogoFile(file)
//     console.log('[WebsiteEditor] File selected for upload:', file.name)
//   }

//   const handleUploadLogo = async () => {
//     if (!logoFile || !website?._id) {
//       toast.error('No logo file selected')
//       return
//     }

//     try {
//       setUploadingLogo(true)
//       const formDataUpload = new FormData()
//       formDataUpload.append('logo', logoFile)

//       console.log('[Logo Upload] Starting upload for website:', website._id)
//       console.log('[Logo Upload] File:', logoFile.name, 'Size:', logoFile.size, 'Type:', logoFile.type)

//       const response = await axios.post(`/websites/${website._id}/logo`, formDataUpload, {
//         headers: {
//           'Content-Type': 'multipart/form-data'
//         },
//         timeout: 30000 // 30 second timeout
//       })

//       console.log('[Logo Upload] Response received:', response.status)
//       console.log('[Logo Upload] Response data:', response.data)
      
//       const returnedWebsite = response.data?.website
//       const returnedLogoUrl = returnedWebsite?.logo?.url
      
//       console.log('[Logo Upload] Returned website:', returnedWebsite ? 'YES' : 'NO')
//       console.log('[Logo Upload] Returned logo URL:', returnedLogoUrl)
      
//       if (returnedLogoUrl) {
//         // Validate the URL format
//         if (!returnedLogoUrl.startsWith('/') && !returnedLogoUrl.startsWith('http')) {
//           console.warn('[Logo Upload] WARNING: URL does not start with / or http:', returnedLogoUrl)
//         }
        
//         // Convert relative URL to absolute for image loading in browser
//         const absoluteUrl = getAbsoluteLogoUrl(returnedLogoUrl)
//         setLogoPreview(absoluteUrl)
//         console.log('[Logo Upload] Logo preview set to:', absoluteUrl)
//         toast.success('✅ Logo uploaded successfully! It will appear on your public website, replacing the website name.')
        
//         // Call parent callback to update website data with the uploaded logo
//         if (onLogoUpload && returnedWebsite) {
//           try {
//             onLogoUpload(returnedWebsite)
//             console.log('[Logo Upload] Called onLogoUpload callback successfully')
//           } catch (callbackError) {
//             console.error('[Logo Upload] Error in onLogoUpload callback:', callbackError)
//             // Don't fail the whole operation if callback fails
//           }
//         } else {
//           console.warn('[Logo Upload] onLogoUpload callback not available or returnedWebsite missing')
//         }
//       } else {
//         console.error('[Logo Upload] Logo URL is missing from response')
//         console.error('[Logo Upload] Full response:', JSON.stringify(response.data))
//         toast.error('Upload succeeded but logo URL is missing from server response')
//       }
      
//       // Clear file input after successful upload
//       setLogoFile(null)
//     } catch (error) {
//       console.error('[Logo Upload] Error occurred:')
//       console.error('[Logo Upload] Error message:', error.message)
//       console.error('[Logo Upload] Error code:', error.code)
//       console.error('[Logo Upload] Status:', error.response?.status)
//       console.error('[Logo Upload] Status text:', error.response?.statusText)
//       console.error('[Logo Upload] Response data:', error.response?.data)
//       console.error('[Logo Upload] Full error:', error)
      
//       let errorMessage = 'Failed to upload logo'
//       if (error.response?.data?.message) {
//         errorMessage = error.response.data.message
//       } else if (error.code === 'ECONNABORTED') {
//         errorMessage = 'Upload timeout - please try again'
//       } else if (error.message) {
//         errorMessage = error.message
//       }
      
//       toast.error(errorMessage)
//     } finally {
//       setUploadingLogo(false)
//     }
//   }

//   const handleDeleteLogo = async () => {
//     if (!website?._id) return

//     if (!window.confirm('Are you sure you want to delete the logo?')) return

//     try {
//       setUploadingLogo(true)
//       console.log('[Logo Delete] Starting delete for website:', website._id)
      
//       const response = await axios.delete(`/websites/${website._id}/logo`, {
//         timeout: 30000 // 30 second timeout
//       })
      
//       console.log('[Logo Delete] Response received:', response.status)
//       console.log('[Logo Delete] Response data:', response.data)
      
//       const updatedWebsite = response.data?.website
//       console.log('[Logo Delete] Updated website:', updatedWebsite ? 'YES' : 'NO')
      
//       toast.success('Logo deleted successfully')
//       setLogoPreview(null)
//       setLogoFile(null)
      
//       // Call parent callback to update website data after logo deletion
//       if (onLogoDelete && updatedWebsite) {
//         try {
//           onLogoDelete(updatedWebsite)
//           console.log('[Logo Delete] Called onLogoDelete callback successfully')
//         } catch (callbackError) {
//           console.error('[Logo Delete] Error in onLogoDelete callback:', callbackError)
//           // Don't fail the whole operation if callback fails
//         }
//       } else {
//         console.warn('[Logo Delete] onLogoDelete callback not available or updatedWebsite missing')
//       }
//     } catch (error) {
//       console.error('[Logo Delete] Error occurred:')
//       console.error('[Logo Delete] Error message:', error.message)
//       console.error('[Logo Delete] Status:', error.response?.status)
//       console.error('[Logo Delete] Response data:', error.response?.data)
      
//       let errorMessage = 'Failed to delete logo'
//       if (error.response?.data?.message) {
//         errorMessage = error.response.data.message
//       } else if (error.code === 'ECONNABORTED') {
//         errorMessage = 'Delete timeout - please try again'
//       }
      
//       toast.error(errorMessage)
//     } finally {
//       setUploadingLogo(false)
//     }
//   }

//   const handleSubmit = (e) => {
//     e.preventDefault()
    
//     if (!formData.name.trim()) {
//       toast.error('Website name is required')
//       return
//     }

//     if (!website || !website._id) {
//       toast.error('Invalid website - please select a website to edit')
//       return
//     }

//     const submitData = {
//       name: formData.name.trim(),
//       description: formData.description,
//       seo: {
//         metaTitle: formData.metaTitle,
//         metaDescription: formData.metaDescription,
//         metaKeywords: formData.metaKeywords
//       },
//       colors: {
//         primary: formData.primaryColor,
//         secondary: formData.secondaryColor,
//         accent: formData.accentColor
//       },
//       fonts: {
//         primary: formData.primaryFont,
//         secondary: formData.secondaryFont
//       }
//     }

//     onSubmit(submitData)
//   }

//   if (!isOpen) return null

//   return (
//     <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
//       <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
//         {/* Header */}
//         <div className="flex justify-between items-center p-6 border-b border-gray-200 sticky top-0 bg-white">
//           <h2 className="text-2xl font-bold text-gray-900">Edit Website</h2>
//           <button
//             onClick={onClose}
//             disabled={isSaving}
//             className="p-1 hover:bg-gray-100 rounded-lg transition-colors disabled:opacity-50"
//           >
//             <FiX className="w-6 h-6" />
//           </button>
//         </div>

//         {/* Content */}
//         <form onSubmit={handleSubmit} className="p-6 space-y-6">
//           {/* Basic Information */}
//           <div className="space-y-4">
//             <h3 className="text-lg font-semibold text-gray-900">Basic Information</h3>
            
//             <div>
//               <label className="block text-sm font-medium text-gray-700 mb-2">
//                 Website Name *
//               </label>
//               <input
//                 type="text"
//                 name="name"
//                 value={formData.name}
//                 onChange={handleInputChange}
//                 placeholder="Enter website name"
//                 className="input-field w-full"
//                 required
//               />
//             </div>

//             <div>
//               <label className="block text-sm font-medium text-gray-700 mb-2">
//                 Description
//               </label>
//               <textarea
//                 name="description"
//                 value={formData.description}
//                 onChange={handleInputChange}
//                 placeholder="Brief description of your website"
//                 rows="3"
//                 className="input-field w-full resize-none"
//               />
//             </div>

//             {/* Website Logo */}
//             <div className="mt-6 p-4 bg-gray-50 rounded-lg border border-gray-200">
//               <label className="block text-sm font-medium text-gray-700 mb-3">
//                 Website Logo
//               </label>
//               <p className="text-xs text-gray-600 mb-3">
//                 Upload a logo to display in the header of your published public website. Once uploaded, the logo will replace your website name. If no logo is uploaded, your website name will be displayed instead. Recommended: square image (PNG, JPG, GIF, WebP, or SVG). Max 5MB.
//               </p>

//               {/* Logo Preview */}
//               {logoPreview && (
//                 <div className="mb-4">
//                   <div className="relative inline-block">
//                     <img
//                       src={logoPreview}
//                       alt="Logo preview"
//                       className="max-w-xs max-h-24 rounded-lg border border-gray-300"
//                       onLoad={() => console.log('[Logo Preview] Image loaded successfully from:', logoPreview)}
//                       onError={(e) => {
//                         console.error('[Logo Preview] Image failed to load')
//                         console.error('[Logo Preview] Attempted URL:', logoPreview)
//                         console.error('[Logo Preview] Backend origin:', axios.defaults.baseURL)
//                         console.error('[Logo Preview] Event:', e)
//                         toast.error('Logo image failed to load at: ' + logoPreview)
//                       }}
//                     />
//                   </div>
//                 </div>
//               )}

//               {/* File Input */}
//               <div className="flex gap-2">
//                 <label className="flex-1">
//                   <input
//                     type="file"
//                     accept="image/*"
//                     onChange={handleLogoChange}
//                     disabled={uploadingLogo}
//                     className="hidden"
//                   />
//                   <span className="inline-flex items-center gap-2 px-4 py-2 bg-blue-600 text-white hover:bg-blue-700 disabled:bg-gray-400 rounded-lg font-medium transition-colors cursor-pointer w-full justify-center">
//                     <FiUpload className="w-4 h-4" />
//                     Choose File
//                   </span>
//                 </label>

//                 {logoFile && !uploadingLogo && (
//                   <button
//                     type="button"
//                     onClick={handleUploadLogo}
//                     disabled={uploadingLogo}
//                     className="px-4 py-2 bg-green-600 text-white hover:bg-green-700 disabled:bg-gray-400 rounded-lg font-medium transition-colors flex items-center gap-2"
//                   >
//                     Upload
//                   </button>
//                 )}

//                 {logoPreview && (
//                   <button
//                     type="button"
//                     onClick={handleDeleteLogo}
//                     disabled={uploadingLogo}
//                     className="px-4 py-2 bg-red-600 text-white hover:bg-red-700 disabled:bg-gray-400 rounded-lg font-medium transition-colors flex items-center gap-2"
//                   >
//                     <FiTrash2 className="w-4 h-4" />
//                     Remove
//                   </button>
//                 )}

//                 {uploadingLogo && (
//                   <button
//                     type="button"
//                     disabled
//                     className="px-4 py-2 bg-gray-400 text-white rounded-lg font-medium flex items-center gap-2"
//                   >
//                     <FiLoader className="w-4 h-4 animate-spin" />
//                     Uploading...
//                   </button>
//                 )}
//               </div>

//               {logoFile && (
//                 <p className="text-xs text-gray-600 mt-2">
//                   Selected: {logoFile.name}
//                 </p>
//               )}
//             </div>
//           </div>

//           {/* SEO Settings */}
//           <div className="space-y-4 pt-6 border-t border-gray-200">
//             <h3 className="text-lg font-semibold text-gray-900">SEO Settings</h3>
            
//             <div>
//               <label className="block text-sm font-medium text-gray-700 mb-2">
//                 Meta Title
//               </label>
//               <input
//                 type="text"
//                 name="metaTitle"
//                 value={formData.metaTitle}
//                 onChange={handleInputChange}
//                 placeholder="Enter SEO title (60 characters)"
//                 maxLength="60"
//                 className="input-field w-full"
//               />
//               <p className="text-xs text-gray-500 mt-1">{formData.metaTitle.length}/60</p>
//             </div>

//             <div>
//               <label className="block text-sm font-medium text-gray-700 mb-2">
//                 Meta Description
//               </label>
//               <textarea
//                 name="metaDescription"
//                 value={formData.metaDescription}
//                 onChange={handleInputChange}
//                 placeholder="Enter SEO description (160 characters)"
//                 maxLength="160"
//                 rows="2"
//                 className="input-field w-full resize-none"
//               />
//               <p className="text-xs text-gray-500 mt-1">{formData.metaDescription.length}/160</p>
//             </div>

//             <div>
//               <label className="block text-sm font-medium text-gray-700 mb-2">
//                 Keywords
//               </label>
//               <input
//                 type="text"
//                 name="metaKeywords"
//                 value={formData.metaKeywords}
//                 onChange={handleInputChange}
//                 placeholder="Comma-separated keywords"
//                 className="input-field w-full"
//               />
//             </div>
//           </div>

//           {/* Custom Domain */}
//           <div className="space-y-4 pt-6 border-t border-gray-200">
//             <h3 className="text-lg font-semibold text-gray-900">Custom Domain</h3>
//             <p className="text-sm text-gray-600">
//               Connect your own domain (e.g., example.com or www.example.com). You'll verify ownership using DNS records.
//             </p>

//             <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
//               <input
//                 type="text"
//                 placeholder="Enter custom domain (example.com)"
//                 value={customDomainInput}
//                 onChange={(e) => setCustomDomainInput(e.target.value)}
//                 className="input-field w-full col-span-2"
//               />

//               <div className="flex gap-2">
//                 <button
//                   type="button"
//                   onClick={async () => {
//                     if (!website || !website._id)
//                       return toast.error('Select a website first')

//                     const domain = customDomainInput.trim()
//                     if (!domain) return toast.error('Enter a domain')

//                     try {
//                       setDomainAdding(true)
//                       const res = await axios.post(
//                         `/websites/${website._id}/custom-domain`,
//                         { domain }
//                       )

//                       if (res?.data?.customDomain) {
//                         // Update local state with the returned data
//                         website.customDomain = res.data.customDomain
//                         if (res.data.frontendHostname) {
//                           setFrontendHostname(res.data.frontendHostname)
//                         }
//                         toast.success('✅ Custom domain added! Review DNS instructions below to verify.')
//                         setCustomDomainInput('')
//                       }
//                     } catch (err) {
//                       toast.error(
//                         err.response?.data?.message ||
//                         'Failed to add domain'
//                       )
//                     } finally {
//                       setDomainAdding(false)
//                     }
//                   }}
//                   disabled={domainAdding || domainVerifying || domainRemoving}
//                   className={`px-3 py-2 rounded-lg text-white ${
//                     domainAdding
//                       ? 'bg-gray-400'
//                       : 'bg-blue-600 hover:bg-blue-700'
//                   }`}
//                 >
//                   {domainAdding ? (
//                     <span className="inline-flex items-center gap-2">
//                       <FiLoader className="w-4 h-4 animate-spin" />
//                       Adding...
//                     </span>
//                   ) : (
//                     'Add Domain'
//                   )}
//                 </button>

//                 <button
//                   type="button"
//                   onClick={async () => {
//                     if (!website || !website._id) return toast.error('Select a website first')
//                     try {
//                       setDomainVerifying(true)
//                       const res = await axios.post(`/websites/${website._id}/custom-domain/verify`)
//                       website.customDomain = res.data.customDomain
//                       toast.success('✅ Domain verified and activated successfully!')
//                     } catch (err) {
//                       console.error('Error verifying domain:', err)
//                       const errorMsg = err.response?.data?.message || 'Domain verification failed'
//                       toast.error(errorMsg)
//                     } finally {
//                       setDomainVerifying(false)
//                     }
//                   }}
//                   disabled={domainAdding || domainVerifying || domainRemoving || !website?.customDomain?.domain}
//                   className={`px-3 py-2 rounded-lg text-white ${domainVerifying ? 'bg-gray-400' : 'bg-green-600 hover:bg-green-700'}`}
//                 >
//                   {domainVerifying ? (
//                     <span className="inline-flex items-center gap-2"><FiLoader className="w-4 h-4 animate-spin" />Verifying...</span>
//                   ) : 'Verify'}
//                 </button>

//                 <button
//                   type="button"
//                   onClick={async () => {
//                     if (!website || !website._id) return toast.error('Select a website first')
//                     if (!window.confirm('Remove custom domain?')) return
//                     try {
//                       setDomainRemoving(true)
//                       await axios.delete(`/websites/${website._id}/custom-domain`)
//                       website.customDomain = null
//                       setFrontendHostname('')
//                       setCustomDomainInput('')
//                       toast.success('Custom domain removed')
//                     } catch (err) {
//                       console.error('Error removing domain:', err)
//                       toast.error(err.response?.data?.message || 'Failed to remove domain')
//                     } finally {
//                       setDomainRemoving(false)
//                     }
//                   }}
//                   disabled={domainAdding || domainVerifying || domainRemoving || !website?.customDomain?.domain}
//                   className={`px-3 py-2 rounded-lg text-white ${domainRemoving ? 'bg-gray-400' : 'bg-red-600 hover:bg-red-700'}`}
//                 >
//                   {domainRemoving ? (
//                     <span className="inline-flex items-center gap-2"><FiLoader className="w-4 h-4 animate-spin" />Removing...</span>
//                   ) : 'Remove'}
//                 </button>
//               </div>

//               {/* DNS Configuration Instructions */}
//               {website?.customDomain?.domain && (
//                 <div className="col-span-3">
//                   <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
//                     <h4 className="font-semibold text-blue-900 mb-3">📋 DNS Configuration Instructions</h4>
                    
//                     {website.customDomain.verified ? (
//                       <div className="mb-4 p-3 bg-green-100 border border-green-300 rounded">
//                         <p className="text-green-800 font-semibold">✅ Domain Verified and Active</p>
//                         <p className="text-sm text-green-700 mt-1">Your custom domain is now active and your website is accessible at <code className="bg-white px-2 py-1 rounded">https://{website.customDomain.domain}</code></p>
//                       </div>
//                     ) : (
//                       <div className="space-y-4">
//                         <p className="text-sm text-gray-700">
//                           To activate your custom domain, configure your DNS with one of the following methods:
//                         </p>

//                         {/* Method 1: CNAME */}
//                         <div className="bg-white p-3 rounded border border-gray-200">
//                           <p className="font-semibold text-gray-900 text-sm mb-2">Method 1: CNAME Record (Recommended for Subdomains)</p>
//                           <p className="text-xs text-gray-600 mb-2">Add a CNAME record in your domain registrar's DNS settings:</p>
//                           <div className="bg-gray-50 p-2 rounded font-mono text-xs space-y-1">
//                             <p><span className="text-blue-600 font-semibold">Type:</span> CNAME</p>
//                             <p><span className="text-blue-600 font-semibold">Name:</span> www (or your subdomain)</p>
//                             <p><span className="text-blue-600 font-semibold">Value:</span> {frontendHostname || 'marketbooksolution.com'}</p>
//                           </div>
//                           <p className="text-xs text-gray-600 mt-2">This tells your domain to point to our frontend platform.</p>
//                         </div>

//                         {/* Method 2: TXT Verification Token */}
//                         <div className="bg-white p-3 rounded border border-gray-200">
//                           <p className="font-semibold text-gray-900 text-sm mb-2">Method 2: TXT Record (Domain Ownership Verification)</p>
//                           <p className="text-xs text-gray-600 mb-2">Add a TXT record in your domain registrar's DNS settings to prove ownership:</p>
//                           <div className="bg-gray-50 p-2 rounded font-mono text-xs space-y-1">
//                             <p><span className="text-blue-600 font-semibold">Type:</span> TXT</p>
//                             <p><span className="text-blue-600 font-semibold">Name:</span> @ (root domain)</p>
//                             <p className="break-all"><span className="text-blue-600 font-semibold">Value:</span> <span className="bg-yellow-100 px-1 py-0.5 rounded select-all">{website.customDomain.verificationToken}</span></p>
//                           </div>
//                           <p className="text-xs text-gray-600 mt-2">Copy the verification token above and add it as a TXT record. After updating DNS, click the "Verify" button.</p>
//                         </div>

//                         <div className="bg-yellow-50 border border-yellow-200 rounded p-2">
//                           <p className="text-xs text-yellow-800"><strong>⏱️ Note:</strong> DNS changes can take 15 minutes to several hours to propagate. If verification fails, wait a bit and try again.</p>
//                         </div>
//                       </div>
//                     )}
//                   </div>
//                 </div>
//               )}
//             </div>
//           </div>

//           {/* Design Settings */}
//           <div className="space-y-4 pt-6 border-t border-gray-200">
//             <h3 className="text-lg font-semibold text-gray-900">Design Settings</h3>

//             {/* Colors */}
//             <div className="space-y-4">
//               <p className="text-sm text-gray-600">Colors</p>
//               <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-2 sm:gap-4">
//                 <div>
//                   <label className="block text-xs font-medium text-gray-700 mb-2">
//                     Primary Color
//                   </label>
//                   <div className="flex items-center gap-2">
//                     <input
//                       type="color"
//                       value={formData.primaryColor}
//                       onChange={(e) => handleColorChange('primaryColor', e.target.value)}
//                       className="w-12 h-10 rounded cursor-pointer border border-gray-300"
//                     />
//                     <input
//                       type="text"
//                       value={formData.primaryColor}
//                       onChange={(e) => handleColorChange('primaryColor', e.target.value)}
//                       className="input-field flex-1 font-mono text-sm"
//                       maxLength="7"
//                     />
//                   </div>
//                 </div>

//                 <div>
//                   <label className="block text-xs font-medium text-gray-700 mb-2">
//                     Secondary Color
//                   </label>
//                   <div className="flex items-center gap-2">
//                     <input
//                       type="color"
//                       value={formData.secondaryColor}
//                       onChange={(e) => handleColorChange('secondaryColor', e.target.value)}
//                       className="w-12 h-10 rounded cursor-pointer border border-gray-300"
//                     />
//                     <input
//                       type="text"
//                       value={formData.secondaryColor}
//                       onChange={(e) => handleColorChange('secondaryColor', e.target.value)}
//                       className="input-field flex-1 font-mono text-sm"
//                       maxLength="7"
//                     />
//                   </div>
//                 </div>

//                 <div>
//                   <label className="block text-xs font-medium text-gray-700 mb-2">
//                     Accent Color
//                   </label>
//                   <div className="flex items-center gap-2">
//                     <input
//                       type="color"
//                       value={formData.accentColor}
//                       onChange={(e) => handleColorChange('accentColor', e.target.value)}
//                       className="w-12 h-10 rounded cursor-pointer border border-gray-300"
//                     />
//                     <input
//                       type="text"
//                       value={formData.accentColor}
//                       onChange={(e) => handleColorChange('accentColor', e.target.value)}
//                       className="input-field flex-1 font-mono text-sm"
//                       maxLength="7"
//                     />
//                   </div>
//                 </div>
//               </div>
//             </div>

//             {/* Fonts */}
//             <div className="space-y-3">
//               <p className="text-sm text-gray-600">Fonts</p>
//               <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 sm:gap-4">
//                 <div>
//                   <label className="block text-xs font-medium text-gray-700 mb-2">
//                     Primary Font
//                   </label>
//                   <select
//                     name="primaryFont"
//                     value={formData.primaryFont}
//                     onChange={handleInputChange}
//                     className="input-field w-full"
//                   >
//                     <option value="Inter">Inter</option>
//                     <option value="Arial">Arial</option>
//                     <option value="Georgia">Georgia</option>
//                     <option value="Times New Roman">Times New Roman</option>
//                     <option value="Verdana">Verdana</option>
//                   </select>
//                 </div>
//                 <div>
//                   <label className="block text-xs font-medium text-gray-700 mb-2">
//                     Secondary Font
//                   </label>
//                   <select
//                     name="secondaryFont"
//                     value={formData.secondaryFont}
//                     onChange={handleInputChange}
//                     className="input-field w-full"
//                   >
//                     <option value="Inter">Inter</option>
//                     <option value="Arial">Arial</option>
//                     <option value="Georgia">Georgia</option>
//                     <option value="Times New Roman">Times New Roman</option>
//                     <option value="Verdana">Verdana</option>
//                   </select>
//                 </div>
//               </div>
//             </div>
//           </div>

//           {/* Action Buttons */}
//           <div className="flex gap-3 pt-6 border-t border-gray-200">
//             <button
//               type="button"
//               onClick={onClose}
//               disabled={isSaving}
//               className="flex-1 py-2 px-4 bg-gray-100 text-gray-700 hover:bg-gray-200 rounded-lg font-medium transition-colors disabled:opacity-50"
//             >
//               Cancel
//             </button>
//             <button
//               type="submit"
//               disabled={isSaving}
//               className="flex-1 py-2 px-4 bg-primary-600 text-white hover:bg-primary-700 disabled:bg-gray-400 rounded-lg font-medium transition-colors flex items-center justify-center gap-2 disabled:cursor-not-allowed"
//             >
//               {isSaving ? (
//                 <>
//                   <FiLoader className="w-4 h-4 animate-spin" /> Saving...
//                 </>
//               ) : (
//                 <>
//                   <FiSave className="w-4 h-4" /> Save Website
//                 </>
//               )}
//             </button>
//           </div>
//         </form>
//       </div>
//     </div>
//   )
// }























// import { useState, useEffect } from 'react'
// import { FiX, FiLoader, FiSave, FiTrash2, FiUpload } from 'react-icons/fi'
// import toast from 'react-hot-toast'
// import axios from 'axios'

// export default function WebsiteEditorModal({ isOpen, onClose, onSubmit, website, categories, isSaving, mode, onLogoUpload, onLogoDelete }) {
//   const [formData, setFormData] = useState({
//     name: '',
//     description: '',
//     metaTitle: '',
//     metaDescription: '',
//     metaKeywords: '',
//     primaryColor: '#3B82F6',
//     secondaryColor: '#1F2937',
//     accentColor: '#F97316',
//     primaryFont: 'Inter',
//     secondaryFont: 'Inter'
//   })
//   const [logoFile, setLogoFile] = useState(null)
//   const [logoPreview, setLogoPreview] = useState(null)
//   const [uploadingLogo, setUploadingLogo] = useState(false)
//   const [domainAdding, setDomainAdding] = useState(false)
//   const [domainVerifying, setDomainVerifying] = useState(false)
//   const [domainRemoving, setDomainRemoving] = useState(false)

//   // Helper function to convert relative logo paths to absolute URLs
//   const getAbsoluteLogoUrl = (logoUrl) => {
//     if (!logoUrl) return null
    
//     // If already absolute, return as-is
//     if (logoUrl.startsWith('http://') || logoUrl.startsWith('https://')) {
//       return logoUrl
//     }
    
//     // Convert relative path to absolute using backend origin
//     const backendOrigin = axios.defaults.baseURL 
//       ? axios.defaults.baseURL.replace(/\/api\/?$/, '') 
//       : ''
    
//     if (!backendOrigin) {
//       console.warn('[Logo URL] Cannot determine backend origin, using relative URL:', logoUrl)
//       return logoUrl
//     }
    
//     const pathUrl = logoUrl.startsWith('/') ? logoUrl : `/${logoUrl}`
//     const absoluteUrl = `${backendOrigin}${pathUrl}`
//     console.log('[Logo URL] Converted relative to absolute:', { original: logoUrl, absolute: absoluteUrl })
//     return absoluteUrl
//   }

//   useEffect(() => {
//     if (website && isOpen) {
//       setFormData({
//         name: website.name || '',
//         description: website.description || '',
//         metaTitle: website.seo?.metaTitle || '',
//         metaDescription: website.seo?.metaDescription || '',
//         metaKeywords: website.seo?.metaKeywords || '',
//         primaryColor: website.colors?.primary || '#3B82F6',
//         secondaryColor: website.colors?.secondary || '#1F2937',
//         accentColor: website.colors?.accent || '#F97316',
//         primaryFont: website.fonts?.primary || 'Inter',
//         secondaryFont: website.fonts?.secondary || 'Inter'
//       })
      
//       // Display the saved/uploaded logo from backend if it exists
//       if (website.logo?.url) {
//         const absoluteUrl = getAbsoluteLogoUrl(website.logo.url)
//         setLogoPreview(absoluteUrl)
//         console.log('[WebsiteEditor] Showing saved logo from backend:', absoluteUrl)
//       } else {
//         setLogoPreview(null)
//         console.log('[WebsiteEditor] No saved logo found')
//       }
      
//       setLogoFile(null)
//     }
//   }, [website, isOpen])

//   const handleInputChange = (e) => {
//     const { name, value } = e.target
//     setFormData(prev => ({
//       ...prev,
//       [name]: value
//     }))
//   }

//   const handleColorChange = (colorField, value) => {
//     setFormData(prev => ({
//       ...prev,
//       [colorField]: value
//     }))
//   }

//   const handleLogoChange = (e) => {
//     const file = e.target.files?.[0]
//     if (!file) return

//     if (!file.type.startsWith('image/')) {
//       toast.error('Please select an image file')
//       return
//     }

//     if (file.size > 5 * 1024 * 1024) {
//       toast.error('File size must be less than 5MB')
//       return
//     }

//     // Store the file for upload - don't show preview until upload completes
//     setLogoFile(file)
//     console.log('[WebsiteEditor] File selected for upload:', file.name)
//   }

//   const handleUploadLogo = async () => {
//     if (!logoFile || !website?._id) {
//       toast.error('No logo file selected')
//       return
//     }

//     try {
//       setUploadingLogo(true)
//       const formDataUpload = new FormData()
//       formDataUpload.append('logo', logoFile)

//       console.log('[Logo Upload] Starting upload for website:', website._id)
//       console.log('[Logo Upload] File:', logoFile.name, 'Size:', logoFile.size, 'Type:', logoFile.type)

//       const response = await axios.post(`/websites/${website._id}/logo`, formDataUpload, {
//         headers: {
//           'Content-Type': 'multipart/form-data'
//         },
//         timeout: 30000 // 30 second timeout
//       })

//       console.log('[Logo Upload] Response received:', response.status)
//       console.log('[Logo Upload] Response data:', response.data)
      
//       const returnedWebsite = response.data?.website
//       const returnedLogoUrl = returnedWebsite?.logo?.url
      
//       console.log('[Logo Upload] Returned website:', returnedWebsite ? 'YES' : 'NO')
//       console.log('[Logo Upload] Returned logo URL:', returnedLogoUrl)
      
//       if (returnedLogoUrl) {
//         // Validate the URL format
//         if (!returnedLogoUrl.startsWith('/') && !returnedLogoUrl.startsWith('http')) {
//           console.warn('[Logo Upload] WARNING: URL does not start with / or http:', returnedLogoUrl)
//         }
        
//         // Convert relative URL to absolute for image loading in browser
//         const absoluteUrl = getAbsoluteLogoUrl(returnedLogoUrl)
//         setLogoPreview(absoluteUrl)
//         console.log('[Logo Upload] Logo preview set to:', absoluteUrl)
//         toast.success('✅ Logo uploaded successfully! It will appear on your public website, replacing the website name.')
        
//         // Call parent callback to update website data with the uploaded logo
//         if (onLogoUpload && returnedWebsite) {
//           try {
//             onLogoUpload(returnedWebsite)
//             console.log('[Logo Upload] Called onLogoUpload callback successfully')
//           } catch (callbackError) {
//             console.error('[Logo Upload] Error in onLogoUpload callback:', callbackError)
//             // Don't fail the whole operation if callback fails
//           }
//         } else {
//           console.warn('[Logo Upload] onLogoUpload callback not available or returnedWebsite missing')
//         }
//       } else {
//         console.error('[Logo Upload] Logo URL is missing from response')
//         console.error('[Logo Upload] Full response:', JSON.stringify(response.data))
//         toast.error('Upload succeeded but logo URL is missing from server response')
//       }
      
//       // Clear file input after successful upload
//       setLogoFile(null)
//     } catch (error) {
//       console.error('[Logo Upload] Error occurred:')
//       console.error('[Logo Upload] Error message:', error.message)
//       console.error('[Logo Upload] Error code:', error.code)
//       console.error('[Logo Upload] Status:', error.response?.status)
//       console.error('[Logo Upload] Status text:', error.response?.statusText)
//       console.error('[Logo Upload] Response data:', error.response?.data)
//       console.error('[Logo Upload] Full error:', error)
      
//       let errorMessage = 'Failed to upload logo'
//       if (error.response?.data?.message) {
//         errorMessage = error.response.data.message
//       } else if (error.code === 'ECONNABORTED') {
//         errorMessage = 'Upload timeout - please try again'
//       } else if (error.message) {
//         errorMessage = error.message
//       }
      
//       toast.error(errorMessage)
//     } finally {
//       setUploadingLogo(false)
//     }
//   }

//   const handleDeleteLogo = async () => {
//     if (!website?._id) return

//     if (!window.confirm('Are you sure you want to delete the logo?')) return

//     try {
//       setUploadingLogo(true)
//       console.log('[Logo Delete] Starting delete for website:', website._id)
      
//       const response = await axios.delete(`/websites/${website._id}/logo`, {
//         timeout: 30000 // 30 second timeout
//       })
      
//       console.log('[Logo Delete] Response received:', response.status)
//       console.log('[Logo Delete] Response data:', response.data)
      
//       const updatedWebsite = response.data?.website
//       console.log('[Logo Delete] Updated website:', updatedWebsite ? 'YES' : 'NO')
      
//       toast.success('Logo deleted successfully')
//       setLogoPreview(null)
//       setLogoFile(null)
      
//       // Call parent callback to update website data after logo deletion
//       if (onLogoDelete && updatedWebsite) {
//         try {
//           onLogoDelete(updatedWebsite)
//           console.log('[Logo Delete] Called onLogoDelete callback successfully')
//         } catch (callbackError) {
//           console.error('[Logo Delete] Error in onLogoDelete callback:', callbackError)
//           // Don't fail the whole operation if callback fails
//         }
//       } else {
//         console.warn('[Logo Delete] onLogoDelete callback not available or updatedWebsite missing')
//       }
//     } catch (error) {
//       console.error('[Logo Delete] Error occurred:')
//       console.error('[Logo Delete] Error message:', error.message)
//       console.error('[Logo Delete] Status:', error.response?.status)
//       console.error('[Logo Delete] Response data:', error.response?.data)
      
//       let errorMessage = 'Failed to delete logo'
//       if (error.response?.data?.message) {
//         errorMessage = error.response.data.message
//       } else if (error.code === 'ECONNABORTED') {
//         errorMessage = 'Delete timeout - please try again'
//       }
      
//       toast.error(errorMessage)
//     } finally {
//       setUploadingLogo(false)
//     }
//   }

//   const handleSubmit = (e) => {
//     e.preventDefault()
    
//     if (!formData.name.trim()) {
//       toast.error('Website name is required')
//       return
//     }

//     if (!website || !website._id) {
//       toast.error('Invalid website - please select a website to edit')
//       return
//     }

//     const submitData = {
//       name: formData.name.trim(),
//       description: formData.description,
//       seo: {
//         metaTitle: formData.metaTitle,
//         metaDescription: formData.metaDescription,
//         metaKeywords: formData.metaKeywords
//       },
//       colors: {
//         primary: formData.primaryColor,
//         secondary: formData.secondaryColor,
//         accent: formData.accentColor
//       },
//       fonts: {
//         primary: formData.primaryFont,
//         secondary: formData.secondaryFont
//       }
//     }

//     onSubmit(submitData)
//   }

//   if (!isOpen) return null

//   return (
//     <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
//       <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
//         {/* Header */}
//         <div className="flex justify-between items-center p-6 border-b border-gray-200 sticky top-0 bg-white">
//           <h2 className="text-2xl font-bold text-gray-900">Edit Website</h2>
//           <button
//             onClick={onClose}
//             disabled={isSaving}
//             className="p-1 hover:bg-gray-100 rounded-lg transition-colors disabled:opacity-50"
//           >
//             <FiX className="w-6 h-6" />
//           </button>
//         </div>

//         {/* Content */}
//         <form onSubmit={handleSubmit} className="p-6 space-y-6">
//           {/* Basic Information */}
//           <div className="space-y-4">
//             <h3 className="text-lg font-semibold text-gray-900">Basic Information</h3>
            
//             <div>
//               <label className="block text-sm font-medium text-gray-700 mb-2">
//                 Website Name *
//               </label>
//               <input
//                 type="text"
//                 name="name"
//                 value={formData.name}
//                 onChange={handleInputChange}
//                 placeholder="Enter website name"
//                 className="input-field w-full"
//                 required
//               />
//             </div>

//             <div>
//               <label className="block text-sm font-medium text-gray-700 mb-2">
//                 Description
//               </label>
//               <textarea
//                 name="description"
//                 value={formData.description}
//                 onChange={handleInputChange}
//                 placeholder="Brief description of your website"
//                 rows="3"
//                 className="input-field w-full resize-none"
//               />
//             </div>

//             {/* Website Logo */}
//             <div className="mt-6 p-4 bg-gray-50 rounded-lg border border-gray-200">
//               <label className="block text-sm font-medium text-gray-700 mb-3">
//                 Website Logo
//               </label>
//               <p className="text-xs text-gray-600 mb-3">
//                 Upload a logo to display in the header of your published public website. Once uploaded, the logo will replace your website name. If no logo is uploaded, your website name will be displayed instead. Recommended: square image (PNG, JPG, GIF, WebP, or SVG). Max 5MB.
//               </p>

//               {/* Logo Preview */}
//               {logoPreview && (
//                 <div className="mb-4">
//                   <div className="relative inline-block">
//                     <img
//                       src={logoPreview}
//                       alt="Logo preview"
//                       className="max-w-xs max-h-24 rounded-lg border border-gray-300"
//                       onLoad={() => console.log('[Logo Preview] Image loaded successfully from:', logoPreview)}
//                       onError={(e) => {
//                         console.error('[Logo Preview] Image failed to load')
//                         console.error('[Logo Preview] Attempted URL:', logoPreview)
//                         console.error('[Logo Preview] Backend origin:', axios.defaults.baseURL)
//                         console.error('[Logo Preview] Event:', e)
//                         toast.error('Logo image failed to load at: ' + logoPreview)
//                       }}
//                     />
//                   </div>
//                 </div>
//               )}

//               {/* File Input */}
//               <div className="flex gap-2">
//                 <label className="flex-1">
//                   <input
//                     type="file"
//                     accept="image/*"
//                     onChange={handleLogoChange}
//                     disabled={uploadingLogo}
//                     className="hidden"
//                   />
//                   <span className="inline-flex items-center gap-2 px-4 py-2 bg-blue-600 text-white hover:bg-blue-700 disabled:bg-gray-400 rounded-lg font-medium transition-colors cursor-pointer w-full justify-center">
//                     <FiUpload className="w-4 h-4" />
//                     Choose File
//                   </span>
//                 </label>

//                 {logoFile && !uploadingLogo && (
//                   <button
//                     type="button"
//                     onClick={handleUploadLogo}
//                     disabled={uploadingLogo}
//                     className="px-4 py-2 bg-green-600 text-white hover:bg-green-700 disabled:bg-gray-400 rounded-lg font-medium transition-colors flex items-center gap-2"
//                   >
//                     Upload
//                   </button>
//                 )}

//                 {logoPreview && (
//                   <button
//                     type="button"
//                     onClick={handleDeleteLogo}
//                     disabled={uploadingLogo}
//                     className="px-4 py-2 bg-red-600 text-white hover:bg-red-700 disabled:bg-gray-400 rounded-lg font-medium transition-colors flex items-center gap-2"
//                   >
//                     <FiTrash2 className="w-4 h-4" />
//                     Remove
//                   </button>
//                 )}

//                 {uploadingLogo && (
//                   <button
//                     type="button"
//                     disabled
//                     className="px-4 py-2 bg-gray-400 text-white rounded-lg font-medium flex items-center gap-2"
//                   >
//                     <FiLoader className="w-4 h-4 animate-spin" />
//                     Uploading...
//                   </button>
//                 )}
//               </div>

//               {logoFile && (
//                 <p className="text-xs text-gray-600 mt-2">
//                   Selected: {logoFile.name}
//                 </p>
//               )}
//             </div>
//           </div>

//           {/* SEO Settings */}
//           <div className="space-y-4 pt-6 border-t border-gray-200">
//             <h3 className="text-lg font-semibold text-gray-900">SEO Settings</h3>
            
//             <div>
//               <label className="block text-sm font-medium text-gray-700 mb-2">
//                 Meta Title
//               </label>
//               <input
//                 type="text"
//                 name="metaTitle"
//                 value={formData.metaTitle}
//                 onChange={handleInputChange}
//                 placeholder="Enter SEO title (60 characters)"
//                 maxLength="60"
//                 className="input-field w-full"
//               />
//               <p className="text-xs text-gray-500 mt-1">{formData.metaTitle.length}/60</p>
//             </div>

//             <div>
//               <label className="block text-sm font-medium text-gray-700 mb-2">
//                 Meta Description
//               </label>
//               <textarea
//                 name="metaDescription"
//                 value={formData.metaDescription}
//                 onChange={handleInputChange}
//                 placeholder="Enter SEO description (160 characters)"
//                 maxLength="160"
//                 rows="2"
//                 className="input-field w-full resize-none"
//               />
//               <p className="text-xs text-gray-500 mt-1">{formData.metaDescription.length}/160</p>
//             </div>

//             <div>
//               <label className="block text-sm font-medium text-gray-700 mb-2">
//                 Keywords
//               </label>
//               <input
//                 type="text"
//                 name="metaKeywords"
//                 value={formData.metaKeywords}
//                 onChange={handleInputChange}
//                 placeholder="Comma-separated keywords"
//                 className="input-field w-full"
//               />
//             </div>
//           </div>

//           {/* Custom Domain */}
//           <div className="space-y-4 pt-6 border-t border-gray-200">
//             <h3 className="text-lg font-semibold text-gray-900">Custom Domain</h3>
//             <p className="text-sm text-gray-600">Connect your own domain (e.g., example.com or www.example.com). Only one primary domain allowed.</p>

//             <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
//               <input
//                 type="text"
//                 placeholder="Enter custom domain (example.com)"
//                 value={website?.customDomain?.domain || ''}
//                 onChange={(e) => {
//                   const d = e.target.value
//                   // optimistic local update
//                   website.customDomain = website.customDomain || {}
//                   website.customDomain.domain = d
//                   setLogoFile(null) // no-op to force update? keep minimal
//                 }}
//                 className="input-field w-full col-span-2"
//               />

//               <div className="flex gap-2">
//                 <button
//                   type="button"
//                   onClick={async () => {
//                     if (!website || !website._id) return toast.error('Select a website first')
//                     const domain = (website.customDomain?.domain || '').trim()
//                     if (!domain) return toast.error('Enter a domain')
//                     try {
//                       setDomainAdding(true)
//                       const res = await axios.post(`/websites/${website._id}/custom-domain`, { domain })
//                       // Save returned customDomain on the website object so UI can show verification token
//                       if (res?.data?.customDomain) {
//                         website.customDomain = res.data.customDomain
//                       }
//                       // If server returned a verification token, surface it to the user
//                       const token = res?.data?.customDomain?.verificationToken
//                       if (token) {
//                         toast.success('Custom domain added. Verification token generated. Add it as a TXT record.')
//                         console.log('[Custom Domain] Verification token:', token)
//                       } else {
//                         toast.success('Custom domain added. Follow DNS instructions to verify.')
//                       }
//                     } catch (err) {
//                       console.error('Error adding domain:', err)
//                       toast.error(err.response?.data?.message || 'Failed to add domain')
//                     } finally {
//                       setDomainAdding(false)
//                     }
//                   }}
//                   disabled={domainAdding || domainVerifying || domainRemoving}
//                   className={`px-3 py-2 rounded-lg text-white ${domainAdding ? 'bg-gray-400' : 'bg-blue-600 hover:bg-blue-700'}`}
//                 >
//                   {domainAdding ? (
//                     <span className="inline-flex items-center gap-2"><FiLoader className="w-4 h-4 animate-spin" />Adding...</span>
//                   ) : 'Add Domain'}
//                 </button>

//                 <button
//                   type="button"
//                   onClick={async () => {
//                     if (!website || !website._id) return toast.error('Select a website first')
//                     try {
//                       setDomainVerifying(true)
//                       const res = await axios.post(`/websites/${website._id}/custom-domain/verify`)
//                       toast.success('Domain verified and activated')
//                     } catch (err) {
//                       console.error('Error verifying domain:', err)
//                       toast.error(err.response?.data?.message || 'Domain verification failed')
//                     } finally {
//                       setDomainVerifying(false)
//                     }
//                   }}
//                   disabled={domainAdding || domainVerifying || domainRemoving}
//                   className={`px-3 py-2 rounded-lg text-white ${domainVerifying ? 'bg-gray-400' : 'bg-green-600 hover:bg-green-700'}`}
//                 >
//                   {domainVerifying ? (
//                     <span className="inline-flex items-center gap-2"><FiLoader className="w-4 h-4 animate-spin" />Verifying...</span>
//                   ) : 'Verify'}
//                 </button>

//                 <button
//                   type="button"
//                   onClick={async () => {
//                     if (!website || !website._id) return toast.error('Select a website first')
//                     if (!window.confirm('Remove custom domain?')) return
//                     try {
//                       setDomainRemoving(true)
//                       await axios.delete(`/websites/${website._id}/custom-domain`)
//                       toast.success('Custom domain removed')
//                     } catch (err) {
//                       console.error('Error removing domain:', err)
//                       toast.error(err.response?.data?.message || 'Failed to remove domain')
//                     } finally {
//                       setDomainRemoving(false)
//                     }
//                   }}
//                   disabled={domainAdding || domainVerifying || domainRemoving}
//                   className={`px-3 py-2 rounded-lg text-white ${domainRemoving ? 'bg-gray-400' : 'bg-red-600 hover:bg-red-700'}`}
//                 >
//                   {domainRemoving ? (
//                     <span className="inline-flex items-center gap-2"><FiLoader className="w-4 h-4 animate-spin" />Removing...</span>
//                   ) : 'Remove'}
//                 </button>
//               </div>

//               <div className="col-span-3 text-xs text-gray-600">
//                 <p>DNS Instructions:</p>
//                 <ul className="list-disc ml-5">
//                   <li>Point your root domain or APEX to your DNS provider configuration — Render and Vercel do not provide static platform IPs.</li>
//                   <li>OR add a CNAME for a subdomain (e.g., <code>www</code>) pointing to the platform host: <strong>{process.env.REACT_APP_PLATFORM_PUBLIC_URL ? (() => { try { return new URL(process.env.REACT_APP_PLATFORM_PUBLIC_URL).hostname } catch { return process.env.REACT_APP_PLATFORM_PUBLIC_URL } })() : window.location.hostname}</strong></li>
//                   <li>Optional TXT verification: add a TXT record with the verification token shown in the website settings after adding the domain.</li>
//                 </ul>
//                 {website?.customDomain?.verificationToken && (
//                   <div className="mt-2 bg-yellow-50 p-2 rounded-sm border border-yellow-200">
//                     <p className="text-xs font-semibold text-yellow-800">Verification token (add as TXT record):</p>
//                     <p className="text-xs text-gray-700 font-mono break-all">{website.customDomain.verificationToken}</p>
//                   </div>
//                 )}
//               </div>
//             </div>
//           </div>

//           {/* Design Settings */}
//           <div className="space-y-4 pt-6 border-t border-gray-200">
//             <h3 className="text-lg font-semibold text-gray-900">Design Settings</h3>

//             {/* Colors */}
//             <div className="space-y-4">
//               <p className="text-sm text-gray-600">Colors</p>
//               <div className="grid grid-cols-3 gap-4">
//                 <div>
//                   <label className="block text-xs font-medium text-gray-700 mb-2">
//                     Primary Color
//                   </label>
//                   <div className="flex items-center gap-2">
//                     <input
//                       type="color"
//                       value={formData.primaryColor}
//                       onChange={(e) => handleColorChange('primaryColor', e.target.value)}
//                       className="w-12 h-10 rounded cursor-pointer border border-gray-300"
//                     />
//                     <input
//                       type="text"
//                       value={formData.primaryColor}
//                       onChange={(e) => handleColorChange('primaryColor', e.target.value)}
//                       className="input-field flex-1 font-mono text-sm"
//                       maxLength="7"
//                     />
//                   </div>
//                 </div>

//                 <div>
//                   <label className="block text-xs font-medium text-gray-700 mb-2">
//                     Secondary Color
//                   </label>
//                   <div className="flex items-center gap-2">
//                     <input
//                       type="color"
//                       value={formData.secondaryColor}
//                       onChange={(e) => handleColorChange('secondaryColor', e.target.value)}
//                       className="w-12 h-10 rounded cursor-pointer border border-gray-300"
//                     />
//                     <input
//                       type="text"
//                       value={formData.secondaryColor}
//                       onChange={(e) => handleColorChange('secondaryColor', e.target.value)}
//                       className="input-field flex-1 font-mono text-sm"
//                       maxLength="7"
//                     />
//                   </div>
//                 </div>

//                 <div>
//                   <label className="block text-xs font-medium text-gray-700 mb-2">
//                     Accent Color
//                   </label>
//                   <div className="flex items-center gap-2">
//                     <input
//                       type="color"
//                       value={formData.accentColor}
//                       onChange={(e) => handleColorChange('accentColor', e.target.value)}
//                       className="w-12 h-10 rounded cursor-pointer border border-gray-300"
//                     />
//                     <input
//                       type="text"
//                       value={formData.accentColor}
//                       onChange={(e) => handleColorChange('accentColor', e.target.value)}
//                       className="input-field flex-1 font-mono text-sm"
//                       maxLength="7"
//                     />
//                   </div>
//                 </div>
//               </div>
//             </div>

//             {/* Fonts */}
//             <div className="space-y-3">
//               <p className="text-sm text-gray-600">Fonts</p>
//               <div className="grid grid-cols-2 gap-4">
//                 <div>
//                   <label className="block text-xs font-medium text-gray-700 mb-2">
//                     Primary Font
//                   </label>
//                   <select
//                     name="primaryFont"
//                     value={formData.primaryFont}
//                     onChange={handleInputChange}
//                     className="input-field w-full"
//                   >
//                     <option value="Inter">Inter</option>
//                     <option value="Arial">Arial</option>
//                     <option value="Georgia">Georgia</option>
//                     <option value="Times New Roman">Times New Roman</option>
//                     <option value="Verdana">Verdana</option>
//                   </select>
//                 </div>
//                 <div>
//                   <label className="block text-xs font-medium text-gray-700 mb-2">
//                     Secondary Font
//                   </label>
//                   <select
//                     name="secondaryFont"
//                     value={formData.secondaryFont}
//                     onChange={handleInputChange}
//                     className="input-field w-full"
//                   >
//                     <option value="Inter">Inter</option>
//                     <option value="Arial">Arial</option>
//                     <option value="Georgia">Georgia</option>
//                     <option value="Times New Roman">Times New Roman</option>
//                     <option value="Verdana">Verdana</option>
//                   </select>
//                 </div>
//               </div>
//             </div>
//           </div>

//           {/* Action Buttons */}
//           <div className="flex gap-3 pt-6 border-t border-gray-200">
//             <button
//               type="button"
//               onClick={onClose}
//               disabled={isSaving}
//               className="flex-1 py-2 px-4 bg-gray-100 text-gray-700 hover:bg-gray-200 rounded-lg font-medium transition-colors disabled:opacity-50"
//             >
//               Cancel
//             </button>
//             <button
//               type="submit"
//               disabled={isSaving}
//               className="flex-1 py-2 px-4 bg-primary-600 text-white hover:bg-primary-700 disabled:bg-gray-400 rounded-lg font-medium transition-colors flex items-center justify-center gap-2 disabled:cursor-not-allowed"
//             >
//               {isSaving ? (
//                 <>
//                   <FiLoader className="w-4 h-4 animate-spin" /> Saving...
//                 </>
//               ) : (
//                 <>
//                   <FiSave className="w-4 h-4" /> Save Website
//                 </>
//               )}
//             </button>
//           </div>
//         </form>
//       </div>
//     </div>
//   )
// }



























// import { useState, useEffect } from 'react'
// import { FiX, FiLoader, FiSave, FiTrash2, FiUpload } from 'react-icons/fi'
// import toast from 'react-hot-toast'
// import axios from 'axios'

// export default function WebsiteEditorModal({ isOpen, onClose, onSubmit, website, categories, isSaving, mode, onLogoUpload, onLogoDelete }) {
//   const [formData, setFormData] = useState({
//     name: '',
//     description: '',
//     metaTitle: '',
//     metaDescription: '',
//     metaKeywords: '',
//     primaryColor: '#3B82F6',
//     secondaryColor: '#1F2937',
//     accentColor: '#F97316',
//     primaryFont: 'Inter',
//     secondaryFont: 'Inter'
//   })
//   const [logoFile, setLogoFile] = useState(null)
//   const [logoPreview, setLogoPreview] = useState(null)
//   const [uploadingLogo, setUploadingLogo] = useState(false)
//   const [domainAdding, setDomainAdding] = useState(false)
//   const [domainVerifying, setDomainVerifying] = useState(false)
//   const [domainRemoving, setDomainRemoving] = useState(false)

//   // Helper function to convert relative logo paths to absolute URLs
//   const getAbsoluteLogoUrl = (logoUrl) => {
//     if (!logoUrl) return null
    
//     // If already absolute, return as-is
//     if (logoUrl.startsWith('http://') || logoUrl.startsWith('https://')) {
//       return logoUrl
//     }
    
//     // Convert relative path to absolute using backend origin
//     const backendOrigin = axios.defaults.baseURL 
//       ? axios.defaults.baseURL.replace(/\/api\/?$/, '') 
//       : ''
    
//     if (!backendOrigin) {
//       console.warn('[Logo URL] Cannot determine backend origin, using relative URL:', logoUrl)
//       return logoUrl
//     }
    
//     const pathUrl = logoUrl.startsWith('/') ? logoUrl : `/${logoUrl}`
//     const absoluteUrl = `${backendOrigin}${pathUrl}`
//     console.log('[Logo URL] Converted relative to absolute:', { original: logoUrl, absolute: absoluteUrl })
//     return absoluteUrl
//   }

//   useEffect(() => {
//     if (website && isOpen) {
//       setFormData({
//         name: website.name || '',
//         description: website.description || '',
//         metaTitle: website.seo?.metaTitle || '',
//         metaDescription: website.seo?.metaDescription || '',
//         metaKeywords: website.seo?.metaKeywords || '',
//         primaryColor: website.colors?.primary || '#3B82F6',
//         secondaryColor: website.colors?.secondary || '#1F2937',
//         accentColor: website.colors?.accent || '#F97316',
//         primaryFont: website.fonts?.primary || 'Inter',
//         secondaryFont: website.fonts?.secondary || 'Inter'
//       })
      
//       // Display the saved/uploaded logo from backend if it exists
//       if (website.logo?.url) {
//         const absoluteUrl = getAbsoluteLogoUrl(website.logo.url)
//         setLogoPreview(absoluteUrl)
//         console.log('[WebsiteEditor] Showing saved logo from backend:', absoluteUrl)
//       } else {
//         setLogoPreview(null)
//         console.log('[WebsiteEditor] No saved logo found')
//       }
      
//       setLogoFile(null)
//     }
//   }, [website, isOpen])

//   const handleInputChange = (e) => {
//     const { name, value } = e.target
//     setFormData(prev => ({
//       ...prev,
//       [name]: value
//     }))
//   }

//   const handleColorChange = (colorField, value) => {
//     setFormData(prev => ({
//       ...prev,
//       [colorField]: value
//     }))
//   }

//   const handleLogoChange = (e) => {
//     const file = e.target.files?.[0]
//     if (!file) return

//     if (!file.type.startsWith('image/')) {
//       toast.error('Please select an image file')
//       return
//     }

//     if (file.size > 5 * 1024 * 1024) {
//       toast.error('File size must be less than 5MB')
//       return
//     }

//     // Store the file for upload - don't show preview until upload completes
//     setLogoFile(file)
//     console.log('[WebsiteEditor] File selected for upload:', file.name)
//   }

//   const handleUploadLogo = async () => {
//     if (!logoFile || !website?._id) {
//       toast.error('No logo file selected')
//       return
//     }

//     try {
//       setUploadingLogo(true)
//       const formDataUpload = new FormData()
//       formDataUpload.append('logo', logoFile)

//       console.log('[Logo Upload] Starting upload for website:', website._id)
//       console.log('[Logo Upload] File:', logoFile.name, 'Size:', logoFile.size, 'Type:', logoFile.type)

//       const response = await axios.post(`/websites/${website._id}/logo`, formDataUpload, {
//         headers: {
//           'Content-Type': 'multipart/form-data'
//         },
//         timeout: 30000 // 30 second timeout
//       })

//       console.log('[Logo Upload] Response received:', response.status)
//       console.log('[Logo Upload] Response data:', response.data)
      
//       const returnedWebsite = response.data?.website
//       const returnedLogoUrl = returnedWebsite?.logo?.url
      
//       console.log('[Logo Upload] Returned website:', returnedWebsite ? 'YES' : 'NO')
//       console.log('[Logo Upload] Returned logo URL:', returnedLogoUrl)
      
//       if (returnedLogoUrl) {
//         // Validate the URL format
//         if (!returnedLogoUrl.startsWith('/') && !returnedLogoUrl.startsWith('http')) {
//           console.warn('[Logo Upload] WARNING: URL does not start with / or http:', returnedLogoUrl)
//         }
        
//         // Convert relative URL to absolute for image loading in browser
//         const absoluteUrl = getAbsoluteLogoUrl(returnedLogoUrl)
//         setLogoPreview(absoluteUrl)
//         console.log('[Logo Upload] Logo preview set to:', absoluteUrl)
//         toast.success('✅ Logo uploaded successfully! It will appear on your public website, replacing the website name.')
        
//         // Call parent callback to update website data with the uploaded logo
//         if (onLogoUpload && returnedWebsite) {
//           try {
//             onLogoUpload(returnedWebsite)
//             console.log('[Logo Upload] Called onLogoUpload callback successfully')
//           } catch (callbackError) {
//             console.error('[Logo Upload] Error in onLogoUpload callback:', callbackError)
//             // Don't fail the whole operation if callback fails
//           }
//         } else {
//           console.warn('[Logo Upload] onLogoUpload callback not available or returnedWebsite missing')
//         }
//       } else {
//         console.error('[Logo Upload] Logo URL is missing from response')
//         console.error('[Logo Upload] Full response:', JSON.stringify(response.data))
//         toast.error('Upload succeeded but logo URL is missing from server response')
//       }
      
//       // Clear file input after successful upload
//       setLogoFile(null)
//     } catch (error) {
//       console.error('[Logo Upload] Error occurred:')
//       console.error('[Logo Upload] Error message:', error.message)
//       console.error('[Logo Upload] Error code:', error.code)
//       console.error('[Logo Upload] Status:', error.response?.status)
//       console.error('[Logo Upload] Status text:', error.response?.statusText)
//       console.error('[Logo Upload] Response data:', error.response?.data)
//       console.error('[Logo Upload] Full error:', error)
      
//       let errorMessage = 'Failed to upload logo'
//       if (error.response?.data?.message) {
//         errorMessage = error.response.data.message
//       } else if (error.code === 'ECONNABORTED') {
//         errorMessage = 'Upload timeout - please try again'
//       } else if (error.message) {
//         errorMessage = error.message
//       }
      
//       toast.error(errorMessage)
//     } finally {
//       setUploadingLogo(false)
//     }
//   }

//   const handleDeleteLogo = async () => {
//     if (!website?._id) return

//     if (!window.confirm('Are you sure you want to delete the logo?')) return

//     try {
//       setUploadingLogo(true)
//       console.log('[Logo Delete] Starting delete for website:', website._id)
      
//       const response = await axios.delete(`/websites/${website._id}/logo`, {
//         timeout: 30000 // 30 second timeout
//       })
      
//       console.log('[Logo Delete] Response received:', response.status)
//       console.log('[Logo Delete] Response data:', response.data)
      
//       const updatedWebsite = response.data?.website
//       console.log('[Logo Delete] Updated website:', updatedWebsite ? 'YES' : 'NO')
      
//       toast.success('Logo deleted successfully')
//       setLogoPreview(null)
//       setLogoFile(null)
      
//       // Call parent callback to update website data after logo deletion
//       if (onLogoDelete && updatedWebsite) {
//         try {
//           onLogoDelete(updatedWebsite)
//           console.log('[Logo Delete] Called onLogoDelete callback successfully')
//         } catch (callbackError) {
//           console.error('[Logo Delete] Error in onLogoDelete callback:', callbackError)
//           // Don't fail the whole operation if callback fails
//         }
//       } else {
//         console.warn('[Logo Delete] onLogoDelete callback not available or updatedWebsite missing')
//       }
//     } catch (error) {
//       console.error('[Logo Delete] Error occurred:')
//       console.error('[Logo Delete] Error message:', error.message)
//       console.error('[Logo Delete] Status:', error.response?.status)
//       console.error('[Logo Delete] Response data:', error.response?.data)
      
//       let errorMessage = 'Failed to delete logo'
//       if (error.response?.data?.message) {
//         errorMessage = error.response.data.message
//       } else if (error.code === 'ECONNABORTED') {
//         errorMessage = 'Delete timeout - please try again'
//       }
      
//       toast.error(errorMessage)
//     } finally {
//       setUploadingLogo(false)
//     }
//   }

//   const handleSubmit = (e) => {
//     e.preventDefault()
    
//     if (!formData.name.trim()) {
//       toast.error('Website name is required')
//       return
//     }

//     if (!website || !website._id) {
//       toast.error('Invalid website - please select a website to edit')
//       return
//     }

//     const submitData = {
//       name: formData.name.trim(),
//       description: formData.description,
//       seo: {
//         metaTitle: formData.metaTitle,
//         metaDescription: formData.metaDescription,
//         metaKeywords: formData.metaKeywords
//       },
//       colors: {
//         primary: formData.primaryColor,
//         secondary: formData.secondaryColor,
//         accent: formData.accentColor
//       },
//       fonts: {
//         primary: formData.primaryFont,
//         secondary: formData.secondaryFont
//       }
//     }

//     onSubmit(submitData)
//   }

//   if (!isOpen) return null

//   return (
//     <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
//       <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
//         {/* Header */}
//         <div className="flex justify-between items-center p-6 border-b border-gray-200 sticky top-0 bg-white">
//           <h2 className="text-2xl font-bold text-gray-900">Edit Website</h2>
//           <button
//             onClick={onClose}
//             disabled={isSaving}
//             className="p-1 hover:bg-gray-100 rounded-lg transition-colors disabled:opacity-50"
//           >
//             <FiX className="w-6 h-6" />
//           </button>
//         </div>

//         {/* Content */}
//         <form onSubmit={handleSubmit} className="p-6 space-y-6">
//           {/* Basic Information */}
//           <div className="space-y-4">
//             <h3 className="text-lg font-semibold text-gray-900">Basic Information</h3>
            
//             <div>
//               <label className="block text-sm font-medium text-gray-700 mb-2">
//                 Website Name *
//               </label>
//               <input
//                 type="text"
//                 name="name"
//                 value={formData.name}
//                 onChange={handleInputChange}
//                 placeholder="Enter website name"
//                 className="input-field w-full"
//                 required
//               />
//             </div>

//             <div>
//               <label className="block text-sm font-medium text-gray-700 mb-2">
//                 Description
//               </label>
//               <textarea
//                 name="description"
//                 value={formData.description}
//                 onChange={handleInputChange}
//                 placeholder="Brief description of your website"
//                 rows="3"
//                 className="input-field w-full resize-none"
//               />
//             </div>

//             {/* Website Logo */}
//             <div className="mt-6 p-4 bg-gray-50 rounded-lg border border-gray-200">
//               <label className="block text-sm font-medium text-gray-700 mb-3">
//                 Website Logo
//               </label>
//               <p className="text-xs text-gray-600 mb-3">
//                 Upload a logo to display in the header of your published public website. Once uploaded, the logo will replace your website name. If no logo is uploaded, your website name will be displayed instead. Recommended: square image (PNG, JPG, GIF, WebP, or SVG). Max 5MB.
//               </p>

//               {/* Logo Preview */}
//               {logoPreview && (
//                 <div className="mb-4">
//                   <div className="relative inline-block">
//                     <img
//                       src={logoPreview}
//                       alt="Logo preview"
//                       className="max-w-xs max-h-24 rounded-lg border border-gray-300"
//                       onLoad={() => console.log('[Logo Preview] Image loaded successfully from:', logoPreview)}
//                       onError={(e) => {
//                         console.error('[Logo Preview] Image failed to load')
//                         console.error('[Logo Preview] Attempted URL:', logoPreview)
//                         console.error('[Logo Preview] Backend origin:', axios.defaults.baseURL)
//                         console.error('[Logo Preview] Event:', e)
//                         toast.error('Logo image failed to load at: ' + logoPreview)
//                       }}
//                     />
//                   </div>
//                 </div>
//               )}

//               {/* File Input */}
//               <div className="flex gap-2">
//                 <label className="flex-1">
//                   <input
//                     type="file"
//                     accept="image/*"
//                     onChange={handleLogoChange}
//                     disabled={uploadingLogo}
//                     className="hidden"
//                   />
//                   <span className="inline-flex items-center gap-2 px-4 py-2 bg-blue-600 text-white hover:bg-blue-700 disabled:bg-gray-400 rounded-lg font-medium transition-colors cursor-pointer w-full justify-center">
//                     <FiUpload className="w-4 h-4" />
//                     Choose File
//                   </span>
//                 </label>

//                 {logoFile && !uploadingLogo && (
//                   <button
//                     type="button"
//                     onClick={handleUploadLogo}
//                     disabled={uploadingLogo}
//                     className="px-4 py-2 bg-green-600 text-white hover:bg-green-700 disabled:bg-gray-400 rounded-lg font-medium transition-colors flex items-center gap-2"
//                   >
//                     Upload
//                   </button>
//                 )}

//                 {logoPreview && (
//                   <button
//                     type="button"
//                     onClick={handleDeleteLogo}
//                     disabled={uploadingLogo}
//                     className="px-4 py-2 bg-red-600 text-white hover:bg-red-700 disabled:bg-gray-400 rounded-lg font-medium transition-colors flex items-center gap-2"
//                   >
//                     <FiTrash2 className="w-4 h-4" />
//                     Remove
//                   </button>
//                 )}

//                 {uploadingLogo && (
//                   <button
//                     type="button"
//                     disabled
//                     className="px-4 py-2 bg-gray-400 text-white rounded-lg font-medium flex items-center gap-2"
//                   >
//                     <FiLoader className="w-4 h-4 animate-spin" />
//                     Uploading...
//                   </button>
//                 )}
//               </div>

//               {logoFile && (
//                 <p className="text-xs text-gray-600 mt-2">
//                   Selected: {logoFile.name}
//                 </p>
//               )}
//             </div>
//           </div>

//           {/* SEO Settings */}
//           <div className="space-y-4 pt-6 border-t border-gray-200">
//             <h3 className="text-lg font-semibold text-gray-900">SEO Settings</h3>
            
//             <div>
//               <label className="block text-sm font-medium text-gray-700 mb-2">
//                 Meta Title
//               </label>
//               <input
//                 type="text"
//                 name="metaTitle"
//                 value={formData.metaTitle}
//                 onChange={handleInputChange}
//                 placeholder="Enter SEO title (60 characters)"
//                 maxLength="60"
//                 className="input-field w-full"
//               />
//               <p className="text-xs text-gray-500 mt-1">{formData.metaTitle.length}/60</p>
//             </div>

//             <div>
//               <label className="block text-sm font-medium text-gray-700 mb-2">
//                 Meta Description
//               </label>
//               <textarea
//                 name="metaDescription"
//                 value={formData.metaDescription}
//                 onChange={handleInputChange}
//                 placeholder="Enter SEO description (160 characters)"
//                 maxLength="160"
//                 rows="2"
//                 className="input-field w-full resize-none"
//               />
//               <p className="text-xs text-gray-500 mt-1">{formData.metaDescription.length}/160</p>
//             </div>

//             <div>
//               <label className="block text-sm font-medium text-gray-700 mb-2">
//                 Keywords
//               </label>
//               <input
//                 type="text"
//                 name="metaKeywords"
//                 value={formData.metaKeywords}
//                 onChange={handleInputChange}
//                 placeholder="Comma-separated keywords"
//                 className="input-field w-full"
//               />
//             </div>
//           </div>

//           {/* Custom Domain */}
//           <div className="space-y-4 pt-6 border-t border-gray-200">
//             <h3 className="text-lg font-semibold text-gray-900">Custom Domain</h3>
//             <p className="text-sm text-gray-600">Connect your own domain (e.g., example.com or www.example.com). Only one primary domain allowed.</p>

//             <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
//               <input
//                 type="text"
//                 placeholder="Enter custom domain (example.com)"
//                 value={website?.customDomain?.domain || ''}
//                 onChange={(e) => {
//                   const d = e.target.value
//                   // optimistic local update
//                   website.customDomain = website.customDomain || {}
//                   website.customDomain.domain = d
//                   setLogoFile(null) // no-op to force update? keep minimal
//                 }}
//                 className="input-field w-full col-span-2"
//               />

//               <div className="flex gap-2">
//                 <button
//                   type="button"
//                   onClick={async () => {
//                     if (!website || !website._id) return toast.error('Select a website first')
//                     const domain = (website.customDomain?.domain || '').trim()
//                     if (!domain) return toast.error('Enter a domain')
//                     try {
//                       setDomainAdding(true)
//                       const res = await axios.post(`/websites/${website._id}/custom-domain`, { domain })
//                       toast.success('Custom domain added. Follow DNS instructions to verify.')
//                     } catch (err) {
//                       console.error('Error adding domain:', err)
//                       toast.error(err.response?.data?.message || 'Failed to add domain')
//                     } finally {
//                       setDomainAdding(false)
//                     }
//                   }}
//                   disabled={domainAdding || domainVerifying || domainRemoving}
//                   className={`px-3 py-2 rounded-lg text-white ${domainAdding ? 'bg-gray-400' : 'bg-blue-600 hover:bg-blue-700'}`}
//                 >
//                   {domainAdding ? (
//                     <span className="inline-flex items-center gap-2"><FiLoader className="w-4 h-4 animate-spin" />Adding...</span>
//                   ) : 'Add Domain'}
//                 </button>

//                 <button
//                   type="button"
//                   onClick={async () => {
//                     if (!website || !website._id) return toast.error('Select a website first')
//                     try {
//                       setDomainVerifying(true)
//                       const res = await axios.post(`/websites/${website._id}/custom-domain/verify`)
//                       toast.success('Domain verified and activated')
//                     } catch (err) {
//                       console.error('Error verifying domain:', err)
//                       toast.error(err.response?.data?.message || 'Domain verification failed')
//                     } finally {
//                       setDomainVerifying(false)
//                     }
//                   }}
//                   disabled={domainAdding || domainVerifying || domainRemoving}
//                   className={`px-3 py-2 rounded-lg text-white ${domainVerifying ? 'bg-gray-400' : 'bg-green-600 hover:bg-green-700'}`}
//                 >
//                   {domainVerifying ? (
//                     <span className="inline-flex items-center gap-2"><FiLoader className="w-4 h-4 animate-spin" />Verifying...</span>
//                   ) : 'Verify'}
//                 </button>

//                 <button
//                   type="button"
//                   onClick={async () => {
//                     if (!website || !website._id) return toast.error('Select a website first')
//                     if (!window.confirm('Remove custom domain?')) return
//                     try {
//                       setDomainRemoving(true)
//                       await axios.delete(`/websites/${website._id}/custom-domain`)
//                       toast.success('Custom domain removed')
//                     } catch (err) {
//                       console.error('Error removing domain:', err)
//                       toast.error(err.response?.data?.message || 'Failed to remove domain')
//                     } finally {
//                       setDomainRemoving(false)
//                     }
//                   }}
//                   disabled={domainAdding || domainVerifying || domainRemoving}
//                   className={`px-3 py-2 rounded-lg text-white ${domainRemoving ? 'bg-gray-400' : 'bg-red-600 hover:bg-red-700'}`}
//                 >
//                   {domainRemoving ? (
//                     <span className="inline-flex items-center gap-2"><FiLoader className="w-4 h-4 animate-spin" />Removing...</span>
//                   ) : 'Remove'}
//                 </button>
//               </div>

//               <div className="col-span-3 text-xs text-gray-600">
//                 <p>DNS Instructions:</p>
//                 <ul className="list-disc ml-5">
//                   <li>Point your root domain or APEX to your DNS provider configuration — Render and Vercel do not provide static platform IPs.</li>
//                   <li>OR add a CNAME for a subdomain (e.g., <code>www</code>) pointing to the platform host: <strong>{process.env.REACT_APP_PLATFORM_PUBLIC_URL ? (() => { try { return new URL(process.env.REACT_APP_PLATFORM_PUBLIC_URL).hostname } catch { return process.env.REACT_APP_PLATFORM_PUBLIC_URL } })() : window.location.hostname}</strong></li>
//                   <li>Optional TXT verification: add a TXT record with the verification token shown in the website settings after adding the domain.</li>
//                 </ul>
//               </div>
//             </div>
//           </div>

//           {/* Design Settings */}
//           <div className="space-y-4 pt-6 border-t border-gray-200">
//             <h3 className="text-lg font-semibold text-gray-900">Design Settings</h3>

//             {/* Colors */}
//             <div className="space-y-4">
//               <p className="text-sm text-gray-600">Colors</p>
//               <div className="grid grid-cols-3 gap-4">
//                 <div>
//                   <label className="block text-xs font-medium text-gray-700 mb-2">
//                     Primary Color
//                   </label>
//                   <div className="flex items-center gap-2">
//                     <input
//                       type="color"
//                       value={formData.primaryColor}
//                       onChange={(e) => handleColorChange('primaryColor', e.target.value)}
//                       className="w-12 h-10 rounded cursor-pointer border border-gray-300"
//                     />
//                     <input
//                       type="text"
//                       value={formData.primaryColor}
//                       onChange={(e) => handleColorChange('primaryColor', e.target.value)}
//                       className="input-field flex-1 font-mono text-sm"
//                       maxLength="7"
//                     />
//                   </div>
//                 </div>

//                 <div>
//                   <label className="block text-xs font-medium text-gray-700 mb-2">
//                     Secondary Color
//                   </label>
//                   <div className="flex items-center gap-2">
//                     <input
//                       type="color"
//                       value={formData.secondaryColor}
//                       onChange={(e) => handleColorChange('secondaryColor', e.target.value)}
//                       className="w-12 h-10 rounded cursor-pointer border border-gray-300"
//                     />
//                     <input
//                       type="text"
//                       value={formData.secondaryColor}
//                       onChange={(e) => handleColorChange('secondaryColor', e.target.value)}
//                       className="input-field flex-1 font-mono text-sm"
//                       maxLength="7"
//                     />
//                   </div>
//                 </div>

//                 <div>
//                   <label className="block text-xs font-medium text-gray-700 mb-2">
//                     Accent Color
//                   </label>
//                   <div className="flex items-center gap-2">
//                     <input
//                       type="color"
//                       value={formData.accentColor}
//                       onChange={(e) => handleColorChange('accentColor', e.target.value)}
//                       className="w-12 h-10 rounded cursor-pointer border border-gray-300"
//                     />
//                     <input
//                       type="text"
//                       value={formData.accentColor}
//                       onChange={(e) => handleColorChange('accentColor', e.target.value)}
//                       className="input-field flex-1 font-mono text-sm"
//                       maxLength="7"
//                     />
//                   </div>
//                 </div>
//               </div>
//             </div>

//             {/* Fonts */}
//             <div className="space-y-3">
//               <p className="text-sm text-gray-600">Fonts</p>
//               <div className="grid grid-cols-2 gap-4">
//                 <div>
//                   <label className="block text-xs font-medium text-gray-700 mb-2">
//                     Primary Font
//                   </label>
//                   <select
//                     name="primaryFont"
//                     value={formData.primaryFont}
//                     onChange={handleInputChange}
//                     className="input-field w-full"
//                   >
//                     <option value="Inter">Inter</option>
//                     <option value="Arial">Arial</option>
//                     <option value="Georgia">Georgia</option>
//                     <option value="Times New Roman">Times New Roman</option>
//                     <option value="Verdana">Verdana</option>
//                   </select>
//                 </div>
//                 <div>
//                   <label className="block text-xs font-medium text-gray-700 mb-2">
//                     Secondary Font
//                   </label>
//                   <select
//                     name="secondaryFont"
//                     value={formData.secondaryFont}
//                     onChange={handleInputChange}
//                     className="input-field w-full"
//                   >
//                     <option value="Inter">Inter</option>
//                     <option value="Arial">Arial</option>
//                     <option value="Georgia">Georgia</option>
//                     <option value="Times New Roman">Times New Roman</option>
//                     <option value="Verdana">Verdana</option>
//                   </select>
//                 </div>
//               </div>
//             </div>
//           </div>

//           {/* Action Buttons */}
//           <div className="flex gap-3 pt-6 border-t border-gray-200">
//             <button
//               type="button"
//               onClick={onClose}
//               disabled={isSaving}
//               className="flex-1 py-2 px-4 bg-gray-100 text-gray-700 hover:bg-gray-200 rounded-lg font-medium transition-colors disabled:opacity-50"
//             >
//               Cancel
//             </button>
//             <button
//               type="submit"
//               disabled={isSaving}
//               className="flex-1 py-2 px-4 bg-primary-600 text-white hover:bg-primary-700 disabled:bg-gray-400 rounded-lg font-medium transition-colors flex items-center justify-center gap-2 disabled:cursor-not-allowed"
//             >
//               {isSaving ? (
//                 <>
//                   <FiLoader className="w-4 h-4 animate-spin" /> Saving...
//                 </>
//               ) : (
//                 <>
//                   <FiSave className="w-4 h-4" /> Save Website
//                 </>
//               )}
//             </button>
//           </div>
//         </form>
//       </div>
//     </div>
//   )
// }










// import { useState, useEffect } from 'react'
// import { FiX, FiLoader, FiSave, FiTrash2, FiUpload } from 'react-icons/fi'
// import toast from 'react-hot-toast'
// import axios from 'axios'

// export default function WebsiteEditorModal({ isOpen, onClose, onSubmit, website, categories, isSaving, mode, onLogoUpload, onLogoDelete }) {
//   const [formData, setFormData] = useState({
//     name: '',
//     description: '',
//     metaTitle: '',
//     metaDescription: '',
//     metaKeywords: '',
//     primaryColor: '#3B82F6',
//     secondaryColor: '#1F2937',
//     accentColor: '#F97316',
//     primaryFont: 'Inter',
//     secondaryFont: 'Inter'
//   })
//   const [logoFile, setLogoFile] = useState(null)
//   const [logoPreview, setLogoPreview] = useState(null)
//   const [uploadingLogo, setUploadingLogo] = useState(false)
//   const [domainAdding, setDomainAdding] = useState(false)
//   const [domainVerifying, setDomainVerifying] = useState(false)
//   const [domainRemoving, setDomainRemoving] = useState(false)

//   useEffect(() => {
//     if (website && isOpen) {
//       setFormData({
//         name: website.name || '',
//         description: website.description || '',
//         metaTitle: website.seo?.metaTitle || '',
//         metaDescription: website.seo?.metaDescription || '',
//         metaKeywords: website.seo?.metaKeywords || '',
//         primaryColor: website.colors?.primary || '#3B82F6',
//         secondaryColor: website.colors?.secondary || '#1F2937',
//         accentColor: website.colors?.accent || '#F97316',
//         primaryFont: website.fonts?.primary || 'Inter',
//         secondaryFont: website.fonts?.secondary || 'Inter'
//       })
      
//       // Display the saved/uploaded logo from backend if it exists
//       if (website.logo?.url) {
//         setLogoPreview(website.logo.url)
//         console.log('[WebsiteEditor] Showing saved logo from backend:', website.logo.url)
//       } else {
//         setLogoPreview(null)
//         console.log('[WebsiteEditor] No saved logo found')
//       }
      
//       setLogoFile(null)
//     }
//   }, [website, isOpen])

//   const handleInputChange = (e) => {
//     const { name, value } = e.target
//     setFormData(prev => ({
//       ...prev,
//       [name]: value
//     }))
//   }

//   const handleColorChange = (colorField, value) => {
//     setFormData(prev => ({
//       ...prev,
//       [colorField]: value
//     }))
//   }

//   const handleLogoChange = (e) => {
//     const file = e.target.files?.[0]
//     if (!file) return

//     if (!file.type.startsWith('image/')) {
//       toast.error('Please select an image file')
//       return
//     }

//     if (file.size > 5 * 1024 * 1024) {
//       toast.error('File size must be less than 5MB')
//       return
//     }

//     // Store the file for upload - don't show preview until upload completes
//     setLogoFile(file)
//     console.log('[WebsiteEditor] File selected for upload:', file.name)
//   }

//   const handleUploadLogo = async () => {
//     if (!logoFile || !website?._id) {
//       toast.error('No logo file selected')
//       return
//     }

//     try {
//       setUploadingLogo(true)
//       const formDataUpload = new FormData()
//       formDataUpload.append('logo', logoFile)

//       console.log('[Logo Upload] Starting upload for website:', website._id)
//       console.log('[Logo Upload] File:', logoFile.name, 'Size:', logoFile.size, 'Type:', logoFile.type)

//       const response = await axios.post(`/websites/${website._id}/logo`, formDataUpload, {
//         headers: {
//           'Content-Type': 'multipart/form-data'
//         },
//         timeout: 30000 // 30 second timeout
//       })

//       console.log('[Logo Upload] Response received:', response.status)
//       console.log('[Logo Upload] Response data:', response.data)
      
//       const returnedWebsite = response.data?.website
//       const returnedLogoUrl = returnedWebsite?.logo?.url
      
//       console.log('[Logo Upload] Returned website:', returnedWebsite ? 'YES' : 'NO')
//       console.log('[Logo Upload] Returned logo URL:', returnedLogoUrl)
      
//       if (returnedLogoUrl) {
//         // Validate the URL format
//         if (!returnedLogoUrl.startsWith('/') && !returnedLogoUrl.startsWith('http')) {
//           console.warn('[Logo Upload] WARNING: URL does not start with / or http:', returnedLogoUrl)
//         }
        
//         // Display the exact uploaded logo URL as returned by backend
//         setLogoPreview(returnedLogoUrl)
//         console.log('[Logo Upload] Logo preview set to:', returnedLogoUrl)
//         toast.success('✅ Logo uploaded successfully! It will appear on your public website, replacing the website name.')
        
//         // Call parent callback to update website data with the uploaded logo
//         if (onLogoUpload && returnedWebsite) {
//           try {
//             onLogoUpload(returnedWebsite)
//             console.log('[Logo Upload] Called onLogoUpload callback successfully')
//           } catch (callbackError) {
//             console.error('[Logo Upload] Error in onLogoUpload callback:', callbackError)
//             // Don't fail the whole operation if callback fails
//           }
//         } else {
//           console.warn('[Logo Upload] onLogoUpload callback not available or returnedWebsite missing')
//         }
//       } else {
//         console.error('[Logo Upload] Logo URL is missing from response')
//         console.error('[Logo Upload] Full response:', JSON.stringify(response.data))
//         toast.error('Upload succeeded but logo URL is missing from server response')
//       }
      
//       // Clear file input after successful upload
//       setLogoFile(null)
//     } catch (error) {
//       console.error('[Logo Upload] Error occurred:')
//       console.error('[Logo Upload] Error message:', error.message)
//       console.error('[Logo Upload] Error code:', error.code)
//       console.error('[Logo Upload] Status:', error.response?.status)
//       console.error('[Logo Upload] Status text:', error.response?.statusText)
//       console.error('[Logo Upload] Response data:', error.response?.data)
//       console.error('[Logo Upload] Full error:', error)
      
//       let errorMessage = 'Failed to upload logo'
//       if (error.response?.data?.message) {
//         errorMessage = error.response.data.message
//       } else if (error.code === 'ECONNABORTED') {
//         errorMessage = 'Upload timeout - please try again'
//       } else if (error.message) {
//         errorMessage = error.message
//       }
      
//       toast.error(errorMessage)
//     } finally {
//       setUploadingLogo(false)
//     }
//   }

//   const handleDeleteLogo = async () => {
//     if (!website?._id) return

//     if (!window.confirm('Are you sure you want to delete the logo?')) return

//     try {
//       setUploadingLogo(true)
//       console.log('[Logo Delete] Starting delete for website:', website._id)
      
//       const response = await axios.delete(`/websites/${website._id}/logo`, {
//         timeout: 30000 // 30 second timeout
//       })
      
//       console.log('[Logo Delete] Response received:', response.status)
//       console.log('[Logo Delete] Response data:', response.data)
      
//       const updatedWebsite = response.data?.website
//       console.log('[Logo Delete] Updated website:', updatedWebsite ? 'YES' : 'NO')
      
//       toast.success('Logo deleted successfully')
//       setLogoPreview(null)
//       setLogoFile(null)
      
//       // Call parent callback to update website data after logo deletion
//       if (onLogoDelete && updatedWebsite) {
//         try {
//           onLogoDelete(updatedWebsite)
//           console.log('[Logo Delete] Called onLogoDelete callback successfully')
//         } catch (callbackError) {
//           console.error('[Logo Delete] Error in onLogoDelete callback:', callbackError)
//           // Don't fail the whole operation if callback fails
//         }
//       } else {
//         console.warn('[Logo Delete] onLogoDelete callback not available or updatedWebsite missing')
//       }
//     } catch (error) {
//       console.error('[Logo Delete] Error occurred:')
//       console.error('[Logo Delete] Error message:', error.message)
//       console.error('[Logo Delete] Status:', error.response?.status)
//       console.error('[Logo Delete] Response data:', error.response?.data)
      
//       let errorMessage = 'Failed to delete logo'
//       if (error.response?.data?.message) {
//         errorMessage = error.response.data.message
//       } else if (error.code === 'ECONNABORTED') {
//         errorMessage = 'Delete timeout - please try again'
//       }
      
//       toast.error(errorMessage)
//     } finally {
//       setUploadingLogo(false)
//     }
//   }

//   const handleSubmit = (e) => {
//     e.preventDefault()
    
//     if (!formData.name.trim()) {
//       toast.error('Website name is required')
//       return
//     }

//     if (!website || !website._id) {
//       toast.error('Invalid website - please select a website to edit')
//       return
//     }

//     const submitData = {
//       name: formData.name.trim(),
//       description: formData.description,
//       seo: {
//         metaTitle: formData.metaTitle,
//         metaDescription: formData.metaDescription,
//         metaKeywords: formData.metaKeywords
//       },
//       colors: {
//         primary: formData.primaryColor,
//         secondary: formData.secondaryColor,
//         accent: formData.accentColor
//       },
//       fonts: {
//         primary: formData.primaryFont,
//         secondary: formData.secondaryFont
//       }
//     }

//     onSubmit(submitData)
//   }

//   if (!isOpen) return null

//   return (
//     <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
//       <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
//         {/* Header */}
//         <div className="flex justify-between items-center p-6 border-b border-gray-200 sticky top-0 bg-white">
//           <h2 className="text-2xl font-bold text-gray-900">Edit Website</h2>
//           <button
//             onClick={onClose}
//             disabled={isSaving}
//             className="p-1 hover:bg-gray-100 rounded-lg transition-colors disabled:opacity-50"
//           >
//             <FiX className="w-6 h-6" />
//           </button>
//         </div>

//         {/* Content */}
//         <form onSubmit={handleSubmit} className="p-6 space-y-6">
//           {/* Basic Information */}
//           <div className="space-y-4">
//             <h3 className="text-lg font-semibold text-gray-900">Basic Information</h3>
            
//             <div>
//               <label className="block text-sm font-medium text-gray-700 mb-2">
//                 Website Name *
//               </label>
//               <input
//                 type="text"
//                 name="name"
//                 value={formData.name}
//                 onChange={handleInputChange}
//                 placeholder="Enter website name"
//                 className="input-field w-full"
//                 required
//               />
//             </div>

//             <div>
//               <label className="block text-sm font-medium text-gray-700 mb-2">
//                 Description
//               </label>
//               <textarea
//                 name="description"
//                 value={formData.description}
//                 onChange={handleInputChange}
//                 placeholder="Brief description of your website"
//                 rows="3"
//                 className="input-field w-full resize-none"
//               />
//             </div>

//             {/* Website Logo */}
//             <div className="mt-6 p-4 bg-gray-50 rounded-lg border border-gray-200">
//               <label className="block text-sm font-medium text-gray-700 mb-3">
//                 Website Logo
//               </label>
//               <p className="text-xs text-gray-600 mb-3">
//                 Upload a logo to display in the header of your published public website. Once uploaded, the logo will replace your website name. If no logo is uploaded, your website name will be displayed instead. Recommended: square image (PNG, JPG, GIF, WebP, or SVG). Max 5MB.
//               </p>

//               {/* Logo Preview */}
//               {logoPreview && (
//                 <div className="mb-4">
//                   <div className="relative inline-block">
//                     <img
//                       src={logoPreview}
//                       alt="Logo preview"
//                       className="max-w-xs max-h-24 rounded-lg border border-gray-300"
//                       onLoad={() => console.log('[Logo Preview] Image loaded successfully from:', logoPreview)}
//                       onError={(e) => {
//                         console.error('[Logo Preview] Image failed to load')
//                         console.error('[Logo Preview] Attempted URL:', logoPreview)
//                         console.error('[Logo Preview] Event:', e)
//                         toast.error('Logo image failed to load. The file may not be accessible at: ' + logoPreview)
//                       }}
//                     />
//                   </div>
//                 </div>
//               )}

//               {/* File Input */}
//               <div className="flex gap-2">
//                 <label className="flex-1">
//                   <input
//                     type="file"
//                     accept="image/*"
//                     onChange={handleLogoChange}
//                     disabled={uploadingLogo}
//                     className="hidden"
//                   />
//                   <span className="inline-flex items-center gap-2 px-4 py-2 bg-blue-600 text-white hover:bg-blue-700 disabled:bg-gray-400 rounded-lg font-medium transition-colors cursor-pointer w-full justify-center">
//                     <FiUpload className="w-4 h-4" />
//                     Choose File
//                   </span>
//                 </label>

//                 {logoFile && !uploadingLogo && (
//                   <button
//                     type="button"
//                     onClick={handleUploadLogo}
//                     disabled={uploadingLogo}
//                     className="px-4 py-2 bg-green-600 text-white hover:bg-green-700 disabled:bg-gray-400 rounded-lg font-medium transition-colors flex items-center gap-2"
//                   >
//                     Upload
//                   </button>
//                 )}

//                 {logoPreview && (
//                   <button
//                     type="button"
//                     onClick={handleDeleteLogo}
//                     disabled={uploadingLogo}
//                     className="px-4 py-2 bg-red-600 text-white hover:bg-red-700 disabled:bg-gray-400 rounded-lg font-medium transition-colors flex items-center gap-2"
//                   >
//                     <FiTrash2 className="w-4 h-4" />
//                     Remove
//                   </button>
//                 )}

//                 {uploadingLogo && (
//                   <button
//                     type="button"
//                     disabled
//                     className="px-4 py-2 bg-gray-400 text-white rounded-lg font-medium flex items-center gap-2"
//                   >
//                     <FiLoader className="w-4 h-4 animate-spin" />
//                     Uploading...
//                   </button>
//                 )}
//               </div>

//               {logoFile && (
//                 <p className="text-xs text-gray-600 mt-2">
//                   Selected: {logoFile.name}
//                 </p>
//               )}
//             </div>
//           </div>

//           {/* SEO Settings */}
//           <div className="space-y-4 pt-6 border-t border-gray-200">
//             <h3 className="text-lg font-semibold text-gray-900">SEO Settings</h3>
            
//             <div>
//               <label className="block text-sm font-medium text-gray-700 mb-2">
//                 Meta Title
//               </label>
//               <input
//                 type="text"
//                 name="metaTitle"
//                 value={formData.metaTitle}
//                 onChange={handleInputChange}
//                 placeholder="Enter SEO title (60 characters)"
//                 maxLength="60"
//                 className="input-field w-full"
//               />
//               <p className="text-xs text-gray-500 mt-1">{formData.metaTitle.length}/60</p>
//             </div>

//             <div>
//               <label className="block text-sm font-medium text-gray-700 mb-2">
//                 Meta Description
//               </label>
//               <textarea
//                 name="metaDescription"
//                 value={formData.metaDescription}
//                 onChange={handleInputChange}
//                 placeholder="Enter SEO description (160 characters)"
//                 maxLength="160"
//                 rows="2"
//                 className="input-field w-full resize-none"
//               />
//               <p className="text-xs text-gray-500 mt-1">{formData.metaDescription.length}/160</p>
//             </div>

//             <div>
//               <label className="block text-sm font-medium text-gray-700 mb-2">
//                 Keywords
//               </label>
//               <input
//                 type="text"
//                 name="metaKeywords"
//                 value={formData.metaKeywords}
//                 onChange={handleInputChange}
//                 placeholder="Comma-separated keywords"
//                 className="input-field w-full"
//               />
//             </div>
//           </div>

//           {/* Custom Domain */}
//           <div className="space-y-4 pt-6 border-t border-gray-200">
//             <h3 className="text-lg font-semibold text-gray-900">Custom Domain</h3>
//             <p className="text-sm text-gray-600">Connect your own domain (e.g., example.com or www.example.com). Only one primary domain allowed.</p>

//             <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
//               <input
//                 type="text"
//                 placeholder="Enter custom domain (example.com)"
//                 value={website?.customDomain?.domain || ''}
//                 onChange={(e) => {
//                   const d = e.target.value
//                   // optimistic local update
//                   website.customDomain = website.customDomain || {}
//                   website.customDomain.domain = d
//                   setLogoFile(null) // no-op to force update? keep minimal
//                 }}
//                 className="input-field w-full col-span-2"
//               />

//               <div className="flex gap-2">
//                 <button
//                   type="button"
//                   onClick={async () => {
//                     if (!website || !website._id) return toast.error('Select a website first')
//                     const domain = (website.customDomain?.domain || '').trim()
//                     if (!domain) return toast.error('Enter a domain')
//                     try {
//                       setDomainAdding(true)
//                       const res = await axios.post(`/websites/${website._id}/custom-domain`, { domain })
//                       toast.success('Custom domain added. Follow DNS instructions to verify.')
//                     } catch (err) {
//                       console.error('Error adding domain:', err)
//                       toast.error(err.response?.data?.message || 'Failed to add domain')
//                     } finally {
//                       setDomainAdding(false)
//                     }
//                   }}
//                   disabled={domainAdding || domainVerifying || domainRemoving}
//                   className={`px-3 py-2 rounded-lg text-white ${domainAdding ? 'bg-gray-400' : 'bg-blue-600 hover:bg-blue-700'}`}
//                 >
//                   {domainAdding ? (
//                     <span className="inline-flex items-center gap-2"><FiLoader className="w-4 h-4 animate-spin" />Adding...</span>
//                   ) : 'Add Domain'}
//                 </button>

//                 <button
//                   type="button"
//                   onClick={async () => {
//                     if (!website || !website._id) return toast.error('Select a website first')
//                     try {
//                       setDomainVerifying(true)
//                       const res = await axios.post(`/websites/${website._id}/custom-domain/verify`)
//                       toast.success('Domain verified and activated')
//                     } catch (err) {
//                       console.error('Error verifying domain:', err)
//                       toast.error(err.response?.data?.message || 'Domain verification failed')
//                     } finally {
//                       setDomainVerifying(false)
//                     }
//                   }}
//                   disabled={domainAdding || domainVerifying || domainRemoving}
//                   className={`px-3 py-2 rounded-lg text-white ${domainVerifying ? 'bg-gray-400' : 'bg-green-600 hover:bg-green-700'}`}
//                 >
//                   {domainVerifying ? (
//                     <span className="inline-flex items-center gap-2"><FiLoader className="w-4 h-4 animate-spin" />Verifying...</span>
//                   ) : 'Verify'}
//                 </button>

//                 <button
//                   type="button"
//                   onClick={async () => {
//                     if (!website || !website._id) return toast.error('Select a website first')
//                     if (!window.confirm('Remove custom domain?')) return
//                     try {
//                       setDomainRemoving(true)
//                       await axios.delete(`/websites/${website._id}/custom-domain`)
//                       toast.success('Custom domain removed')
//                     } catch (err) {
//                       console.error('Error removing domain:', err)
//                       toast.error(err.response?.data?.message || 'Failed to remove domain')
//                     } finally {
//                       setDomainRemoving(false)
//                     }
//                   }}
//                   disabled={domainAdding || domainVerifying || domainRemoving}
//                   className={`px-3 py-2 rounded-lg text-white ${domainRemoving ? 'bg-gray-400' : 'bg-red-600 hover:bg-red-700'}`}
//                 >
//                   {domainRemoving ? (
//                     <span className="inline-flex items-center gap-2"><FiLoader className="w-4 h-4 animate-spin" />Removing...</span>
//                   ) : 'Remove'}
//                 </button>
//               </div>

//               <div className="col-span-3 text-xs text-gray-600">
//                 <p>DNS Instructions:</p>
//                 <ul className="list-disc ml-5">
//                   <li>Point your root domain or APEX to your DNS provider configuration — Render and Vercel do not provide static platform IPs.</li>
//                   <li>OR add a CNAME for a subdomain (e.g., <code>www</code>) pointing to the platform host: <strong>{process.env.REACT_APP_PLATFORM_PUBLIC_URL ? (() => { try { return new URL(process.env.REACT_APP_PLATFORM_PUBLIC_URL).hostname } catch { return process.env.REACT_APP_PLATFORM_PUBLIC_URL } })() : window.location.hostname}</strong></li>
//                   <li>Optional TXT verification: add a TXT record with the verification token shown in the website settings after adding the domain.</li>
//                 </ul>
//               </div>
//             </div>
//           </div>

//           {/* Design Settings */}
//           <div className="space-y-4 pt-6 border-t border-gray-200">
//             <h3 className="text-lg font-semibold text-gray-900">Design Settings</h3>

//             {/* Colors */}
//             <div className="space-y-4">
//               <p className="text-sm text-gray-600">Colors</p>
//               <div className="grid grid-cols-3 gap-4">
//                 <div>
//                   <label className="block text-xs font-medium text-gray-700 mb-2">
//                     Primary Color
//                   </label>
//                   <div className="flex items-center gap-2">
//                     <input
//                       type="color"
//                       value={formData.primaryColor}
//                       onChange={(e) => handleColorChange('primaryColor', e.target.value)}
//                       className="w-12 h-10 rounded cursor-pointer border border-gray-300"
//                     />
//                     <input
//                       type="text"
//                       value={formData.primaryColor}
//                       onChange={(e) => handleColorChange('primaryColor', e.target.value)}
//                       className="input-field flex-1 font-mono text-sm"
//                       maxLength="7"
//                     />
//                   </div>
//                 </div>

//                 <div>
//                   <label className="block text-xs font-medium text-gray-700 mb-2">
//                     Secondary Color
//                   </label>
//                   <div className="flex items-center gap-2">
//                     <input
//                       type="color"
//                       value={formData.secondaryColor}
//                       onChange={(e) => handleColorChange('secondaryColor', e.target.value)}
//                       className="w-12 h-10 rounded cursor-pointer border border-gray-300"
//                     />
//                     <input
//                       type="text"
//                       value={formData.secondaryColor}
//                       onChange={(e) => handleColorChange('secondaryColor', e.target.value)}
//                       className="input-field flex-1 font-mono text-sm"
//                       maxLength="7"
//                     />
//                   </div>
//                 </div>

//                 <div>
//                   <label className="block text-xs font-medium text-gray-700 mb-2">
//                     Accent Color
//                   </label>
//                   <div className="flex items-center gap-2">
//                     <input
//                       type="color"
//                       value={formData.accentColor}
//                       onChange={(e) => handleColorChange('accentColor', e.target.value)}
//                       className="w-12 h-10 rounded cursor-pointer border border-gray-300"
//                     />
//                     <input
//                       type="text"
//                       value={formData.accentColor}
//                       onChange={(e) => handleColorChange('accentColor', e.target.value)}
//                       className="input-field flex-1 font-mono text-sm"
//                       maxLength="7"
//                     />
//                   </div>
//                 </div>
//               </div>
//             </div>

//             {/* Fonts */}
//             <div className="space-y-3">
//               <p className="text-sm text-gray-600">Fonts</p>
//               <div className="grid grid-cols-2 gap-4">
//                 <div>
//                   <label className="block text-xs font-medium text-gray-700 mb-2">
//                     Primary Font
//                   </label>
//                   <select
//                     name="primaryFont"
//                     value={formData.primaryFont}
//                     onChange={handleInputChange}
//                     className="input-field w-full"
//                   >
//                     <option value="Inter">Inter</option>
//                     <option value="Arial">Arial</option>
//                     <option value="Georgia">Georgia</option>
//                     <option value="Times New Roman">Times New Roman</option>
//                     <option value="Verdana">Verdana</option>
//                   </select>
//                 </div>
//                 <div>
//                   <label className="block text-xs font-medium text-gray-700 mb-2">
//                     Secondary Font
//                   </label>
//                   <select
//                     name="secondaryFont"
//                     value={formData.secondaryFont}
//                     onChange={handleInputChange}
//                     className="input-field w-full"
//                   >
//                     <option value="Inter">Inter</option>
//                     <option value="Arial">Arial</option>
//                     <option value="Georgia">Georgia</option>
//                     <option value="Times New Roman">Times New Roman</option>
//                     <option value="Verdana">Verdana</option>
//                   </select>
//                 </div>
//               </div>
//             </div>
//           </div>

//           {/* Action Buttons */}
//           <div className="flex gap-3 pt-6 border-t border-gray-200">
//             <button
//               type="button"
//               onClick={onClose}
//               disabled={isSaving}
//               className="flex-1 py-2 px-4 bg-gray-100 text-gray-700 hover:bg-gray-200 rounded-lg font-medium transition-colors disabled:opacity-50"
//             >
//               Cancel
//             </button>
//             <button
//               type="submit"
//               disabled={isSaving}
//               className="flex-1 py-2 px-4 bg-primary-600 text-white hover:bg-primary-700 disabled:bg-gray-400 rounded-lg font-medium transition-colors flex items-center justify-center gap-2 disabled:cursor-not-allowed"
//             >
//               {isSaving ? (
//                 <>
//                   <FiLoader className="w-4 h-4 animate-spin" /> Saving...
//                 </>
//               ) : (
//                 <>
//                   <FiSave className="w-4 h-4" /> Save Website
//                 </>
//               )}
//             </button>
//           </div>
//         </form>
//       </div>
//     </div>
//   )
// }



























// import { useState, useEffect } from 'react'
// import { FiX, FiLoader, FiSave, FiTrash2, FiUpload } from 'react-icons/fi'
// import toast from 'react-hot-toast'
// import axios from 'axios'

// export default function WebsiteEditorModal({ isOpen, onClose, onSubmit, website, categories, isSaving, mode, onLogoUpload, onLogoDelete }) {
//   const [formData, setFormData] = useState({
//     name: '',
//     description: '',
//     metaTitle: '',
//     metaDescription: '',
//     metaKeywords: '',
//     primaryColor: '#3B82F6',
//     secondaryColor: '#1F2937',
//     accentColor: '#F97316',
//     primaryFont: 'Inter',
//     secondaryFont: 'Inter'
//   })
//   const [logoFile, setLogoFile] = useState(null)
//   const [logoPreview, setLogoPreview] = useState(null)
//   const [uploadingLogo, setUploadingLogo] = useState(false)
//   const [domainAdding, setDomainAdding] = useState(false)
//   const [domainVerifying, setDomainVerifying] = useState(false)
//   const [domainRemoving, setDomainRemoving] = useState(false)

//   useEffect(() => {
//     if (website && isOpen) {
//       setFormData({
//         name: website.name || '',
//         description: website.description || '',
//         metaTitle: website.seo?.metaTitle || '',
//         metaDescription: website.seo?.metaDescription || '',
//         metaKeywords: website.seo?.metaKeywords || '',
//         primaryColor: website.colors?.primary || '#3B82F6',
//         secondaryColor: website.colors?.secondary || '#1F2937',
//         accentColor: website.colors?.accent || '#F97316',
//         primaryFont: website.fonts?.primary || 'Inter',
//         secondaryFont: website.fonts?.secondary || 'Inter'
//       })
      
//       // Display the saved/uploaded logo from backend if it exists
//       if (website.logo?.url) {
//         setLogoPreview(website.logo.url)
//         console.log('[WebsiteEditor] Showing saved logo from backend:', website.logo.url)
//       } else {
//         setLogoPreview(null)
//         console.log('[WebsiteEditor] No saved logo found')
//       }
      
//       setLogoFile(null)
//     }
//   }, [website, isOpen])

//   const handleInputChange = (e) => {
//     const { name, value } = e.target
//     setFormData(prev => ({
//       ...prev,
//       [name]: value
//     }))
//   }

//   const handleColorChange = (colorField, value) => {
//     setFormData(prev => ({
//       ...prev,
//       [colorField]: value
//     }))
//   }

//   const handleLogoChange = (e) => {
//     const file = e.target.files?.[0]
//     if (!file) return

//     if (!file.type.startsWith('image/')) {
//       toast.error('Please select an image file')
//       return
//     }

//     if (file.size > 5 * 1024 * 1024) {
//       toast.error('File size must be less than 5MB')
//       return
//     }

//     // Store the file for upload - don't show preview until upload completes
//     setLogoFile(file)
//     console.log('[WebsiteEditor] File selected for upload:', file.name)
//   }

//   const handleUploadLogo = async () => {
//     if (!logoFile || !website?._id) {
//       toast.error('No logo file selected')
//       return
//     }

//     try {
//       setUploadingLogo(true)
//       const formDataUpload = new FormData()
//       formDataUpload.append('logo', logoFile)

//       const response = await axios.post(`/websites/${website._id}/logo`, formDataUpload, {
//         headers: {
//           'Content-Type': 'multipart/form-data'
//         }
//       })

//       const returnedWebsite = response.data?.website
//       const returnedLogoUrl = returnedWebsite?.logo?.url
      
//       if (returnedLogoUrl) {
//         // Display the exact uploaded logo URL as returned by backend
//         setLogoPreview(returnedLogoUrl)
//         console.log('[WebsiteEditor] Logo uploaded successfully:', returnedLogoUrl)
//         toast.success('✅ Logo uploaded successfully! It will appear on your public website, replacing the website name.')
        
//         // Call parent callback to update website data with the uploaded logo
//         if (onLogoUpload && returnedWebsite) {
//           onLogoUpload(returnedWebsite)
//           console.log('[WebsiteEditor] Called onLogoUpload callback')
//         }
//       } else {
//         toast.error('Upload succeeded but logo URL is missing')
//       }
      
//       // Clear file input after successful upload
//       setLogoFile(null)
//     } catch (error) {
//       console.error('[Logo Upload] Error:', error)
//       toast.error(error.response?.data?.message || 'Failed to upload logo')
//     } finally {
//       setUploadingLogo(false)
//     }
//   }

//   const handleDeleteLogo = async () => {
//     if (!website?._id) return

//     if (!window.confirm('Are you sure you want to delete the logo?')) return

//     try {
//       setUploadingLogo(true)
//       const response = await axios.delete(`/websites/${website._id}/logo`)
//       toast.success('Logo deleted successfully')
//       setLogoPreview(null)
//       setLogoFile(null)
      
//       // Call parent callback to update website data after logo deletion
//       if (onLogoDelete && response.data?.website) {
//         onLogoDelete(response.data.website)
//         console.log('[WebsiteEditor] Called onLogoDelete callback with updated website')
//       }
//     } catch (error) {
//       console.error('[Logo Delete] Error:', error)
//       toast.error(error.response?.data?.message || 'Failed to delete logo')
//     } finally {
//       setUploadingLogo(false)
//     }
//   }

//   const handleSubmit = (e) => {
//     e.preventDefault()
    
//     if (!formData.name.trim()) {
//       toast.error('Website name is required')
//       return
//     }

//     if (!website || !website._id) {
//       toast.error('Invalid website - please select a website to edit')
//       return
//     }

//     const submitData = {
//       name: formData.name.trim(),
//       description: formData.description,
//       seo: {
//         metaTitle: formData.metaTitle,
//         metaDescription: formData.metaDescription,
//         metaKeywords: formData.metaKeywords
//       },
//       colors: {
//         primary: formData.primaryColor,
//         secondary: formData.secondaryColor,
//         accent: formData.accentColor
//       },
//       fonts: {
//         primary: formData.primaryFont,
//         secondary: formData.secondaryFont
//       }
//     }

//     onSubmit(submitData)
//   }

//   if (!isOpen) return null

//   return (
//     <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
//       <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
//         {/* Header */}
//         <div className="flex justify-between items-center p-6 border-b border-gray-200 sticky top-0 bg-white">
//           <h2 className="text-2xl font-bold text-gray-900">Edit Website</h2>
//           <button
//             onClick={onClose}
//             disabled={isSaving}
//             className="p-1 hover:bg-gray-100 rounded-lg transition-colors disabled:opacity-50"
//           >
//             <FiX className="w-6 h-6" />
//           </button>
//         </div>

//         {/* Content */}
//         <form onSubmit={handleSubmit} className="p-6 space-y-6">
//           {/* Basic Information */}
//           <div className="space-y-4">
//             <h3 className="text-lg font-semibold text-gray-900">Basic Information</h3>
            
//             <div>
//               <label className="block text-sm font-medium text-gray-700 mb-2">
//                 Website Name *
//               </label>
//               <input
//                 type="text"
//                 name="name"
//                 value={formData.name}
//                 onChange={handleInputChange}
//                 placeholder="Enter website name"
//                 className="input-field w-full"
//                 required
//               />
//             </div>

//             <div>
//               <label className="block text-sm font-medium text-gray-700 mb-2">
//                 Description
//               </label>
//               <textarea
//                 name="description"
//                 value={formData.description}
//                 onChange={handleInputChange}
//                 placeholder="Brief description of your website"
//                 rows="3"
//                 className="input-field w-full resize-none"
//               />
//             </div>

//             {/* Website Logo */}
//             <div className="mt-6 p-4 bg-gray-50 rounded-lg border border-gray-200">
//               <label className="block text-sm font-medium text-gray-700 mb-3">
//                 Website Logo
//               </label>
//               <p className="text-xs text-gray-600 mb-3">
//                 Upload a logo to display in the header of your published public website. Once uploaded, the logo will replace your website name. If no logo is uploaded, your website name will be displayed instead. Recommended: square image (PNG, JPG, GIF, WebP, or SVG). Max 5MB.
//               </p>

//               {/* Logo Preview */}
//               {logoPreview && (
//                 <div className="mb-4">
//                   <div className="relative inline-block">
//                     <img
//                       src={logoPreview}
//                       alt="Logo preview"
//                       className="max-w-xs max-h-24 rounded-lg border border-gray-300"
//                     />
//                   </div>
//                 </div>
//               )}

//               {/* File Input */}
//               <div className="flex gap-2">
//                 <label className="flex-1">
//                   <input
//                     type="file"
//                     accept="image/*"
//                     onChange={handleLogoChange}
//                     disabled={uploadingLogo}
//                     className="hidden"
//                   />
//                   <span className="inline-flex items-center gap-2 px-4 py-2 bg-blue-600 text-white hover:bg-blue-700 disabled:bg-gray-400 rounded-lg font-medium transition-colors cursor-pointer w-full justify-center">
//                     <FiUpload className="w-4 h-4" />
//                     Choose File
//                   </span>
//                 </label>

//                 {logoFile && !uploadingLogo && (
//                   <button
//                     type="button"
//                     onClick={handleUploadLogo}
//                     disabled={uploadingLogo}
//                     className="px-4 py-2 bg-green-600 text-white hover:bg-green-700 disabled:bg-gray-400 rounded-lg font-medium transition-colors flex items-center gap-2"
//                   >
//                     Upload
//                   </button>
//                 )}

//                 {logoPreview && (
//                   <button
//                     type="button"
//                     onClick={handleDeleteLogo}
//                     disabled={uploadingLogo}
//                     className="px-4 py-2 bg-red-600 text-white hover:bg-red-700 disabled:bg-gray-400 rounded-lg font-medium transition-colors flex items-center gap-2"
//                   >
//                     <FiTrash2 className="w-4 h-4" />
//                     Remove
//                   </button>
//                 )}

//                 {uploadingLogo && (
//                   <button
//                     type="button"
//                     disabled
//                     className="px-4 py-2 bg-gray-400 text-white rounded-lg font-medium flex items-center gap-2"
//                   >
//                     <FiLoader className="w-4 h-4 animate-spin" />
//                     Uploading...
//                   </button>
//                 )}
//               </div>

//               {logoFile && (
//                 <p className="text-xs text-gray-600 mt-2">
//                   Selected: {logoFile.name}
//                 </p>
//               )}
//             </div>
//           </div>

//           {/* SEO Settings */}
//           <div className="space-y-4 pt-6 border-t border-gray-200">
//             <h3 className="text-lg font-semibold text-gray-900">SEO Settings</h3>
            
//             <div>
//               <label className="block text-sm font-medium text-gray-700 mb-2">
//                 Meta Title
//               </label>
//               <input
//                 type="text"
//                 name="metaTitle"
//                 value={formData.metaTitle}
//                 onChange={handleInputChange}
//                 placeholder="Enter SEO title (60 characters)"
//                 maxLength="60"
//                 className="input-field w-full"
//               />
//               <p className="text-xs text-gray-500 mt-1">{formData.metaTitle.length}/60</p>
//             </div>

//             <div>
//               <label className="block text-sm font-medium text-gray-700 mb-2">
//                 Meta Description
//               </label>
//               <textarea
//                 name="metaDescription"
//                 value={formData.metaDescription}
//                 onChange={handleInputChange}
//                 placeholder="Enter SEO description (160 characters)"
//                 maxLength="160"
//                 rows="2"
//                 className="input-field w-full resize-none"
//               />
//               <p className="text-xs text-gray-500 mt-1">{formData.metaDescription.length}/160</p>
//             </div>

//             <div>
//               <label className="block text-sm font-medium text-gray-700 mb-2">
//                 Keywords
//               </label>
//               <input
//                 type="text"
//                 name="metaKeywords"
//                 value={formData.metaKeywords}
//                 onChange={handleInputChange}
//                 placeholder="Comma-separated keywords"
//                 className="input-field w-full"
//               />
//             </div>
//           </div>

//           {/* Custom Domain */}
//           <div className="space-y-4 pt-6 border-t border-gray-200">
//             <h3 className="text-lg font-semibold text-gray-900">Custom Domain</h3>
//             <p className="text-sm text-gray-600">Connect your own domain (e.g., example.com or www.example.com). Only one primary domain allowed.</p>

//             <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
//               <input
//                 type="text"
//                 placeholder="Enter custom domain (example.com)"
//                 value={website?.customDomain?.domain || ''}
//                 onChange={(e) => {
//                   const d = e.target.value
//                   // optimistic local update
//                   website.customDomain = website.customDomain || {}
//                   website.customDomain.domain = d
//                   setLogoFile(null) // no-op to force update? keep minimal
//                 }}
//                 className="input-field w-full col-span-2"
//               />

//               <div className="flex gap-2">
//                 <button
//                   type="button"
//                   onClick={async () => {
//                     if (!website || !website._id) return toast.error('Select a website first')
//                     const domain = (website.customDomain?.domain || '').trim()
//                     if (!domain) return toast.error('Enter a domain')
//                     try {
//                       setDomainAdding(true)
//                       const res = await axios.post(`/websites/${website._id}/custom-domain`, { domain })
//                       toast.success('Custom domain added. Follow DNS instructions to verify.')
//                     } catch (err) {
//                       console.error('Error adding domain:', err)
//                       toast.error(err.response?.data?.message || 'Failed to add domain')
//                     } finally {
//                       setDomainAdding(false)
//                     }
//                   }}
//                   disabled={domainAdding || domainVerifying || domainRemoving}
//                   className={`px-3 py-2 rounded-lg text-white ${domainAdding ? 'bg-gray-400' : 'bg-blue-600 hover:bg-blue-700'}`}
//                 >
//                   {domainAdding ? (
//                     <span className="inline-flex items-center gap-2"><FiLoader className="w-4 h-4 animate-spin" />Adding...</span>
//                   ) : 'Add Domain'}
//                 </button>

//                 <button
//                   type="button"
//                   onClick={async () => {
//                     if (!website || !website._id) return toast.error('Select a website first')
//                     try {
//                       setDomainVerifying(true)
//                       const res = await axios.post(`/websites/${website._id}/custom-domain/verify`)
//                       toast.success('Domain verified and activated')
//                     } catch (err) {
//                       console.error('Error verifying domain:', err)
//                       toast.error(err.response?.data?.message || 'Domain verification failed')
//                     } finally {
//                       setDomainVerifying(false)
//                     }
//                   }}
//                   disabled={domainAdding || domainVerifying || domainRemoving}
//                   className={`px-3 py-2 rounded-lg text-white ${domainVerifying ? 'bg-gray-400' : 'bg-green-600 hover:bg-green-700'}`}
//                 >
//                   {domainVerifying ? (
//                     <span className="inline-flex items-center gap-2"><FiLoader className="w-4 h-4 animate-spin" />Verifying...</span>
//                   ) : 'Verify'}
//                 </button>

//                 <button
//                   type="button"
//                   onClick={async () => {
//                     if (!website || !website._id) return toast.error('Select a website first')
//                     if (!window.confirm('Remove custom domain?')) return
//                     try {
//                       setDomainRemoving(true)
//                       await axios.delete(`/websites/${website._id}/custom-domain`)
//                       toast.success('Custom domain removed')
//                     } catch (err) {
//                       console.error('Error removing domain:', err)
//                       toast.error(err.response?.data?.message || 'Failed to remove domain')
//                     } finally {
//                       setDomainRemoving(false)
//                     }
//                   }}
//                   disabled={domainAdding || domainVerifying || domainRemoving}
//                   className={`px-3 py-2 rounded-lg text-white ${domainRemoving ? 'bg-gray-400' : 'bg-red-600 hover:bg-red-700'}`}
//                 >
//                   {domainRemoving ? (
//                     <span className="inline-flex items-center gap-2"><FiLoader className="w-4 h-4 animate-spin" />Removing...</span>
//                   ) : 'Remove'}
//                 </button>
//               </div>

//               <div className="col-span-3 text-xs text-gray-600">
//                 <p>DNS Instructions:</p>
//                 <ul className="list-disc ml-5">
//                   <li>Point your root domain or APEX to your DNS provider configuration — Render and Vercel do not provide static platform IPs.</li>
//                   <li>OR add a CNAME for a subdomain (e.g., <code>www</code>) pointing to the platform host: <strong>{process.env.REACT_APP_PLATFORM_PUBLIC_URL ? (() => { try { return new URL(process.env.REACT_APP_PLATFORM_PUBLIC_URL).hostname } catch { return process.env.REACT_APP_PLATFORM_PUBLIC_URL } })() : window.location.hostname}</strong></li>
//                   <li>Optional TXT verification: add a TXT record with the verification token shown in the website settings after adding the domain.</li>
//                 </ul>
//               </div>
//             </div>
//           </div>

//           {/* Design Settings */}
//           <div className="space-y-4 pt-6 border-t border-gray-200">
//             <h3 className="text-lg font-semibold text-gray-900">Design Settings</h3>

//             {/* Colors */}
//             <div className="space-y-4">
//               <p className="text-sm text-gray-600">Colors</p>
//               <div className="grid grid-cols-3 gap-4">
//                 <div>
//                   <label className="block text-xs font-medium text-gray-700 mb-2">
//                     Primary Color
//                   </label>
//                   <div className="flex items-center gap-2">
//                     <input
//                       type="color"
//                       value={formData.primaryColor}
//                       onChange={(e) => handleColorChange('primaryColor', e.target.value)}
//                       className="w-12 h-10 rounded cursor-pointer border border-gray-300"
//                     />
//                     <input
//                       type="text"
//                       value={formData.primaryColor}
//                       onChange={(e) => handleColorChange('primaryColor', e.target.value)}
//                       className="input-field flex-1 font-mono text-sm"
//                       maxLength="7"
//                     />
//                   </div>
//                 </div>

//                 <div>
//                   <label className="block text-xs font-medium text-gray-700 mb-2">
//                     Secondary Color
//                   </label>
//                   <div className="flex items-center gap-2">
//                     <input
//                       type="color"
//                       value={formData.secondaryColor}
//                       onChange={(e) => handleColorChange('secondaryColor', e.target.value)}
//                       className="w-12 h-10 rounded cursor-pointer border border-gray-300"
//                     />
//                     <input
//                       type="text"
//                       value={formData.secondaryColor}
//                       onChange={(e) => handleColorChange('secondaryColor', e.target.value)}
//                       className="input-field flex-1 font-mono text-sm"
//                       maxLength="7"
//                     />
//                   </div>
//                 </div>

//                 <div>
//                   <label className="block text-xs font-medium text-gray-700 mb-2">
//                     Accent Color
//                   </label>
//                   <div className="flex items-center gap-2">
//                     <input
//                       type="color"
//                       value={formData.accentColor}
//                       onChange={(e) => handleColorChange('accentColor', e.target.value)}
//                       className="w-12 h-10 rounded cursor-pointer border border-gray-300"
//                     />
//                     <input
//                       type="text"
//                       value={formData.accentColor}
//                       onChange={(e) => handleColorChange('accentColor', e.target.value)}
//                       className="input-field flex-1 font-mono text-sm"
//                       maxLength="7"
//                     />
//                   </div>
//                 </div>
//               </div>
//             </div>

//             {/* Fonts */}
//             <div className="space-y-3">
//               <p className="text-sm text-gray-600">Fonts</p>
//               <div className="grid grid-cols-2 gap-4">
//                 <div>
//                   <label className="block text-xs font-medium text-gray-700 mb-2">
//                     Primary Font
//                   </label>
//                   <select
//                     name="primaryFont"
//                     value={formData.primaryFont}
//                     onChange={handleInputChange}
//                     className="input-field w-full"
//                   >
//                     <option value="Inter">Inter</option>
//                     <option value="Arial">Arial</option>
//                     <option value="Georgia">Georgia</option>
//                     <option value="Times New Roman">Times New Roman</option>
//                     <option value="Verdana">Verdana</option>
//                   </select>
//                 </div>
//                 <div>
//                   <label className="block text-xs font-medium text-gray-700 mb-2">
//                     Secondary Font
//                   </label>
//                   <select
//                     name="secondaryFont"
//                     value={formData.secondaryFont}
//                     onChange={handleInputChange}
//                     className="input-field w-full"
//                   >
//                     <option value="Inter">Inter</option>
//                     <option value="Arial">Arial</option>
//                     <option value="Georgia">Georgia</option>
//                     <option value="Times New Roman">Times New Roman</option>
//                     <option value="Verdana">Verdana</option>
//                   </select>
//                 </div>
//               </div>
//             </div>
//           </div>

//           {/* Action Buttons */}
//           <div className="flex gap-3 pt-6 border-t border-gray-200">
//             <button
//               type="button"
//               onClick={onClose}
//               disabled={isSaving}
//               className="flex-1 py-2 px-4 bg-gray-100 text-gray-700 hover:bg-gray-200 rounded-lg font-medium transition-colors disabled:opacity-50"
//             >
//               Cancel
//             </button>
//             <button
//               type="submit"
//               disabled={isSaving}
//               className="flex-1 py-2 px-4 bg-primary-600 text-white hover:bg-primary-700 disabled:bg-gray-400 rounded-lg font-medium transition-colors flex items-center justify-center gap-2 disabled:cursor-not-allowed"
//             >
//               {isSaving ? (
//                 <>
//                   <FiLoader className="w-4 h-4 animate-spin" /> Saving...
//                 </>
//               ) : (
//                 <>
//                   <FiSave className="w-4 h-4" /> Save Website
//                 </>
//               )}
//             </button>
//           </div>
//         </form>
//       </div>
//     </div>
//   )
// }













// import { useState, useEffect } from 'react'
// import { FiX, FiLoader, FiSave, FiTrash2, FiUpload } from 'react-icons/fi'
// import toast from 'react-hot-toast'
// import axios from 'axios'

// export default function WebsiteEditorModal({ isOpen, onClose, onSubmit, website, categories, isSaving, mode, onLogoUpload, onLogoDelete }) {
//   const [formData, setFormData] = useState({
//     name: '',
//     description: '',
//     metaTitle: '',
//     metaDescription: '',
//     metaKeywords: '',
//     primaryColor: '#3B82F6',
//     secondaryColor: '#1F2937',
//     accentColor: '#F97316',
//     primaryFont: 'Inter',
//     secondaryFont: 'Inter'
//   })
//   const [logoFile, setLogoFile] = useState(null)
//   const [logoPreview, setLogoPreview] = useState(null)
//   const [uploadingLogo, setUploadingLogo] = useState(false)
//   const [domainAdding, setDomainAdding] = useState(false)
//   const [domainVerifying, setDomainVerifying] = useState(false)
//   const [domainRemoving, setDomainRemoving] = useState(false)

//   useEffect(() => {
//     if (website && isOpen) {
//       setFormData({
//         name: website.name || '',
//         description: website.description || '',
//         metaTitle: website.seo?.metaTitle || '',
//         metaDescription: website.seo?.metaDescription || '',
//         metaKeywords: website.seo?.metaKeywords || '',
//         primaryColor: website.colors?.primary || '#3B82F6',
//         secondaryColor: website.colors?.secondary || '#1F2937',
//         accentColor: website.colors?.accent || '#F97316',
//         primaryFont: website.fonts?.primary || 'Inter',
//         secondaryFont: website.fonts?.secondary || 'Inter'
//       })
      
//       // Show the saved/uploaded logo from backend ONLY if it exists and is a stored file
//       if (website.logo?.url && !website.logo.url.startsWith('data:') && !website.logo.url.startsWith('blob:')) {
//         const backendOrigin = axios.defaults.baseURL ? axios.defaults.baseURL.replace(/\/api\/?$/, '') : ''
//         const storedUrl = website.logo.url
//         let previewUrl = storedUrl
        
//         // Convert relative path to absolute URL for display
//         if (!storedUrl.startsWith('http')) {
//           const pathUrl = storedUrl.startsWith('/') ? storedUrl : `/${storedUrl}`
//           previewUrl = backendOrigin ? `${backendOrigin}${pathUrl}` : pathUrl
//         }
        
//         setLogoPreview(previewUrl)
//         console.log('[WebsiteEditor] Showing saved logo from backend:', previewUrl)
//       } else {
//         // No saved logo - start fresh for new upload
//         setLogoPreview(null)
//         console.log('[WebsiteEditor] No saved logo found')
//       }
      
//       setLogoFile(null)
//     }
//   }, [website, isOpen])

//   const handleInputChange = (e) => {
//     const { name, value } = e.target
//     setFormData(prev => ({
//       ...prev,
//       [name]: value
//     }))
//   }

//   const handleColorChange = (colorField, value) => {
//     setFormData(prev => ({
//       ...prev,
//       [colorField]: value
//     }))
//   }

//   const handleLogoChange = (e) => {
//     const file = e.target.files?.[0]
//     if (!file) return

//     if (!file.type.startsWith('image/')) {
//       toast.error('Please select an image file')
//       return
//     }

//     if (file.size > 5 * 1024 * 1024) {
//       toast.error('File size must be less than 5MB')
//       return
//     }

//     setLogoFile(file)
//     const reader = new FileReader()
//     reader.onload = (evt) => {
//       setLogoPreview(evt.target?.result)
//     }
//     reader.readAsDataURL(file)
//   }

//   const handleUploadLogo = async () => {
//     if (!logoFile || !website?._id) {
//       toast.error('No logo file selected')
//       return
//     }

//     try {
//       setUploadingLogo(true)
//       const formDataUpload = new FormData()
//       formDataUpload.append('logo', logoFile)

//       const response = await axios.post(`/websites/${website._id}/logo`, formDataUpload, {
//         headers: {
//           'Content-Type': 'multipart/form-data'
//         }
//       })

//       // Immediately show the uploaded logo in preview from backend
//       const returnedWebsite = response.data?.website
//       const returnedLogoUrl = returnedWebsite?.logo?.url
      
//       if (returnedLogoUrl) {
//         // Build absolute URL for display (logo is stored as relative path on backend)
//         let displayUrl = returnedLogoUrl
        
//         // If relative path and we have baseURL, use it to construct absolute URL
//         if (!returnedLogoUrl.startsWith('http') && !returnedLogoUrl.startsWith('data:') && !returnedLogoUrl.startsWith('blob:')) {
//           const backendOrigin = axios.defaults.baseURL ? axios.defaults.baseURL.replace(/\/api\/?$/, '') : ''
//           const pathUrl = returnedLogoUrl.startsWith('/') ? returnedLogoUrl : `/${returnedLogoUrl}`
          
//           if (backendOrigin) {
//             displayUrl = `${backendOrigin}${pathUrl}`
//           } else {
//             // Fallback to relative path - browser will resolve it
//             displayUrl = pathUrl
//           }
//         }
        
//         setLogoPreview(displayUrl)
//         console.log('[WebsiteEditor] Logo uploaded successfully - preview:', displayUrl)
//         toast.success('✅ Logo uploaded successfully! It will appear on your public website, replacing the website name.')
//       } else {
//         toast.error('Upload succeeded but logo URL is missing')
//       }
      
//       // Call parent callback to update website data with the uploaded logo
//       if (onLogoUpload && returnedWebsite) {
//         onLogoUpload(returnedWebsite)
//         console.log('[WebsiteEditor] Called onLogoUpload callback')
//       }
      
//       // Clear file input after successful upload
//       setLogoFile(null)
//     } catch (error) {
//       console.error('[Logo Upload] Error:', error)
//       toast.error(error.response?.data?.message || 'Failed to upload logo')
//     } finally {
//       setUploadingLogo(false)
//     }
//   }

//   const handleDeleteLogo = async () => {
//     if (!website?._id) return

//     if (!window.confirm('Are you sure you want to delete the logo?')) return

//     try {
//       setUploadingLogo(true)
//       const response = await axios.delete(`/websites/${website._id}/logo`)
//       toast.success('Logo deleted successfully')
//       setLogoPreview(null)
//       setLogoFile(null)
      
//       // Call parent callback to update website data after logo deletion
//       if (onLogoDelete && response.data?.website) {
//         onLogoDelete(response.data.website)
//         console.log('[WebsiteEditor] Called onLogoDelete callback with updated website')
//       }
//     } catch (error) {
//       console.error('[Logo Delete] Error:', error)
//       toast.error(error.response?.data?.message || 'Failed to delete logo')
//     } finally {
//       setUploadingLogo(false)
//     }
//   }

//   const handleSubmit = (e) => {
//     e.preventDefault()
    
//     if (!formData.name.trim()) {
//       toast.error('Website name is required')
//       return
//     }

//     if (!website || !website._id) {
//       toast.error('Invalid website - please select a website to edit')
//       return
//     }

//     const submitData = {
//       name: formData.name.trim(),
//       description: formData.description,
//       seo: {
//         metaTitle: formData.metaTitle,
//         metaDescription: formData.metaDescription,
//         metaKeywords: formData.metaKeywords
//       },
//       colors: {
//         primary: formData.primaryColor,
//         secondary: formData.secondaryColor,
//         accent: formData.accentColor
//       },
//       fonts: {
//         primary: formData.primaryFont,
//         secondary: formData.secondaryFont
//       }
//     }

//     onSubmit(submitData)
//   }

//   if (!isOpen) return null

//   return (
//     <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
//       <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
//         {/* Header */}
//         <div className="flex justify-between items-center p-6 border-b border-gray-200 sticky top-0 bg-white">
//           <h2 className="text-2xl font-bold text-gray-900">Edit Website</h2>
//           <button
//             onClick={onClose}
//             disabled={isSaving}
//             className="p-1 hover:bg-gray-100 rounded-lg transition-colors disabled:opacity-50"
//           >
//             <FiX className="w-6 h-6" />
//           </button>
//         </div>

//         {/* Content */}
//         <form onSubmit={handleSubmit} className="p-6 space-y-6">
//           {/* Basic Information */}
//           <div className="space-y-4">
//             <h3 className="text-lg font-semibold text-gray-900">Basic Information</h3>
            
//             <div>
//               <label className="block text-sm font-medium text-gray-700 mb-2">
//                 Website Name *
//               </label>
//               <input
//                 type="text"
//                 name="name"
//                 value={formData.name}
//                 onChange={handleInputChange}
//                 placeholder="Enter website name"
//                 className="input-field w-full"
//                 required
//               />
//             </div>

//             <div>
//               <label className="block text-sm font-medium text-gray-700 mb-2">
//                 Description
//               </label>
//               <textarea
//                 name="description"
//                 value={formData.description}
//                 onChange={handleInputChange}
//                 placeholder="Brief description of your website"
//                 rows="3"
//                 className="input-field w-full resize-none"
//               />
//             </div>

//             {/* Website Logo */}
//             <div className="mt-6 p-4 bg-gray-50 rounded-lg border border-gray-200">
//               <label className="block text-sm font-medium text-gray-700 mb-3">
//                 Website Logo
//               </label>
//               <p className="text-xs text-gray-600 mb-3">
//                 Upload a logo to display in the header of your published public website. Once uploaded, the logo will replace your website name. If no logo is uploaded, your website name will be displayed instead. Recommended: square image (PNG, JPG, GIF, WebP, or SVG). Max 5MB.
//               </p>

//               {/* Logo Preview */}
//               {logoPreview && (
//                 <div className="mb-4">
//                   <div className="relative inline-block">
//                     <img
//                       src={logoPreview}
//                       alt="Logo preview"
//                       className="max-w-xs max-h-24 rounded-lg border border-gray-300"
//                     />
//                   </div>
//                 </div>
//               )}

//               {/* File Input */}
//               <div className="flex gap-2">
//                 <label className="flex-1">
//                   <input
//                     type="file"
//                     accept="image/*"
//                     onChange={handleLogoChange}
//                     disabled={uploadingLogo}
//                     className="hidden"
//                   />
//                   <span className="inline-flex items-center gap-2 px-4 py-2 bg-blue-600 text-white hover:bg-blue-700 disabled:bg-gray-400 rounded-lg font-medium transition-colors cursor-pointer w-full justify-center">
//                     <FiUpload className="w-4 h-4" />
//                     Choose File
//                   </span>
//                 </label>

//                 {logoFile && !uploadingLogo && (
//                   <button
//                     type="button"
//                     onClick={handleUploadLogo}
//                     disabled={uploadingLogo}
//                     className="px-4 py-2 bg-green-600 text-white hover:bg-green-700 disabled:bg-gray-400 rounded-lg font-medium transition-colors flex items-center gap-2"
//                   >
//                     Upload
//                   </button>
//                 )}

//                 {logoPreview && (
//                   <button
//                     type="button"
//                     onClick={handleDeleteLogo}
//                     disabled={uploadingLogo}
//                     className="px-4 py-2 bg-red-600 text-white hover:bg-red-700 disabled:bg-gray-400 rounded-lg font-medium transition-colors flex items-center gap-2"
//                   >
//                     <FiTrash2 className="w-4 h-4" />
//                     Remove
//                   </button>
//                 )}

//                 {uploadingLogo && (
//                   <button
//                     type="button"
//                     disabled
//                     className="px-4 py-2 bg-gray-400 text-white rounded-lg font-medium flex items-center gap-2"
//                   >
//                     <FiLoader className="w-4 h-4 animate-spin" />
//                     Uploading...
//                   </button>
//                 )}
//               </div>

//               {logoFile && (
//                 <p className="text-xs text-gray-600 mt-2">
//                   Selected: {logoFile.name}
//                 </p>
//               )}
//             </div>
//           </div>

//           {/* SEO Settings */}
//           <div className="space-y-4 pt-6 border-t border-gray-200">
//             <h3 className="text-lg font-semibold text-gray-900">SEO Settings</h3>
            
//             <div>
//               <label className="block text-sm font-medium text-gray-700 mb-2">
//                 Meta Title
//               </label>
//               <input
//                 type="text"
//                 name="metaTitle"
//                 value={formData.metaTitle}
//                 onChange={handleInputChange}
//                 placeholder="Enter SEO title (60 characters)"
//                 maxLength="60"
//                 className="input-field w-full"
//               />
//               <p className="text-xs text-gray-500 mt-1">{formData.metaTitle.length}/60</p>
//             </div>

//             <div>
//               <label className="block text-sm font-medium text-gray-700 mb-2">
//                 Meta Description
//               </label>
//               <textarea
//                 name="metaDescription"
//                 value={formData.metaDescription}
//                 onChange={handleInputChange}
//                 placeholder="Enter SEO description (160 characters)"
//                 maxLength="160"
//                 rows="2"
//                 className="input-field w-full resize-none"
//               />
//               <p className="text-xs text-gray-500 mt-1">{formData.metaDescription.length}/160</p>
//             </div>

//             <div>
//               <label className="block text-sm font-medium text-gray-700 mb-2">
//                 Keywords
//               </label>
//               <input
//                 type="text"
//                 name="metaKeywords"
//                 value={formData.metaKeywords}
//                 onChange={handleInputChange}
//                 placeholder="Comma-separated keywords"
//                 className="input-field w-full"
//               />
//             </div>
//           </div>

//           {/* Custom Domain */}
//           <div className="space-y-4 pt-6 border-t border-gray-200">
//             <h3 className="text-lg font-semibold text-gray-900">Custom Domain</h3>
//             <p className="text-sm text-gray-600">Connect your own domain (e.g., example.com or www.example.com). Only one primary domain allowed.</p>

//             <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
//               <input
//                 type="text"
//                 placeholder="Enter custom domain (example.com)"
//                 value={website?.customDomain?.domain || ''}
//                 onChange={(e) => {
//                   const d = e.target.value
//                   // optimistic local update
//                   website.customDomain = website.customDomain || {}
//                   website.customDomain.domain = d
//                   setLogoFile(null) // no-op to force update? keep minimal
//                 }}
//                 className="input-field w-full col-span-2"
//               />

//               <div className="flex gap-2">
//                 <button
//                   type="button"
//                   onClick={async () => {
//                     if (!website || !website._id) return toast.error('Select a website first')
//                     const domain = (website.customDomain?.domain || '').trim()
//                     if (!domain) return toast.error('Enter a domain')
//                     try {
//                       setDomainAdding(true)
//                       const res = await axios.post(`/websites/${website._id}/custom-domain`, { domain })
//                       toast.success('Custom domain added. Follow DNS instructions to verify.')
//                     } catch (err) {
//                       console.error('Error adding domain:', err)
//                       toast.error(err.response?.data?.message || 'Failed to add domain')
//                     } finally {
//                       setDomainAdding(false)
//                     }
//                   }}
//                   disabled={domainAdding || domainVerifying || domainRemoving}
//                   className={`px-3 py-2 rounded-lg text-white ${domainAdding ? 'bg-gray-400' : 'bg-blue-600 hover:bg-blue-700'}`}
//                 >
//                   {domainAdding ? (
//                     <span className="inline-flex items-center gap-2"><FiLoader className="w-4 h-4 animate-spin" />Adding...</span>
//                   ) : 'Add Domain'}
//                 </button>

//                 <button
//                   type="button"
//                   onClick={async () => {
//                     if (!website || !website._id) return toast.error('Select a website first')
//                     try {
//                       setDomainVerifying(true)
//                       const res = await axios.post(`/websites/${website._id}/custom-domain/verify`)
//                       toast.success('Domain verified and activated')
//                     } catch (err) {
//                       console.error('Error verifying domain:', err)
//                       toast.error(err.response?.data?.message || 'Domain verification failed')
//                     } finally {
//                       setDomainVerifying(false)
//                     }
//                   }}
//                   disabled={domainAdding || domainVerifying || domainRemoving}
//                   className={`px-3 py-2 rounded-lg text-white ${domainVerifying ? 'bg-gray-400' : 'bg-green-600 hover:bg-green-700'}`}
//                 >
//                   {domainVerifying ? (
//                     <span className="inline-flex items-center gap-2"><FiLoader className="w-4 h-4 animate-spin" />Verifying...</span>
//                   ) : 'Verify'}
//                 </button>

//                 <button
//                   type="button"
//                   onClick={async () => {
//                     if (!website || !website._id) return toast.error('Select a website first')
//                     if (!window.confirm('Remove custom domain?')) return
//                     try {
//                       setDomainRemoving(true)
//                       await axios.delete(`/websites/${website._id}/custom-domain`)
//                       toast.success('Custom domain removed')
//                     } catch (err) {
//                       console.error('Error removing domain:', err)
//                       toast.error(err.response?.data?.message || 'Failed to remove domain')
//                     } finally {
//                       setDomainRemoving(false)
//                     }
//                   }}
//                   disabled={domainAdding || domainVerifying || domainRemoving}
//                   className={`px-3 py-2 rounded-lg text-white ${domainRemoving ? 'bg-gray-400' : 'bg-red-600 hover:bg-red-700'}`}
//                 >
//                   {domainRemoving ? (
//                     <span className="inline-flex items-center gap-2"><FiLoader className="w-4 h-4 animate-spin" />Removing...</span>
//                   ) : 'Remove'}
//                 </button>
//               </div>

//               <div className="col-span-3 text-xs text-gray-600">
//                 <p>DNS Instructions:</p>
//                 <ul className="list-disc ml-5">
//                   <li>Point your root domain or APEX to your DNS provider configuration — Render and Vercel do not provide static platform IPs.</li>
//                   <li>OR add a CNAME for a subdomain (e.g., <code>www</code>) pointing to the platform host: <strong>{process.env.REACT_APP_PLATFORM_PUBLIC_URL ? (() => { try { return new URL(process.env.REACT_APP_PLATFORM_PUBLIC_URL).hostname } catch { return process.env.REACT_APP_PLATFORM_PUBLIC_URL } })() : window.location.hostname}</strong></li>
//                   <li>Optional TXT verification: add a TXT record with the verification token shown in the website settings after adding the domain.</li>
//                 </ul>
//               </div>
//             </div>
//           </div>

//           {/* Design Settings */}
//           <div className="space-y-4 pt-6 border-t border-gray-200">
//             <h3 className="text-lg font-semibold text-gray-900">Design Settings</h3>

//             {/* Colors */}
//             <div className="space-y-4">
//               <p className="text-sm text-gray-600">Colors</p>
//               <div className="grid grid-cols-3 gap-4">
//                 <div>
//                   <label className="block text-xs font-medium text-gray-700 mb-2">
//                     Primary Color
//                   </label>
//                   <div className="flex items-center gap-2">
//                     <input
//                       type="color"
//                       value={formData.primaryColor}
//                       onChange={(e) => handleColorChange('primaryColor', e.target.value)}
//                       className="w-12 h-10 rounded cursor-pointer border border-gray-300"
//                     />
//                     <input
//                       type="text"
//                       value={formData.primaryColor}
//                       onChange={(e) => handleColorChange('primaryColor', e.target.value)}
//                       className="input-field flex-1 font-mono text-sm"
//                       maxLength="7"
//                     />
//                   </div>
//                 </div>

//                 <div>
//                   <label className="block text-xs font-medium text-gray-700 mb-2">
//                     Secondary Color
//                   </label>
//                   <div className="flex items-center gap-2">
//                     <input
//                       type="color"
//                       value={formData.secondaryColor}
//                       onChange={(e) => handleColorChange('secondaryColor', e.target.value)}
//                       className="w-12 h-10 rounded cursor-pointer border border-gray-300"
//                     />
//                     <input
//                       type="text"
//                       value={formData.secondaryColor}
//                       onChange={(e) => handleColorChange('secondaryColor', e.target.value)}
//                       className="input-field flex-1 font-mono text-sm"
//                       maxLength="7"
//                     />
//                   </div>
//                 </div>

//                 <div>
//                   <label className="block text-xs font-medium text-gray-700 mb-2">
//                     Accent Color
//                   </label>
//                   <div className="flex items-center gap-2">
//                     <input
//                       type="color"
//                       value={formData.accentColor}
//                       onChange={(e) => handleColorChange('accentColor', e.target.value)}
//                       className="w-12 h-10 rounded cursor-pointer border border-gray-300"
//                     />
//                     <input
//                       type="text"
//                       value={formData.accentColor}
//                       onChange={(e) => handleColorChange('accentColor', e.target.value)}
//                       className="input-field flex-1 font-mono text-sm"
//                       maxLength="7"
//                     />
//                   </div>
//                 </div>
//               </div>
//             </div>

//             {/* Fonts */}
//             <div className="space-y-3">
//               <p className="text-sm text-gray-600">Fonts</p>
//               <div className="grid grid-cols-2 gap-4">
//                 <div>
//                   <label className="block text-xs font-medium text-gray-700 mb-2">
//                     Primary Font
//                   </label>
//                   <select
//                     name="primaryFont"
//                     value={formData.primaryFont}
//                     onChange={handleInputChange}
//                     className="input-field w-full"
//                   >
//                     <option value="Inter">Inter</option>
//                     <option value="Arial">Arial</option>
//                     <option value="Georgia">Georgia</option>
//                     <option value="Times New Roman">Times New Roman</option>
//                     <option value="Verdana">Verdana</option>
//                   </select>
//                 </div>
//                 <div>
//                   <label className="block text-xs font-medium text-gray-700 mb-2">
//                     Secondary Font
//                   </label>
//                   <select
//                     name="secondaryFont"
//                     value={formData.secondaryFont}
//                     onChange={handleInputChange}
//                     className="input-field w-full"
//                   >
//                     <option value="Inter">Inter</option>
//                     <option value="Arial">Arial</option>
//                     <option value="Georgia">Georgia</option>
//                     <option value="Times New Roman">Times New Roman</option>
//                     <option value="Verdana">Verdana</option>
//                   </select>
//                 </div>
//               </div>
//             </div>
//           </div>

//           {/* Action Buttons */}
//           <div className="flex gap-3 pt-6 border-t border-gray-200">
//             <button
//               type="button"
//               onClick={onClose}
//               disabled={isSaving}
//               className="flex-1 py-2 px-4 bg-gray-100 text-gray-700 hover:bg-gray-200 rounded-lg font-medium transition-colors disabled:opacity-50"
//             >
//               Cancel
//             </button>
//             <button
//               type="submit"
//               disabled={isSaving}
//               className="flex-1 py-2 px-4 bg-primary-600 text-white hover:bg-primary-700 disabled:bg-gray-400 rounded-lg font-medium transition-colors flex items-center justify-center gap-2 disabled:cursor-not-allowed"
//             >
//               {isSaving ? (
//                 <>
//                   <FiLoader className="w-4 h-4 animate-spin" /> Saving...
//                 </>
//               ) : (
//                 <>
//                   <FiSave className="w-4 h-4" /> Save Website
//                 </>
//               )}
//             </button>
//           </div>
//         </form>
//       </div>
//     </div>
//   )
// }











// import { useState, useEffect } from 'react'
// import { FiX, FiLoader, FiSave, FiTrash2, FiUpload } from 'react-icons/fi'
// import toast from 'react-hot-toast'
// import axios from 'axios'

// export default function WebsiteEditorModal({ isOpen, onClose, onSubmit, website, categories, isSaving, mode, onLogoUpload, onLogoDelete }) {
//   const [formData, setFormData] = useState({
//     name: '',
//     description: '',
//     metaTitle: '',
//     metaDescription: '',
//     metaKeywords: '',
//     primaryColor: '#3B82F6',
//     secondaryColor: '#1F2937',
//     accentColor: '#F97316',
//     primaryFont: 'Inter',
//     secondaryFont: 'Inter'
//   })
//   const [logoFile, setLogoFile] = useState(null)
//   const [logoPreview, setLogoPreview] = useState(null)
//   const [uploadingLogo, setUploadingLogo] = useState(false)
//   const [domainAdding, setDomainAdding] = useState(false)
//   const [domainVerifying, setDomainVerifying] = useState(false)
//   const [domainRemoving, setDomainRemoving] = useState(false)

//   useEffect(() => {
//     if (website && isOpen) {
//       setFormData({
//         name: website.name || '',
//         description: website.description || '',
//         metaTitle: website.seo?.metaTitle || '',
//         metaDescription: website.seo?.metaDescription || '',
//         metaKeywords: website.seo?.metaKeywords || '',
//         primaryColor: website.colors?.primary || '#3B82F6',
//         secondaryColor: website.colors?.secondary || '#1F2937',
//         accentColor: website.colors?.accent || '#F97316',
//         primaryFont: website.fonts?.primary || 'Inter',
//         secondaryFont: website.fonts?.secondary || 'Inter'
//       })
      
//       // Show the saved/uploaded logo from backend ONLY if it exists and is a stored file
//       if (website.logo?.url && !website.logo.url.startsWith('data:') && !website.logo.url.startsWith('blob:')) {
//         const backendOrigin = axios.defaults.baseURL ? axios.defaults.baseURL.replace(/\/api\/?$/, '') : ''
//         const storedUrl = website.logo.url
//         let previewUrl = storedUrl
        
//         // Convert relative path to absolute URL for display
//         if (!storedUrl.startsWith('http')) {
//           const pathUrl = storedUrl.startsWith('/') ? storedUrl : `/${storedUrl}`
//           previewUrl = backendOrigin ? `${backendOrigin}${pathUrl}` : pathUrl
//         }
        
//         setLogoPreview(previewUrl)
//         console.log('[WebsiteEditor] Showing saved logo from backend:', previewUrl)
//       } else {
//         // No saved logo - start fresh for new upload
//         setLogoPreview(null)
//         console.log('[WebsiteEditor] No saved logo found')
//       }
      
//       setLogoFile(null)
//     }
//   }, [website, isOpen])

//   const handleInputChange = (e) => {
//     const { name, value } = e.target
//     setFormData(prev => ({
//       ...prev,
//       [name]: value
//     }))
//   }

//   const handleColorChange = (colorField, value) => {
//     setFormData(prev => ({
//       ...prev,
//       [colorField]: value
//     }))
//   }

//   const handleLogoChange = (e) => {
//     const file = e.target.files?.[0]
//     if (!file) return

//     if (!file.type.startsWith('image/')) {
//       toast.error('Please select an image file')
//       return
//     }

//     if (file.size > 5 * 1024 * 1024) {
//       toast.error('File size must be less than 5MB')
//       return
//     }

//     setLogoFile(file)
//     const reader = new FileReader()
//     reader.onload = (evt) => {
//       setLogoPreview(evt.target?.result)
//     }
//     reader.readAsDataURL(file)
//   }

//   const handleUploadLogo = async () => {
//     if (!logoFile || !website?._id) {
//       toast.error('No logo file selected')
//       return
//     }

//     try {
//       setUploadingLogo(true)
//       const formDataUpload = new FormData()
//       formDataUpload.append('logo', logoFile)

//       const response = await axios.post(`/websites/${website._id}/logo`, formDataUpload, {
//         headers: {
//           'Content-Type': 'multipart/form-data'
//         }
//       })

//       // Immediately show the uploaded logo in preview from backend
//       const returnedWebsite = response.data?.website
//       const returnedLogoUrl = returnedWebsite?.logo?.url
      
//       if (returnedLogoUrl) {
//         // Build absolute URL for display (logo is stored as relative path on backend)
//         let displayUrl = returnedLogoUrl
        
//         // If relative path and we have baseURL, use it to construct absolute URL
//         if (!returnedLogoUrl.startsWith('http') && !returnedLogoUrl.startsWith('data:') && !returnedLogoUrl.startsWith('blob:')) {
//           const backendOrigin = axios.defaults.baseURL ? axios.defaults.baseURL.replace(/\/api\/?$/, '') : ''
//           const pathUrl = returnedLogoUrl.startsWith('/') ? returnedLogoUrl : `/${returnedLogoUrl}`
          
//           if (backendOrigin) {
//             displayUrl = `${backendOrigin}${pathUrl}`
//           } else {
//             // Fallback to relative path - browser will resolve it
//             displayUrl = pathUrl
//           }
//         }
        
//         setLogoPreview(displayUrl)
//         console.log('[WebsiteEditor] Logo uploaded successfully - preview:', displayUrl)
//         toast.success('✅ Logo uploaded and saved successfully!')
//       } else {
//         toast.error('Upload succeeded but logo URL is missing')
//       }
      
//       // Call parent callback to update website data with the uploaded logo
//       if (onLogoUpload && returnedWebsite) {
//         onLogoUpload(returnedWebsite)
//         console.log('[WebsiteEditor] Called onLogoUpload callback')
//       }
      
//       // Clear file input after successful upload
//       setLogoFile(null)
//     } catch (error) {
//       console.error('[Logo Upload] Error:', error)
//       toast.error(error.response?.data?.message || 'Failed to upload logo')
//     } finally {
//       setUploadingLogo(false)
//     }
//   }

//   const handleDeleteLogo = async () => {
//     if (!website?._id) return

//     if (!window.confirm('Are you sure you want to delete the logo?')) return

//     try {
//       setUploadingLogo(true)
//       const response = await axios.delete(`/websites/${website._id}/logo`)
//       toast.success('Logo deleted successfully')
//       setLogoPreview(null)
//       setLogoFile(null)
      
//       // Call parent callback to update website data after logo deletion
//       if (onLogoDelete && response.data?.website) {
//         onLogoDelete(response.data.website)
//         console.log('[WebsiteEditor] Called onLogoDelete callback with updated website')
//       }
//     } catch (error) {
//       console.error('[Logo Delete] Error:', error)
//       toast.error(error.response?.data?.message || 'Failed to delete logo')
//     } finally {
//       setUploadingLogo(false)
//     }
//   }

//   const handleSubmit = (e) => {
//     e.preventDefault()
    
//     if (!formData.name.trim()) {
//       toast.error('Website name is required')
//       return
//     }

//     if (!website || !website._id) {
//       toast.error('Invalid website - please select a website to edit')
//       return
//     }

//     const submitData = {
//       name: formData.name.trim(),
//       description: formData.description,
//       seo: {
//         metaTitle: formData.metaTitle,
//         metaDescription: formData.metaDescription,
//         metaKeywords: formData.metaKeywords
//       },
//       colors: {
//         primary: formData.primaryColor,
//         secondary: formData.secondaryColor,
//         accent: formData.accentColor
//       },
//       fonts: {
//         primary: formData.primaryFont,
//         secondary: formData.secondaryFont
//       }
//     }

//     onSubmit(submitData)
//   }

//   if (!isOpen) return null

//   return (
//     <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
//       <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
//         {/* Header */}
//         <div className="flex justify-between items-center p-6 border-b border-gray-200 sticky top-0 bg-white">
//           <h2 className="text-2xl font-bold text-gray-900">Edit Website</h2>
//           <button
//             onClick={onClose}
//             disabled={isSaving}
//             className="p-1 hover:bg-gray-100 rounded-lg transition-colors disabled:opacity-50"
//           >
//             <FiX className="w-6 h-6" />
//           </button>
//         </div>

//         {/* Content */}
//         <form onSubmit={handleSubmit} className="p-6 space-y-6">
//           {/* Basic Information */}
//           <div className="space-y-4">
//             <h3 className="text-lg font-semibold text-gray-900">Basic Information</h3>
            
//             <div>
//               <label className="block text-sm font-medium text-gray-700 mb-2">
//                 Website Name *
//               </label>
//               <input
//                 type="text"
//                 name="name"
//                 value={formData.name}
//                 onChange={handleInputChange}
//                 placeholder="Enter website name"
//                 className="input-field w-full"
//                 required
//               />
//             </div>

//             <div>
//               <label className="block text-sm font-medium text-gray-700 mb-2">
//                 Description
//               </label>
//               <textarea
//                 name="description"
//                 value={formData.description}
//                 onChange={handleInputChange}
//                 placeholder="Brief description of your website"
//                 rows="3"
//                 className="input-field w-full resize-none"
//               />
//             </div>

//             {/* Website Logo */}
//             <div className="mt-6 p-4 bg-gray-50 rounded-lg border border-gray-200">
//               <label className="block text-sm font-medium text-gray-700 mb-3">
//                 Website Logo
//               </label>
//               <p className="text-xs text-gray-600 mb-3">
//                 Upload a logo to replace the website name on your public site. Recommended: square image (PNG, JPG, GIF, WebP, or SVG). Max 5MB.
//               </p>

//               {/* Logo Preview */}
//               {logoPreview && (
//                 <div className="mb-4">
//                   <div className="relative inline-block">
//                     <img
//                       src={logoPreview}
//                       alt="Logo preview"
//                       className="max-w-xs max-h-24 rounded-lg border border-gray-300"
//                     />
//                   </div>
//                 </div>
//               )}

//               {/* File Input */}
//               <div className="flex gap-2">
//                 <label className="flex-1">
//                   <input
//                     type="file"
//                     accept="image/*"
//                     onChange={handleLogoChange}
//                     disabled={uploadingLogo}
//                     className="hidden"
//                   />
//                   <span className="inline-flex items-center gap-2 px-4 py-2 bg-blue-600 text-white hover:bg-blue-700 disabled:bg-gray-400 rounded-lg font-medium transition-colors cursor-pointer w-full justify-center">
//                     <FiUpload className="w-4 h-4" />
//                     Choose File
//                   </span>
//                 </label>

//                 {logoFile && !uploadingLogo && (
//                   <button
//                     type="button"
//                     onClick={handleUploadLogo}
//                     disabled={uploadingLogo}
//                     className="px-4 py-2 bg-green-600 text-white hover:bg-green-700 disabled:bg-gray-400 rounded-lg font-medium transition-colors flex items-center gap-2"
//                   >
//                     Upload
//                   </button>
//                 )}

//                 {logoPreview && (
//                   <button
//                     type="button"
//                     onClick={handleDeleteLogo}
//                     disabled={uploadingLogo}
//                     className="px-4 py-2 bg-red-600 text-white hover:bg-red-700 disabled:bg-gray-400 rounded-lg font-medium transition-colors flex items-center gap-2"
//                   >
//                     <FiTrash2 className="w-4 h-4" />
//                     Remove
//                   </button>
//                 )}

//                 {uploadingLogo && (
//                   <button
//                     type="button"
//                     disabled
//                     className="px-4 py-2 bg-gray-400 text-white rounded-lg font-medium flex items-center gap-2"
//                   >
//                     <FiLoader className="w-4 h-4 animate-spin" />
//                     Uploading...
//                   </button>
//                 )}
//               </div>

//               {logoFile && (
//                 <p className="text-xs text-gray-600 mt-2">
//                   Selected: {logoFile.name}
//                 </p>
//               )}
//             </div>
//           </div>

//           {/* SEO Settings */}
//           <div className="space-y-4 pt-6 border-t border-gray-200">
//             <h3 className="text-lg font-semibold text-gray-900">SEO Settings</h3>
            
//             <div>
//               <label className="block text-sm font-medium text-gray-700 mb-2">
//                 Meta Title
//               </label>
//               <input
//                 type="text"
//                 name="metaTitle"
//                 value={formData.metaTitle}
//                 onChange={handleInputChange}
//                 placeholder="Enter SEO title (60 characters)"
//                 maxLength="60"
//                 className="input-field w-full"
//               />
//               <p className="text-xs text-gray-500 mt-1">{formData.metaTitle.length}/60</p>
//             </div>

//             <div>
//               <label className="block text-sm font-medium text-gray-700 mb-2">
//                 Meta Description
//               </label>
//               <textarea
//                 name="metaDescription"
//                 value={formData.metaDescription}
//                 onChange={handleInputChange}
//                 placeholder="Enter SEO description (160 characters)"
//                 maxLength="160"
//                 rows="2"
//                 className="input-field w-full resize-none"
//               />
//               <p className="text-xs text-gray-500 mt-1">{formData.metaDescription.length}/160</p>
//             </div>

//             <div>
//               <label className="block text-sm font-medium text-gray-700 mb-2">
//                 Keywords
//               </label>
//               <input
//                 type="text"
//                 name="metaKeywords"
//                 value={formData.metaKeywords}
//                 onChange={handleInputChange}
//                 placeholder="Comma-separated keywords"
//                 className="input-field w-full"
//               />
//             </div>
//           </div>

//           {/* Custom Domain */}
//           <div className="space-y-4 pt-6 border-t border-gray-200">
//             <h3 className="text-lg font-semibold text-gray-900">Custom Domain</h3>
//             <p className="text-sm text-gray-600">Connect your own domain (e.g., example.com or www.example.com). Only one primary domain allowed.</p>

//             <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
//               <input
//                 type="text"
//                 placeholder="Enter custom domain (example.com)"
//                 value={website?.customDomain?.domain || ''}
//                 onChange={(e) => {
//                   const d = e.target.value
//                   // optimistic local update
//                   website.customDomain = website.customDomain || {}
//                   website.customDomain.domain = d
//                   setLogoFile(null) // no-op to force update? keep minimal
//                 }}
//                 className="input-field w-full col-span-2"
//               />

//               <div className="flex gap-2">
//                 <button
//                   type="button"
//                   onClick={async () => {
//                     if (!website || !website._id) return toast.error('Select a website first')
//                     const domain = (website.customDomain?.domain || '').trim()
//                     if (!domain) return toast.error('Enter a domain')
//                     try {
//                       setDomainAdding(true)
//                       const res = await axios.post(`/websites/${website._id}/custom-domain`, { domain })
//                       toast.success('Custom domain added. Follow DNS instructions to verify.')
//                     } catch (err) {
//                       console.error('Error adding domain:', err)
//                       toast.error(err.response?.data?.message || 'Failed to add domain')
//                     } finally {
//                       setDomainAdding(false)
//                     }
//                   }}
//                   disabled={domainAdding || domainVerifying || domainRemoving}
//                   className={`px-3 py-2 rounded-lg text-white ${domainAdding ? 'bg-gray-400' : 'bg-blue-600 hover:bg-blue-700'}`}
//                 >
//                   {domainAdding ? (
//                     <span className="inline-flex items-center gap-2"><FiLoader className="w-4 h-4 animate-spin" />Adding...</span>
//                   ) : 'Add Domain'}
//                 </button>

//                 <button
//                   type="button"
//                   onClick={async () => {
//                     if (!website || !website._id) return toast.error('Select a website first')
//                     try {
//                       setDomainVerifying(true)
//                       const res = await axios.post(`/websites/${website._id}/custom-domain/verify`)
//                       toast.success('Domain verified and activated')
//                     } catch (err) {
//                       console.error('Error verifying domain:', err)
//                       toast.error(err.response?.data?.message || 'Domain verification failed')
//                     } finally {
//                       setDomainVerifying(false)
//                     }
//                   }}
//                   disabled={domainAdding || domainVerifying || domainRemoving}
//                   className={`px-3 py-2 rounded-lg text-white ${domainVerifying ? 'bg-gray-400' : 'bg-green-600 hover:bg-green-700'}`}
//                 >
//                   {domainVerifying ? (
//                     <span className="inline-flex items-center gap-2"><FiLoader className="w-4 h-4 animate-spin" />Verifying...</span>
//                   ) : 'Verify'}
//                 </button>

//                 <button
//                   type="button"
//                   onClick={async () => {
//                     if (!website || !website._id) return toast.error('Select a website first')
//                     if (!window.confirm('Remove custom domain?')) return
//                     try {
//                       setDomainRemoving(true)
//                       await axios.delete(`/websites/${website._id}/custom-domain`)
//                       toast.success('Custom domain removed')
//                     } catch (err) {
//                       console.error('Error removing domain:', err)
//                       toast.error(err.response?.data?.message || 'Failed to remove domain')
//                     } finally {
//                       setDomainRemoving(false)
//                     }
//                   }}
//                   disabled={domainAdding || domainVerifying || domainRemoving}
//                   className={`px-3 py-2 rounded-lg text-white ${domainRemoving ? 'bg-gray-400' : 'bg-red-600 hover:bg-red-700'}`}
//                 >
//                   {domainRemoving ? (
//                     <span className="inline-flex items-center gap-2"><FiLoader className="w-4 h-4 animate-spin" />Removing...</span>
//                   ) : 'Remove'}
//                 </button>
//               </div>

//               <div className="col-span-3 text-xs text-gray-600">
//                 <p>DNS Instructions:</p>
//                 <ul className="list-disc ml-5">
//                   <li>Point your root domain or APEX to your DNS provider configuration — Render and Vercel do not provide static platform IPs.</li>
//                   <li>OR add a CNAME for a subdomain (e.g., <code>www</code>) pointing to the platform host: <strong>{process.env.REACT_APP_PLATFORM_PUBLIC_URL ? (() => { try { return new URL(process.env.REACT_APP_PLATFORM_PUBLIC_URL).hostname } catch { return process.env.REACT_APP_PLATFORM_PUBLIC_URL } })() : window.location.hostname}</strong></li>
//                   <li>Optional TXT verification: add a TXT record with the verification token shown in the website settings after adding the domain.</li>
//                 </ul>
//               </div>
//             </div>
//           </div>

//           {/* Design Settings */}
//           <div className="space-y-4 pt-6 border-t border-gray-200">
//             <h3 className="text-lg font-semibold text-gray-900">Design Settings</h3>

//             {/* Colors */}
//             <div className="space-y-4">
//               <p className="text-sm text-gray-600">Colors</p>
//               <div className="grid grid-cols-3 gap-4">
//                 <div>
//                   <label className="block text-xs font-medium text-gray-700 mb-2">
//                     Primary Color
//                   </label>
//                   <div className="flex items-center gap-2">
//                     <input
//                       type="color"
//                       value={formData.primaryColor}
//                       onChange={(e) => handleColorChange('primaryColor', e.target.value)}
//                       className="w-12 h-10 rounded cursor-pointer border border-gray-300"
//                     />
//                     <input
//                       type="text"
//                       value={formData.primaryColor}
//                       onChange={(e) => handleColorChange('primaryColor', e.target.value)}
//                       className="input-field flex-1 font-mono text-sm"
//                       maxLength="7"
//                     />
//                   </div>
//                 </div>

//                 <div>
//                   <label className="block text-xs font-medium text-gray-700 mb-2">
//                     Secondary Color
//                   </label>
//                   <div className="flex items-center gap-2">
//                     <input
//                       type="color"
//                       value={formData.secondaryColor}
//                       onChange={(e) => handleColorChange('secondaryColor', e.target.value)}
//                       className="w-12 h-10 rounded cursor-pointer border border-gray-300"
//                     />
//                     <input
//                       type="text"
//                       value={formData.secondaryColor}
//                       onChange={(e) => handleColorChange('secondaryColor', e.target.value)}
//                       className="input-field flex-1 font-mono text-sm"
//                       maxLength="7"
//                     />
//                   </div>
//                 </div>

//                 <div>
//                   <label className="block text-xs font-medium text-gray-700 mb-2">
//                     Accent Color
//                   </label>
//                   <div className="flex items-center gap-2">
//                     <input
//                       type="color"
//                       value={formData.accentColor}
//                       onChange={(e) => handleColorChange('accentColor', e.target.value)}
//                       className="w-12 h-10 rounded cursor-pointer border border-gray-300"
//                     />
//                     <input
//                       type="text"
//                       value={formData.accentColor}
//                       onChange={(e) => handleColorChange('accentColor', e.target.value)}
//                       className="input-field flex-1 font-mono text-sm"
//                       maxLength="7"
//                     />
//                   </div>
//                 </div>
//               </div>
//             </div>

//             {/* Fonts */}
//             <div className="space-y-3">
//               <p className="text-sm text-gray-600">Fonts</p>
//               <div className="grid grid-cols-2 gap-4">
//                 <div>
//                   <label className="block text-xs font-medium text-gray-700 mb-2">
//                     Primary Font
//                   </label>
//                   <select
//                     name="primaryFont"
//                     value={formData.primaryFont}
//                     onChange={handleInputChange}
//                     className="input-field w-full"
//                   >
//                     <option value="Inter">Inter</option>
//                     <option value="Arial">Arial</option>
//                     <option value="Georgia">Georgia</option>
//                     <option value="Times New Roman">Times New Roman</option>
//                     <option value="Verdana">Verdana</option>
//                   </select>
//                 </div>
//                 <div>
//                   <label className="block text-xs font-medium text-gray-700 mb-2">
//                     Secondary Font
//                   </label>
//                   <select
//                     name="secondaryFont"
//                     value={formData.secondaryFont}
//                     onChange={handleInputChange}
//                     className="input-field w-full"
//                   >
//                     <option value="Inter">Inter</option>
//                     <option value="Arial">Arial</option>
//                     <option value="Georgia">Georgia</option>
//                     <option value="Times New Roman">Times New Roman</option>
//                     <option value="Verdana">Verdana</option>
//                   </select>
//                 </div>
//               </div>
//             </div>
//           </div>

//           {/* Action Buttons */}
//           <div className="flex gap-3 pt-6 border-t border-gray-200">
//             <button
//               type="button"
//               onClick={onClose}
//               disabled={isSaving}
//               className="flex-1 py-2 px-4 bg-gray-100 text-gray-700 hover:bg-gray-200 rounded-lg font-medium transition-colors disabled:opacity-50"
//             >
//               Cancel
//             </button>
//             <button
//               type="submit"
//               disabled={isSaving}
//               className="flex-1 py-2 px-4 bg-primary-600 text-white hover:bg-primary-700 disabled:bg-gray-400 rounded-lg font-medium transition-colors flex items-center justify-center gap-2 disabled:cursor-not-allowed"
//             >
//               {isSaving ? (
//                 <>
//                   <FiLoader className="w-4 h-4 animate-spin" /> Saving...
//                 </>
//               ) : (
//                 <>
//                   <FiSave className="w-4 h-4" /> Save Website
//                 </>
//               )}
//             </button>
//           </div>
//         </form>
//       </div>
//     </div>
//   )
// }


















// import { useState, useEffect } from 'react'
// import { FiX, FiLoader, FiSave, FiTrash2, FiUpload } from 'react-icons/fi'
// import toast from 'react-hot-toast'
// import axios from 'axios'

// export default function WebsiteEditorModal({ isOpen, onClose, onSubmit, website, categories, isSaving, mode }) {
//   const [formData, setFormData] = useState({
//     name: '',
//     description: '',
//     metaTitle: '',
//     metaDescription: '',
//     metaKeywords: '',
//     primaryColor: '#3B82F6',
//     secondaryColor: '#1F2937',
//     accentColor: '#F97316',
//     primaryFont: 'Inter',
//     secondaryFont: 'Inter'
//   })
//   const [logoFile, setLogoFile] = useState(null)
//   const [logoPreview, setLogoPreview] = useState(null)
//   const [uploadingLogo, setUploadingLogo] = useState(false)

//   useEffect(() => {
//     if (website && isOpen) {
//       setFormData({
//         name: website.name || '',
//         description: website.description || '',
//         metaTitle: website.seo?.metaTitle || '',
//         metaDescription: website.seo?.metaDescription || '',
//         metaKeywords: website.seo?.metaKeywords || '',
//         primaryColor: website.colors?.primary || '#3B82F6',
//         secondaryColor: website.colors?.secondary || '#1F2937',
//         accentColor: website.colors?.accent || '#F97316',
//         primaryFont: website.fonts?.primary || 'Inter',
//         secondaryFont: website.fonts?.secondary || 'Inter'
//       })
      
//       // Show the saved/uploaded logo from backend if it exists
//       if (website.logo?.url && !website.logo.url.startsWith('data:') && !website.logo.url.startsWith('blob:')) {
//         const backendOrigin = axios.defaults.baseURL ? axios.defaults.baseURL.replace(/\/api\/?$/, '') : ''
//         const storedUrl = website.logo.url
//         let previewUrl = storedUrl
        
//         if (!storedUrl.startsWith('http')) {
//           const pathUrl = storedUrl.startsWith('/') ? storedUrl : `/${storedUrl}`
//           previewUrl = backendOrigin ? `${backendOrigin}${pathUrl}` : pathUrl
//         }
        
//         setLogoPreview(previewUrl)
//         console.log('[WebsiteEditor] Modal opened with saved/uploaded logo:', previewUrl)
//       } else {
//         setLogoPreview(null)
//       }
      
//       setLogoFile(null)
//     }
//   }, [website, isOpen])

//   const handleInputChange = (e) => {
//     const { name, value } = e.target
//     setFormData(prev => ({
//       ...prev,
//       [name]: value
//     }))
//   }

//   const handleColorChange = (colorField, value) => {
//     setFormData(prev => ({
//       ...prev,
//       [colorField]: value
//     }))
//   }

//   const handleLogoChange = (e) => {
//     const file = e.target.files?.[0]
//     if (!file) return

//     if (!file.type.startsWith('image/')) {
//       toast.error('Please select an image file')
//       return
//     }

//     if (file.size > 5 * 1024 * 1024) {
//       toast.error('File size must be less than 5MB')
//       return
//     }

//     setLogoFile(file)
//     const reader = new FileReader()
//     reader.onload = (evt) => {
//       setLogoPreview(evt.target?.result)
//     }
//     reader.readAsDataURL(file)
//   }

//   const handleUploadLogo = async () => {
//     if (!logoFile || !website?._id) {
//       toast.error('No logo file selected')
//       return
//     }

//     try {
//       setUploadingLogo(true)
//       const formDataUpload = new FormData()
//       formDataUpload.append('logo', logoFile)

//       const response = await axios.post(`/websites/${website._id}/logo`, formDataUpload, {
//         headers: {
//           'Content-Type': 'multipart/form-data'
//         }
//       })

//       toast.success('✅ Logo uploaded and saved successfully')
      
//       // Get the uploaded logo URL from server response and show it in preview
//       const returnedWebsite = response.data?.website
//       const returnedLogoUrl = returnedWebsite?.logo?.url
//       console.log('[WebsiteEditor] Upload response website:', returnedWebsite)
//       console.log('[WebsiteEditor] Returned logo URL:', returnedLogoUrl)
      
//       if (returnedLogoUrl) {
//         // Normalize the URL to absolute so it displays properly
//         const backendOrigin = axios.defaults.baseURL ? axios.defaults.baseURL.replace(/\/api\/?$/, '') : ''
//         let absoluteLogoUrl = returnedLogoUrl
        
//         // Only convert relative paths to absolute; keep http(s) URLs as-is
//         if (!returnedLogoUrl.startsWith('http') && !returnedLogoUrl.startsWith('data:') && !returnedLogoUrl.startsWith('blob:')) {
//           const pathUrl = returnedLogoUrl.startsWith('/') ? returnedLogoUrl : `/${returnedLogoUrl}`
//           absoluteLogoUrl = backendOrigin ? `${backendOrigin}${pathUrl}` : pathUrl
//         }
        
//         console.log('[WebsiteEditor] Setting preview to:', absoluteLogoUrl)
//         setLogoPreview(absoluteLogoUrl) // Show the uploaded logo in preview
//       }
      
//       setLogoFile(null) // Clear file input after successful upload
      
//       // Logo is already saved on server — don't trigger onSubmit yet
//       // User should click "Save Website" to persist other form changes if needed
//     } catch (error) {
//       console.error('[Logo Upload] Error:', error)
//       toast.error(error.response?.data?.message || 'Failed to upload logo')
//     } finally {
//       setUploadingLogo(false)
//     }
//   }

//   const handleDeleteLogo = async () => {
//     if (!website?._id) return

//     if (!window.confirm('Are you sure you want to delete the logo?')) return

//     try {
//       setUploadingLogo(true)
//       await axios.delete(`/websites/${website._id}/logo`)
//       toast.success('Logo deleted successfully')
//       setLogoPreview(null)
//       setLogoFile(null)
//       // Logo is already deleted on server — don't trigger onSubmit yet
//       // User should click "Save Website" to persist other form changes if needed
//     } catch (error) {
//       console.error('[Logo Delete] Error:', error)
//       toast.error(error.response?.data?.message || 'Failed to delete logo')
//     } finally {
//       setUploadingLogo(false)
//     }
//   }

//   const handleSubmit = (e) => {
//     e.preventDefault()
    
//     if (!formData.name.trim()) {
//       toast.error('Website name is required')
//       return
//     }

//     if (!website || !website._id) {
//       toast.error('Invalid website - please select a website to edit')
//       return
//     }

//     const submitData = {
//       name: formData.name.trim(),
//       description: formData.description,
//       seo: {
//         metaTitle: formData.metaTitle,
//         metaDescription: formData.metaDescription,
//         metaKeywords: formData.metaKeywords
//       },
//       colors: {
//         primary: formData.primaryColor,
//         secondary: formData.secondaryColor,
//         accent: formData.accentColor
//       },
//       fonts: {
//         primary: formData.primaryFont,
//         secondary: formData.secondaryFont
//       }
//     }

//     onSubmit(submitData)
//   }

//   if (!isOpen) return null

//   return (
//     <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
//       <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
//         {/* Header */}
//         <div className="flex justify-between items-center p-6 border-b border-gray-200 sticky top-0 bg-white">
//           <h2 className="text-2xl font-bold text-gray-900">Edit Website</h2>
//           <button
//             onClick={onClose}
//             disabled={isSaving}
//             className="p-1 hover:bg-gray-100 rounded-lg transition-colors disabled:opacity-50"
//           >
//             <FiX className="w-6 h-6" />
//           </button>
//         </div>

//         {/* Content */}
//         <form onSubmit={handleSubmit} className="p-6 space-y-6">
//           {/* Basic Information */}
//           <div className="space-y-4">
//             <h3 className="text-lg font-semibold text-gray-900">Basic Information</h3>
            
//             <div>
//               <label className="block text-sm font-medium text-gray-700 mb-2">
//                 Website Name *
//               </label>
//               <input
//                 type="text"
//                 name="name"
//                 value={formData.name}
//                 onChange={handleInputChange}
//                 placeholder="Enter website name"
//                 className="input-field w-full"
//                 required
//               />
//             </div>

//             <div>
//               <label className="block text-sm font-medium text-gray-700 mb-2">
//                 Description
//               </label>
//               <textarea
//                 name="description"
//                 value={formData.description}
//                 onChange={handleInputChange}
//                 placeholder="Brief description of your website"
//                 rows="3"
//                 className="input-field w-full resize-none"
//               />
//             </div>

//             {/* Website Logo */}
//             <div className="mt-6 p-4 bg-gray-50 rounded-lg border border-gray-200">
//               <label className="block text-sm font-medium text-gray-700 mb-3">
//                 Website Logo
//               </label>
//               <p className="text-xs text-gray-600 mb-3">
//                 Upload a logo to replace the website name on your public site. Recommended: square image (PNG, JPG, GIF, WebP, or SVG). Max 5MB.
//               </p>

//               {/* Logo Preview */}
//               {logoPreview && (
//                 <div className="mb-4">
//                   <div className="relative inline-block">
//                     <img
//                       src={logoPreview}
//                       alt="Logo preview"
//                       className="max-w-xs max-h-24 rounded-lg border border-gray-300"
//                     />
//                   </div>
//                 </div>
//               )}

//               {/* File Input */}
//               <div className="flex gap-2">
//                 <label className="flex-1">
//                   <input
//                     type="file"
//                     accept="image/*"
//                     onChange={handleLogoChange}
//                     disabled={uploadingLogo}
//                     className="hidden"
//                   />
//                   <span className="inline-flex items-center gap-2 px-4 py-2 bg-blue-600 text-white hover:bg-blue-700 disabled:bg-gray-400 rounded-lg font-medium transition-colors cursor-pointer w-full justify-center">
//                     <FiUpload className="w-4 h-4" />
//                     Choose File
//                   </span>
//                 </label>

//                 {logoFile && !uploadingLogo && (
//                   <button
//                     type="button"
//                     onClick={handleUploadLogo}
//                     disabled={uploadingLogo}
//                     className="px-4 py-2 bg-green-600 text-white hover:bg-green-700 disabled:bg-gray-400 rounded-lg font-medium transition-colors flex items-center gap-2"
//                   >
//                     Upload
//                   </button>
//                 )}

//                 {logoPreview && (
//                   <button
//                     type="button"
//                     onClick={handleDeleteLogo}
//                     disabled={uploadingLogo}
//                     className="px-4 py-2 bg-red-600 text-white hover:bg-red-700 disabled:bg-gray-400 rounded-lg font-medium transition-colors flex items-center gap-2"
//                   >
//                     <FiTrash2 className="w-4 h-4" />
//                     Remove
//                   </button>
//                 )}

//                 {uploadingLogo && (
//                   <button
//                     type="button"
//                     disabled
//                     className="px-4 py-2 bg-gray-400 text-white rounded-lg font-medium flex items-center gap-2"
//                   >
//                     <FiLoader className="w-4 h-4 animate-spin" />
//                     Uploading...
//                   </button>
//                 )}
//               </div>

//               {logoFile && (
//                 <p className="text-xs text-gray-600 mt-2">
//                   Selected: {logoFile.name}
//                 </p>
//               )}
//             </div>
//           </div>

//           {/* SEO Settings */}
//           <div className="space-y-4 pt-6 border-t border-gray-200">
//             <h3 className="text-lg font-semibold text-gray-900">SEO Settings</h3>
            
//             <div>
//               <label className="block text-sm font-medium text-gray-700 mb-2">
//                 Meta Title
//               </label>
//               <input
//                 type="text"
//                 name="metaTitle"
//                 value={formData.metaTitle}
//                 onChange={handleInputChange}
//                 placeholder="Enter SEO title (60 characters)"
//                 maxLength="60"
//                 className="input-field w-full"
//               />
//               <p className="text-xs text-gray-500 mt-1">{formData.metaTitle.length}/60</p>
//             </div>

//             <div>
//               <label className="block text-sm font-medium text-gray-700 mb-2">
//                 Meta Description
//               </label>
//               <textarea
//                 name="metaDescription"
//                 value={formData.metaDescription}
//                 onChange={handleInputChange}
//                 placeholder="Enter SEO description (160 characters)"
//                 maxLength="160"
//                 rows="2"
//                 className="input-field w-full resize-none"
//               />
//               <p className="text-xs text-gray-500 mt-1">{formData.metaDescription.length}/160</p>
//             </div>

//             <div>
//               <label className="block text-sm font-medium text-gray-700 mb-2">
//                 Keywords
//               </label>
//               <input
//                 type="text"
//                 name="metaKeywords"
//                 value={formData.metaKeywords}
//                 onChange={handleInputChange}
//                 placeholder="Comma-separated keywords"
//                 className="input-field w-full"
//               />
//             </div>
//           </div>

//           {/* Design Settings */}
//           <div className="space-y-4 pt-6 border-t border-gray-200">
//             <h3 className="text-lg font-semibold text-gray-900">Design Settings</h3>

//             {/* Colors */}
//             <div className="space-y-4">
//               <p className="text-sm text-gray-600">Colors</p>
//               <div className="grid grid-cols-3 gap-4">
//                 <div>
//                   <label className="block text-xs font-medium text-gray-700 mb-2">
//                     Primary Color
//                   </label>
//                   <div className="flex items-center gap-2">
//                     <input
//                       type="color"
//                       value={formData.primaryColor}
//                       onChange={(e) => handleColorChange('primaryColor', e.target.value)}
//                       className="w-12 h-10 rounded cursor-pointer border border-gray-300"
//                     />
//                     <input
//                       type="text"
//                       value={formData.primaryColor}
//                       onChange={(e) => handleColorChange('primaryColor', e.target.value)}
//                       className="input-field flex-1 font-mono text-sm"
//                       maxLength="7"
//                     />
//                   </div>
//                 </div>

//                 <div>
//                   <label className="block text-xs font-medium text-gray-700 mb-2">
//                     Secondary Color
//                   </label>
//                   <div className="flex items-center gap-2">
//                     <input
//                       type="color"
//                       value={formData.secondaryColor}
//                       onChange={(e) => handleColorChange('secondaryColor', e.target.value)}
//                       className="w-12 h-10 rounded cursor-pointer border border-gray-300"
//                     />
//                     <input
//                       type="text"
//                       value={formData.secondaryColor}
//                       onChange={(e) => handleColorChange('secondaryColor', e.target.value)}
//                       className="input-field flex-1 font-mono text-sm"
//                       maxLength="7"
//                     />
//                   </div>
//                 </div>

//                 <div>
//                   <label className="block text-xs font-medium text-gray-700 mb-2">
//                     Accent Color
//                   </label>
//                   <div className="flex items-center gap-2">
//                     <input
//                       type="color"
//                       value={formData.accentColor}
//                       onChange={(e) => handleColorChange('accentColor', e.target.value)}
//                       className="w-12 h-10 rounded cursor-pointer border border-gray-300"
//                     />
//                     <input
//                       type="text"
//                       value={formData.accentColor}
//                       onChange={(e) => handleColorChange('accentColor', e.target.value)}
//                       className="input-field flex-1 font-mono text-sm"
//                       maxLength="7"
//                     />
//                   </div>
//                 </div>
//               </div>
//             </div>

//             {/* Fonts */}
//             <div className="space-y-3">
//               <p className="text-sm text-gray-600">Fonts</p>
//               <div className="grid grid-cols-2 gap-4">
//                 <div>
//                   <label className="block text-xs font-medium text-gray-700 mb-2">
//                     Primary Font
//                   </label>
//                   <select
//                     name="primaryFont"
//                     value={formData.primaryFont}
//                     onChange={handleInputChange}
//                     className="input-field w-full"
//                   >
//                     <option value="Inter">Inter</option>
//                     <option value="Arial">Arial</option>
//                     <option value="Georgia">Georgia</option>
//                     <option value="Times New Roman">Times New Roman</option>
//                     <option value="Verdana">Verdana</option>
//                   </select>
//                 </div>
//                 <div>
//                   <label className="block text-xs font-medium text-gray-700 mb-2">
//                     Secondary Font
//                   </label>
//                   <select
//                     name="secondaryFont"
//                     value={formData.secondaryFont}
//                     onChange={handleInputChange}
//                     className="input-field w-full"
//                   >
//                     <option value="Inter">Inter</option>
//                     <option value="Arial">Arial</option>
//                     <option value="Georgia">Georgia</option>
//                     <option value="Times New Roman">Times New Roman</option>
//                     <option value="Verdana">Verdana</option>
//                   </select>
//                 </div>
//               </div>
//             </div>
//           </div>

//           {/* Action Buttons */}
//           <div className="flex gap-3 pt-6 border-t border-gray-200">
//             <button
//               type="button"
//               onClick={onClose}
//               disabled={isSaving}
//               className="flex-1 py-2 px-4 bg-gray-100 text-gray-700 hover:bg-gray-200 rounded-lg font-medium transition-colors disabled:opacity-50"
//             >
//               Cancel
//             </button>
//             <button
//               type="submit"
//               disabled={isSaving}
//               className="flex-1 py-2 px-4 bg-primary-600 text-white hover:bg-primary-700 disabled:bg-gray-400 rounded-lg font-medium transition-colors flex items-center justify-center gap-2 disabled:cursor-not-allowed"
//             >
//               {isSaving ? (
//                 <>
//                   <FiLoader className="w-4 h-4 animate-spin" /> Saving...
//                 </>
//               ) : (
//                 <>
//                   <FiSave className="w-4 h-4" /> Save Website
//                 </>
//               )}
//             </button>
//           </div>
//         </form>
//       </div>
//     </div>
//   )
// }












// import { useState, useEffect } from 'react'
// import { FiX, FiLoader, FiSave, FiTrash2, FiUpload } from 'react-icons/fi'
// import toast from 'react-hot-toast'
// import axios from 'axios'

// export default function WebsiteEditorModal({ isOpen, onClose, onSubmit, website, categories, isSaving, mode }) {
//   const [formData, setFormData] = useState({
//     name: '',
//     description: '',
//     metaTitle: '',
//     metaDescription: '',
//     metaKeywords: '',
//     primaryColor: '#3B82F6',
//     secondaryColor: '#1F2937',
//     accentColor: '#F97316',
//     primaryFont: 'Inter',
//     secondaryFont: 'Inter'
//   })
//   const [logoFile, setLogoFile] = useState(null)
//   const [logoPreview, setLogoPreview] = useState(null)
//   const [uploadingLogo, setUploadingLogo] = useState(false)

//   useEffect(() => {
//     if (website && isOpen) {
//       setFormData({
//         name: website.name || '',
//         description: website.description || '',
//         metaTitle: website.seo?.metaTitle || '',
//         metaDescription: website.seo?.metaDescription || '',
//         metaKeywords: website.seo?.metaKeywords || '',
//         primaryColor: website.colors?.primary || '#3B82F6',
//         secondaryColor: website.colors?.secondary || '#1F2937',
//         accentColor: website.colors?.accent || '#F97316',
//         primaryFont: website.fonts?.primary || 'Inter',
//         secondaryFont: website.fonts?.secondary || 'Inter'
//       })
      
//       // Show the saved/uploaded logo from backend if it exists
//       if (website.logo?.url && !website.logo.url.startsWith('data:') && !website.logo.url.startsWith('blob:')) {
//         const backendOrigin = axios.defaults.baseURL ? axios.defaults.baseURL.replace(/\/api\/?$/, '') : ''
//         const storedUrl = website.logo.url
//         let previewUrl = storedUrl
        
//         if (!storedUrl.startsWith('http'))a {
//           const pathUrl = storedUrl.startsWith('/') ? storedUrl : `/${storedUrl}`
//           previewUrl = backendOrigin ? `${backendOrigin}${pathUrl}` : pathUrl
//         }
        
//         setLogoPreview(previewUrl)
//         console.log('[WebsiteEditor] Modal opened with saved/uploaded logo:', previewUrl)
//       } else {
//         setLogoPreview(null)
//       }
      
//       setLogoFile(null)
//     }
//   }, [website, isOpen])

//   const handleInputChange = (e) => {
//     const { name, value } = e.target
//     setFormData(prev => ({
//       ...prev,
//       [name]: value
//     }))
//   }

//   const handleColorChange = (colorField, value) => {
//     setFormData(prev => ({
//       ...prev,
//       [colorField]: value
//     }))
//   }

//   const handleLogoChange = (e) => {
//     const file = e.target.files?.[0]
//     if (!file) return

//     if (!file.type.startsWith('image/')) {
//       toast.error('Please select an image file')
//       return
//     }

//     if (file.size > 5 * 1024 * 1024) {
//       toast.error('File size must be less than 5MB')
//       return
//     }

//     setLogoFile(file)
//     const reader = new FileReader()
//     reader.onload = (evt) => {
//       setLogoPreview(evt.target?.result)
//     }
//     reader.readAsDataURL(file)
//   }

//   const handleUploadLogo = async () => {
//     if (!logoFile || !website?._id) {
//       toast.error('No logo file selected')
//       return
//     }

//     try {
//       setUploadingLogo(true)
//       const formDataUpload = new FormData()
//       formDataUpload.append('logo', logoFile)

//       const response = await axios.post(`/websites/${website._id}/logo`, formDataUpload, {
//         headers: {
//           'Content-Type': 'multipart/form-data'
//         }
//       })

//       toast.success('✅ Logo uploaded and saved successfully')
      
//       // Get the uploaded logo URL from server response and show it in preview
//       const returnedWebsite = response.data?.website
//       const returnedLogoUrl = returnedWebsite?.logo?.url
//       console.log('[WebsiteEditor] Upload response website:', returnedWebsite)
//       console.log('[WebsiteEditor] Returned logo URL:', returnedLogoUrl)
      
//       if (returnedLogoUrl) {
//         // Normalize the URL to absolute so it displays properly
//         const backendOrigin = axios.defaults.baseURL ? axios.defaults.baseURL.replace(/\/api\/?$/, '') : ''
//         let absoluteLogoUrl = returnedLogoUrl
        
//         // Only convert relative paths to absolute; keep http(s) URLs as-is
//         if (!returnedLogoUrl.startsWith('http') && !returnedLogoUrl.startsWith('data:') && !returnedLogoUrl.startsWith('blob:')) {
//           const pathUrl = returnedLogoUrl.startsWith('/') ? returnedLogoUrl : `/${returnedLogoUrl}`
//           absoluteLogoUrl = backendOrigin ? `${backendOrigin}${pathUrl}` : pathUrl
//         }
        
//         console.log('[WebsiteEditor] Setting preview to:', absoluteLogoUrl)
//         setLogoPreview(absoluteLogoUrl) // Show the uploaded logo in preview
//       }
      
//       setLogoFile(null) // Clear file input after successful upload
      
//       // Logo is already saved on server — don't trigger onSubmit yet
//       // User should click "Save Website" to persist other form changes if needed
//     } catch (error) {
//       console.error('[Logo Upload] Error:', error)
//       toast.error(error.response?.data?.message || 'Failed to upload logo')
//     } finally {
//       setUploadingLogo(false)
//     }
//   }

//   const handleDeleteLogo = async () => {
//     if (!website?._id) return

//     if (!window.confirm('Are you sure you want to delete the logo?')) return

//     try {
//       setUploadingLogo(true)
//       await axios.delete(`/websites/${website._id}/logo`)
//       toast.success('Logo deleted successfully')
//       setLogoPreview(null)
//       setLogoFile(null)
//       // Logo is already deleted on server — don't trigger onSubmit yet
//       // User should click "Save Website" to persist other form changes if needed
//     } catch (error) {
//       console.error('[Logo Delete] Error:', error)
//       toast.error(error.response?.data?.message || 'Failed to delete logo')
//     } finally {
//       setUploadingLogo(false)
//     }
//   }

//   const handleSubmit = (e) => {
//     e.preventDefault()
    
//     if (!formData.name.trim()) {
//       toast.error('Website name is required')
//       return
//     }

//     if (!website || !website._id) {
//       toast.error('Invalid website - please select a website to edit')
//       return
//     }

//     const submitData = {
//       name: formData.name.trim(),
//       description: formData.description,
//       seo: {
//         metaTitle: formData.metaTitle,
//         metaDescription: formData.metaDescription,
//         metaKeywords: formData.metaKeywords
//       },
//       colors: {
//         primary: formData.primaryColor,
//         secondary: formData.secondaryColor,
//         accent: formData.accentColor
//       },
//       fonts: {
//         primary: formData.primaryFont,
//         secondary: formData.secondaryFont
//       }
//     }

//     onSubmit(submitData)
//   }

//   if (!isOpen) return null

//   return (
//     <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
//       <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
//         {/* Header */}
//         <div className="flex justify-between items-center p-6 border-b border-gray-200 sticky top-0 bg-white">
//           <h2 className="text-2xl font-bold text-gray-900">Edit Website</h2>
//           <button
//             onClick={onClose}
//             disabled={isSaving}
//             className="p-1 hover:bg-gray-100 rounded-lg transition-colors disabled:opacity-50"
//           >
//             <FiX className="w-6 h-6" />
//           </button>
//         </div>

//         {/* Content */}
//         <form onSubmit={handleSubmit} className="p-6 space-y-6">
//           {/* Basic Information */}
//           <div className="space-y-4">
//             <h3 className="text-lg font-semibold text-gray-900">Basic Information</h3>
            
//             <div>
//               <label className="block text-sm font-medium text-gray-700 mb-2">
//                 Website Name *
//               </label>
//               <input
//                 type="text"
//                 name="name"
//                 value={formData.name}
//                 onChange={handleInputChange}
//                 placeholder="Enter website name"
//                 className="input-field w-full"
//                 required
//               />
//             </div>

//             <div>
//               <label className="block text-sm font-medium text-gray-700 mb-2">
//                 Description
//               </label>
//               <textarea
//                 name="description"
//                 value={formData.description}
//                 onChange={handleInputChange}
//                 placeholder="Brief description of your website"
//                 rows="3"
//                 className="input-field w-full resize-none"
//               />
//             </div>

//             {/* Website Logo */}
//             <div className="mt-6 p-4 bg-gray-50 rounded-lg border border-gray-200">
//               <label className="block text-sm font-medium text-gray-700 mb-3">
//                 Website Logo
//               </label>
//               <p className="text-xs text-gray-600 mb-3">
//                 Upload a logo to replace the website name on your public site. Recommended: square image (PNG, JPG, GIF, WebP, or SVG). Max 5MB.
//               </p>

//               {/* Logo Preview */}
//               {logoPreview && (
//                 <div className="mb-4">
//                   <div className="relative inline-block">
//                     <img
//                       src={logoPreview}
//                       alt="Logo preview"
//                       className="max-w-xs max-h-24 rounded-lg border border-gray-300"
//                     />
//                   </div>
//                 </div>
//               )}

//               {/* File Input */}
//               <div className="flex gap-2">
//                 <label className="flex-1">
//                   <input
//                     type="file"
//                     accept="image/*"
//                     onChange={handleLogoChange}
//                     disabled={uploadingLogo}
//                     className="hidden"
//                   />
//                   <span className="inline-flex items-center gap-2 px-4 py-2 bg-blue-600 text-white hover:bg-blue-700 disabled:bg-gray-400 rounded-lg font-medium transition-colors cursor-pointer w-full justify-center">
//                     <FiUpload className="w-4 h-4" />
//                     Choose File
//                   </span>
//                 </label>

//                 {logoFile && !uploadingLogo && (
//                   <button
//                     type="button"
//                     onClick={handleUploadLogo}
//                     disabled={uploadingLogo}
//                     className="px-4 py-2 bg-green-600 text-white hover:bg-green-700 disabled:bg-gray-400 rounded-lg font-medium transition-colors flex items-center gap-2"
//                   >
//                     Upload
//                   </button>
//                 )}

//                 {logoPreview && (
//                   <button
//                     type="button"
//                     onClick={handleDeleteLogo}
//                     disabled={uploadingLogo}
//                     className="px-4 py-2 bg-red-600 text-white hover:bg-red-700 disabled:bg-gray-400 rounded-lg font-medium transition-colors flex items-center gap-2"
//                   >
//                     <FiTrash2 className="w-4 h-4" />
//                     Remove
//                   </button>
//                 )}

//                 {uploadingLogo && (
//                   <button
//                     type="button"
//                     disabled
//                     className="px-4 py-2 bg-gray-400 text-white rounded-lg font-medium flex items-center gap-2"
//                   >
//                     <FiLoader className="w-4 h-4 animate-spin" />
//                     Uploading...
//                   </button>
//                 )}
//               </div>

//               {logoFile && (
//                 <p className="text-xs text-gray-600 mt-2">
//                   Selected: {logoFile.name}
//                 </p>
//               )}
//             </div>
//           </div>

//           {/* SEO Settings */}
//           <div className="space-y-4 pt-6 border-t border-gray-200">
//             <h3 className="text-lg font-semibold text-gray-900">SEO Settings</h3>
            
//             <div>
//               <label className="block text-sm font-medium text-gray-700 mb-2">
//                 Meta Title
//               </label>
//               <input
//                 type="text"
//                 name="metaTitle"
//                 value={formData.metaTitle}
//                 onChange={handleInputChange}
//                 placeholder="Enter SEO title (60 characters)"
//                 maxLength="60"
//                 className="input-field w-full"
//               />
//               <p className="text-xs text-gray-500 mt-1">{formData.metaTitle.length}/60</p>
//             </div>

//             <div>
//               <label className="block text-sm font-medium text-gray-700 mb-2">
//                 Meta Description
//               </label>
//               <textarea
//                 name="metaDescription"
//                 value={formData.metaDescription}
//                 onChange={handleInputChange}
//                 placeholder="Enter SEO description (160 characters)"
//                 maxLength="160"
//                 rows="2"
//                 className="input-field w-full resize-none"
//               />
//               <p className="text-xs text-gray-500 mt-1">{formData.metaDescription.length}/160</p>
//             </div>

//             <div>
//               <label className="block text-sm font-medium text-gray-700 mb-2">
//                 Keywords
//               </label>
//               <input
//                 type="text"
//                 name="metaKeywords"
//                 value={formData.metaKeywords}
//                 onChange={handleInputChange}
//                 placeholder="Comma-separated keywords"
//                 className="input-field w-full"
//               />
//             </div>
//           </div>

//           {/* Design Settings */}
//           <div className="space-y-4 pt-6 border-t border-gray-200">
//             <h3 className="text-lg font-semibold text-gray-900">Design Settings</h3>

//             {/* Colors */}
//             <div className="space-y-4">
//               <p className="text-sm text-gray-600">Colors</p>
//               <div className="grid grid-cols-3 gap-4">
//                 <div>
//                   <label className="block text-xs font-medium text-gray-700 mb-2">
//                     Primary Color
//                   </label>
//                   <div className="flex items-center gap-2">
//                     <input
//                       type="color"
//                       value={formData.primaryColor}
//                       onChange={(e) => handleColorChange('primaryColor', e.target.value)}
//                       className="w-12 h-10 rounded cursor-pointer border border-gray-300"
//                     />
//                     <input
//                       type="text"
//                       value={formData.primaryColor}
//                       onChange={(e) => handleColorChange('primaryColor', e.target.value)}
//                       className="input-field flex-1 font-mono text-sm"
//                       maxLength="7"
//                     />
//                   </div>
//                 </div>

//                 <div>
//                   <label className="block text-xs font-medium text-gray-700 mb-2">
//                     Secondary Color
//                   </label>
//                   <div className="flex items-center gap-2">
//                     <input
//                       type="color"
//                       value={formData.secondaryColor}
//                       onChange={(e) => handleColorChange('secondaryColor', e.target.value)}
//                       className="w-12 h-10 rounded cursor-pointer border border-gray-300"
//                     />
//                     <input
//                       type="text"
//                       value={formData.secondaryColor}
//                       onChange={(e) => handleColorChange('secondaryColor', e.target.value)}
//                       className="input-field flex-1 font-mono text-sm"
//                       maxLength="7"
//                     />
//                   </div>
//                 </div>

//                 <div>
//                   <label className="block text-xs font-medium text-gray-700 mb-2">
//                     Accent Color
//                   </label>
//                   <div className="flex items-center gap-2">
//                     <input
//                       type="color"
//                       value={formData.accentColor}
//                       onChange={(e) => handleColorChange('accentColor', e.target.value)}
//                       className="w-12 h-10 rounded cursor-pointer border border-gray-300"
//                     />
//                     <input
//                       type="text"
//                       value={formData.accentColor}
//                       onChange={(e) => handleColorChange('accentColor', e.target.value)}
//                       className="input-field flex-1 font-mono text-sm"
//                       maxLength="7"
//                     />
//                   </div>
//                 </div>
//               </div>
//             </div>

//             {/* Fonts */}
//             <div className="space-y-3">
//               <p className="text-sm text-gray-600">Fonts</p>
//               <div className="grid grid-cols-2 gap-4">
//                 <div>
//                   <label className="block text-xs font-medium text-gray-700 mb-2">
//                     Primary Font
//                   </label>
//                   <select
//                     name="primaryFont"
//                     value={formData.primaryFont}
//                     onChange={handleInputChange}
//                     className="input-field w-full"
//                   >
//                     <option value="Inter">Inter</option>
//                     <option value="Arial">Arial</option>
//                     <option value="Georgia">Georgia</option>
//                     <option value="Times New Roman">Times New Roman</option>
//                     <option value="Verdana">Verdana</option>
//                   </select>
//                 </div>
//                 <div>
//                   <label className="block text-xs font-medium text-gray-700 mb-2">
//                     Secondary Font
//                   </label>
//                   <select
//                     name="secondaryFont"
//                     value={formData.secondaryFont}
//                     onChange={handleInputChange}
//                     className="input-field w-full"
//                   >
//                     <option value="Inter">Inter</option>
//                     <option value="Arial">Arial</option>
//                     <option value="Georgia">Georgia</option>
//                     <option value="Times New Roman">Times New Roman</option>
//                     <option value="Verdana">Verdana</option>
//                   </select>
//                 </div>
//               </div>
//             </div>
//           </div>

//           {/* Action Buttons */}
//           <div className="flex gap-3 pt-6 border-t border-gray-200">
//             <button
//               type="button"
//               onClick={onClose}
//               disabled={isSaving}
//               className="flex-1 py-2 px-4 bg-gray-100 text-gray-700 hover:bg-gray-200 rounded-lg font-medium transition-colors disabled:opacity-50"
//             >
//               Cancel
//             </button>
//             <button
//               type="submit"
//               disabled={isSaving}
//               className="flex-1 py-2 px-4 bg-primary-600 text-white hover:bg-primary-700 disabled:bg-gray-400 rounded-lg font-medium transition-colors flex items-center justify-center gap-2 disabled:cursor-not-allowed"
//             >
//               {isSaving ? (
//                 <>
//                   <FiLoader className="w-4 h-4 animate-spin" /> Saving...
//                 </>
//               ) : (
//                 <>
//                   <FiSave className="w-4 h-4" /> Save Website
//                 </>
//               )}
//             </button>
//           </div>
//         </form>
//       </div>
//     </div>
//   )
// }














// import { useState, useEffect } from 'react'
// import { FiX, FiLoader, FiSave, FiTrash2, FiUpload } from 'react-icons/fi'
// import toast from 'react-hot-toast'
// import axios from 'axios'

// export default function WebsiteEditorModal({ isOpen, onClose, onSubmit, website, categories, isSaving, mode }) {
//   const [formData, setFormData] = useState({
//     name: '',
//     description: '',
//     metaTitle: '',
//     metaDescription: '',
//     metaKeywords: '',
//     primaryColor: '#3B82F6',
//     secondaryColor: '#1F2937',
//     accentColor: '#F97316',
//     primaryFont: 'Inter',
//     secondaryFont: 'Inter'
//   })
//   const [logoFile, setLogoFile] = useState(null)
//   const [logoPreview, setLogoPreview] = useState(null)
//   const [uploadingLogo, setUploadingLogo] = useState(false)

//   useEffect(() => {
//     if (website && isOpen) {
//       setFormData({
//         name: website.name || '',
//         description: website.description || '',
//         metaTitle: website.seo?.metaTitle || '',
//         metaDescription: website.seo?.metaDescription || '',
//         metaKeywords: website.seo?.metaKeywords || '',
//         primaryColor: website.colors?.primary || '#3B82F6',
//         secondaryColor: website.colors?.secondary || '#1F2937',
//         accentColor: website.colors?.accent || '#F97316',
//         primaryFont: website.fonts?.primary || 'Inter',
//         secondaryFont: website.fonts?.secondary || 'Inter'
//       })
//       // Don't show any saved logo preview in the modal
//       // Only local file preview (before upload) will be shown
//       setLogoPreview(null)
//       setLogoFile(null)
//     }
//   }, [website, isOpen])

//   const handleInputChange = (e) => {
//     const { name, value } = e.target
//     setFormData(prev => ({
//       ...prev,
//       [name]: value
//     }))
//   }

//   const handleColorChange = (colorField, value) => {
//     setFormData(prev => ({
//       ...prev,
//       [colorField]: value
//     }))
//   }

//   const handleLogoChange = (e) => {
//     const file = e.target.files?.[0]
//     if (!file) return

//     if (!file.type.startsWith('image/')) {
//       toast.error('Please select an image file')
//       return
//     }

//     if (file.size > 5 * 1024 * 1024) {
//       toast.error('File size must be less than 5MB')
//       return
//     }

//     setLogoFile(file)
//     const reader = new FileReader()
//     reader.onload = (evt) => {
//       setLogoPreview(evt.target?.result)
//     }
//     reader.readAsDataURL(file)
//   }

//   const handleUploadLogo = async () => {
//     if (!logoFile || !website?._id) {
//       toast.error('No logo file selected')
//       return
//     }

//     try {
//       setUploadingLogo(true)
//       const formDataUpload = new FormData()
//       formDataUpload.append('logo', logoFile)

//       const response = await axios.post(`/websites/${website._id}/logo`, formDataUpload, {
//         headers: {
//           'Content-Type': 'multipart/form-data'
//         }
//       })

//       toast.success('✅ Logo uploaded and saved successfully')
      
//       // Clear preview and file immediately — logo is now on server
//       // Don't show preview in modal; it will display correctly on public site
//       setLogoFile(null)
//       setLogoPreview(null)
      
//       // Logo is already saved on server — don't trigger onSubmit yet
//       // User should click "Save Website" to persist other form changes if needed
//     } catch (error) {
//       console.error('[Logo Upload] Error:', error)
//       toast.error(error.response?.data?.message || 'Failed to upload logo')
//     } finally {
//       setUploadingLogo(false)
//     }
//   }

//   const handleDeleteLogo = async () => {
//     if (!website?._id) return

//     if (!window.confirm('Are you sure you want to delete the logo?')) return

//     try {
//       setUploadingLogo(true)
//       await axios.delete(`/websites/${website._id}/logo`)
//       toast.success('Logo deleted successfully')
//       setLogoPreview(null)
//       setLogoFile(null)
//       // Logo is already deleted on server — don't trigger onSubmit yet
//       // User should click "Save Website" to persist other form changes if needed
//     } catch (error) {
//       console.error('[Logo Delete] Error:', error)
//       toast.error(error.response?.data?.message || 'Failed to delete logo')
//     } finally {
//       setUploadingLogo(false)
//     }
//   }

//   const handleSubmit = (e) => {
//     e.preventDefault()
    
//     if (!formData.name.trim()) {
//       toast.error('Website name is required')
//       return
//     }

//     if (!website || !website._id) {
//       toast.error('Invalid website - please select a website to edit')
//       return
//     }

//     const submitData = {
//       name: formData.name.trim(),
//       description: formData.description,
//       seo: {
//         metaTitle: formData.metaTitle,
//         metaDescription: formData.metaDescription,
//         metaKeywords: formData.metaKeywords
//       },
//       colors: {
//         primary: formData.primaryColor,
//         secondary: formData.secondaryColor,
//         accent: formData.accentColor
//       },
//       fonts: {
//         primary: formData.primaryFont,
//         secondary: formData.secondaryFont
//       }
//     }

//     onSubmit(submitData)
//   }

//   if (!isOpen) return null

//   return (
//     <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
//       <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
//         {/* Header */}
//         <div className="flex justify-between items-center p-6 border-b border-gray-200 sticky top-0 bg-white">
//           <h2 className="text-2xl font-bold text-gray-900">Edit Website</h2>
//           <button
//             onClick={onClose}
//             disabled={isSaving}
//             className="p-1 hover:bg-gray-100 rounded-lg transition-colors disabled:opacity-50"
//           >
//             <FiX className="w-6 h-6" />
//           </button>
//         </div>

//         {/* Content */}
//         <form onSubmit={handleSubmit} className="p-6 space-y-6">
//           {/* Basic Information */}
//           <div className="space-y-4">
//             <h3 className="text-lg font-semibold text-gray-900">Basic Information</h3>
            
//             <div>
//               <label className="block text-sm font-medium text-gray-700 mb-2">
//                 Website Name *
//               </label>
//               <input
//                 type="text"
//                 name="name"
//                 value={formData.name}
//                 onChange={handleInputChange}
//                 placeholder="Enter website name"
//                 className="input-field w-full"
//                 required
//               />
//             </div>

//             <div>
//               <label className="block text-sm font-medium text-gray-700 mb-2">
//                 Description
//               </label>
//               <textarea
//                 name="description"
//                 value={formData.description}
//                 onChange={handleInputChange}
//                 placeholder="Brief description of your website"
//                 rows="3"
//                 className="input-field w-full resize-none"
//               />
//             </div>

//             {/* Website Logo */}
//             <div className="mt-6 p-4 bg-gray-50 rounded-lg border border-gray-200">
//               <label className="block text-sm font-medium text-gray-700 mb-3">
//                 Website Logo
//               </label>
//               <p className="text-xs text-gray-600 mb-3">
//                 Upload a logo to replace the website name on your public site. Recommended: square image (PNG, JPG, GIF, WebP, or SVG). Max 5MB.
//               </p>

//               {/* Logo Preview */}
//               {logoPreview && (
//                 <div className="mb-4">
//                   <div className="relative inline-block">
//                     <img
//                       src={logoPreview}
//                       alt="Logo preview"
//                       className="max-w-xs max-h-24 rounded-lg border border-gray-300"
//                     />
//                   </div>
//                 </div>
//               )}

//               {/* File Input */}
//               <div className="flex gap-2">
//                 <label className="flex-1">
//                   <input
//                     type="file"
//                     accept="image/*"
//                     onChange={handleLogoChange}
//                     disabled={uploadingLogo}
//                     className="hidden"
//                   />
//                   <span className="inline-flex items-center gap-2 px-4 py-2 bg-blue-600 text-white hover:bg-blue-700 disabled:bg-gray-400 rounded-lg font-medium transition-colors cursor-pointer w-full justify-center">
//                     <FiUpload className="w-4 h-4" />
//                     Choose File
//                   </span>
//                 </label>

//                 {logoFile && !uploadingLogo && (
//                   <button
//                     type="button"
//                     onClick={handleUploadLogo}
//                     disabled={uploadingLogo}
//                     className="px-4 py-2 bg-green-600 text-white hover:bg-green-700 disabled:bg-gray-400 rounded-lg font-medium transition-colors flex items-center gap-2"
//                   >
//                     Upload
//                   </button>
//                 )}

//                 {logoPreview && (
//                   <button
//                     type="button"
//                     onClick={handleDeleteLogo}
//                     disabled={uploadingLogo}
//                     className="px-4 py-2 bg-red-600 text-white hover:bg-red-700 disabled:bg-gray-400 rounded-lg font-medium transition-colors flex items-center gap-2"
//                   >
//                     <FiTrash2 className="w-4 h-4" />
//                     Remove
//                   </button>
//                 )}

//                 {uploadingLogo && (
//                   <button
//                     type="button"
//                     disabled
//                     className="px-4 py-2 bg-gray-400 text-white rounded-lg font-medium flex items-center gap-2"
//                   >
//                     <FiLoader className="w-4 h-4 animate-spin" />
//                     Uploading...
//                   </button>
//                 )}
//               </div>

//               {logoFile && (
//                 <p className="text-xs text-gray-600 mt-2">
//                   Selected: {logoFile.name}
//                 </p>
//               )}
//             </div>
//           </div>

//           {/* SEO Settings */}
//           <div className="space-y-4 pt-6 border-t border-gray-200">
//             <h3 className="text-lg font-semibold text-gray-900">SEO Settings</h3>
            
//             <div>
//               <label className="block text-sm font-medium text-gray-700 mb-2">
//                 Meta Title
//               </label>
//               <input
//                 type="text"
//                 name="metaTitle"
//                 value={formData.metaTitle}
//                 onChange={handleInputChange}
//                 placeholder="Enter SEO title (60 characters)"
//                 maxLength="60"
//                 className="input-field w-full"
//               />
//               <p className="text-xs text-gray-500 mt-1">{formData.metaTitle.length}/60</p>
//             </div>

//             <div>
//               <label className="block text-sm font-medium text-gray-700 mb-2">
//                 Meta Description
//               </label>
//               <textarea
//                 name="metaDescription"
//                 value={formData.metaDescription}
//                 onChange={handleInputChange}
//                 placeholder="Enter SEO description (160 characters)"
//                 maxLength="160"
//                 rows="2"
//                 className="input-field w-full resize-none"
//               />
//               <p className="text-xs text-gray-500 mt-1">{formData.metaDescription.length}/160</p>
//             </div>

//             <div>
//               <label className="block text-sm font-medium text-gray-700 mb-2">
//                 Keywords
//               </label>
//               <input
//                 type="text"
//                 name="metaKeywords"
//                 value={formData.metaKeywords}
//                 onChange={handleInputChange}
//                 placeholder="Comma-separated keywords"
//                 className="input-field w-full"
//               />
//             </div>
//           </div>

//           {/* Design Settings */}
//           <div className="space-y-4 pt-6 border-t border-gray-200">
//             <h3 className="text-lg font-semibold text-gray-900">Design Settings</h3>

//             {/* Colors */}
//             <div className="space-y-4">
//               <p className="text-sm text-gray-600">Colors</p>
//               <div className="grid grid-cols-3 gap-4">
//                 <div>
//                   <label className="block text-xs font-medium text-gray-700 mb-2">
//                     Primary Color
//                   </label>
//                   <div className="flex items-center gap-2">
//                     <input
//                       type="color"
//                       value={formData.primaryColor}
//                       onChange={(e) => handleColorChange('primaryColor', e.target.value)}
//                       className="w-12 h-10 rounded cursor-pointer border border-gray-300"
//                     />
//                     <input
//                       type="text"
//                       value={formData.primaryColor}
//                       onChange={(e) => handleColorChange('primaryColor', e.target.value)}
//                       className="input-field flex-1 font-mono text-sm"
//                       maxLength="7"
//                     />
//                   </div>
//                 </div>

//                 <div>
//                   <label className="block text-xs font-medium text-gray-700 mb-2">
//                     Secondary Color
//                   </label>
//                   <div className="flex items-center gap-2">
//                     <input
//                       type="color"
//                       value={formData.secondaryColor}
//                       onChange={(e) => handleColorChange('secondaryColor', e.target.value)}
//                       className="w-12 h-10 rounded cursor-pointer border border-gray-300"
//                     />
//                     <input
//                       type="text"
//                       value={formData.secondaryColor}
//                       onChange={(e) => handleColorChange('secondaryColor', e.target.value)}
//                       className="input-field flex-1 font-mono text-sm"
//                       maxLength="7"
//                     />
//                   </div>
//                 </div>

//                 <div>
//                   <label className="block text-xs font-medium text-gray-700 mb-2">
//                     Accent Color
//                   </label>
//                   <div className="flex items-center gap-2">
//                     <input
//                       type="color"
//                       value={formData.accentColor}
//                       onChange={(e) => handleColorChange('accentColor', e.target.value)}
//                       className="w-12 h-10 rounded cursor-pointer border border-gray-300"
//                     />
//                     <input
//                       type="text"
//                       value={formData.accentColor}
//                       onChange={(e) => handleColorChange('accentColor', e.target.value)}
//                       className="input-field flex-1 font-mono text-sm"
//                       maxLength="7"
//                     />
//                   </div>
//                 </div>
//               </div>
//             </div>

//             {/* Fonts */}
//             <div className="space-y-3">
//               <p className="text-sm text-gray-600">Fonts</p>
//               <div className="grid grid-cols-2 gap-4">
//                 <div>
//                   <label className="block text-xs font-medium text-gray-700 mb-2">
//                     Primary Font
//                   </label>
//                   <select
//                     name="primaryFont"
//                     value={formData.primaryFont}
//                     onChange={handleInputChange}
//                     className="input-field w-full"
//                   >
//                     <option value="Inter">Inter</option>
//                     <option value="Arial">Arial</option>
//                     <option value="Georgia">Georgia</option>
//                     <option value="Times New Roman">Times New Roman</option>
//                     <option value="Verdana">Verdana</option>
//                   </select>
//                 </div>
//                 <div>
//                   <label className="block text-xs font-medium text-gray-700 mb-2">
//                     Secondary Font
//                   </label>
//                   <select
//                     name="secondaryFont"
//                     value={formData.secondaryFont}
//                     onChange={handleInputChange}
//                     className="input-field w-full"
//                   >
//                     <option value="Inter">Inter</option>
//                     <option value="Arial">Arial</option>
//                     <option value="Georgia">Georgia</option>
//                     <option value="Times New Roman">Times New Roman</option>
//                     <option value="Verdana">Verdana</option>
//                   </select>
//                 </div>
//               </div>
//             </div>
//           </div>

//           {/* Action Buttons */}
//           <div className="flex gap-3 pt-6 border-t border-gray-200">
//             <button
//               type="button"
//               onClick={onClose}
//               disabled={isSaving}
//               className="flex-1 py-2 px-4 bg-gray-100 text-gray-700 hover:bg-gray-200 rounded-lg font-medium transition-colors disabled:opacity-50"
//             >
//               Cancel
//             </button>
//             <button
//               type="submit"
//               disabled={isSaving}
//               className="flex-1 py-2 px-4 bg-primary-600 text-white hover:bg-primary-700 disabled:bg-gray-400 rounded-lg font-medium transition-colors flex items-center justify-center gap-2 disabled:cursor-not-allowed"
//             >
//               {isSaving ? (
//                 <>
//                   <FiLoader className="w-4 h-4 animate-spin" /> Saving...
//                 </>
//               ) : (
//                 <>
//                   <FiSave className="w-4 h-4" /> Save Website
//                 </>
//               )}
//             </button>
//           </div>
//         </form>
//       </div>
//     </div>
//   )
// }
















// import { useState, useEffect } from 'react'
// import { FiX, FiLoader, FiSave, FiTrash2, FiUpload } from 'react-icons/fi'
// import toast from 'react-hot-toast'
// import axios from 'axios'

// export default function WebsiteEditorModal({ isOpen, onClose, onSubmit, website, categories, isSaving, mode }) {
//   const [formData, setFormData] = useState({
//     name: '',
//     description: '',
//     metaTitle: '',
//     metaDescription: '',
//     metaKeywords: '',
//     primaryColor: '#3B82F6',
//     secondaryColor: '#1F2937',
//     accentColor: '#F97316',
//     primaryFont: 'Inter',
//     secondaryFont: 'Inter'
//   })
//   const [logoFile, setLogoFile] = useState(null)
//   const [logoPreview, setLogoPreview] = useState(null)
//   const [uploadingLogo, setUploadingLogo] = useState(false)

//   useEffect(() => {
//     if (website && isOpen) {
//       setFormData({
//         name: website.name || '',
//         description: website.description || '',
//         metaTitle: website.seo?.metaTitle || '',
//         metaDescription: website.seo?.metaDescription || '',
//         metaKeywords: website.seo?.metaKeywords || '',
//         primaryColor: website.colors?.primary || '#3B82F6',
//         secondaryColor: website.colors?.secondary || '#1F2937',
//         accentColor: website.colors?.accent || '#F97316',
//         primaryFont: website.fonts?.primary || 'Inter',
//         secondaryFont: website.fonts?.secondary || 'Inter'
//       })
//       // Don't show any saved logo preview in the modal
//       // Only local file preview (before upload) will be shown
//       setLogoPreview(null)
//       setLogoFile(null)
//     }
//   }, [website, isOpen])

//   const handleInputChange = (e) => {
//     const { name, value } = e.target
//     setFormData(prev => ({
//       ...prev,
//       [name]: value
//     }))
//   }

//   const handleColorChange = (colorField, value) => {
//     setFormData(prev => ({
//       ...prev,
//       [colorField]: value
//     }))
//   }

//   const handleLogoChange = (e) => {
//     const file = e.target.files?.[0]
//     if (!file) return

//     if (!file.type.startsWith('image/')) {
//       toast.error('Please select an image file')
//       return
//     }

//     if (file.size > 5 * 1024 * 1024) {
//       toast.error('File size must be less than 5MB')
//       return
//     }

//     setLogoFile(file)
//     const reader = new FileReader()
//     reader.onload = (evt) => {
//       setLogoPreview(evt.target?.result)
//     }
//     reader.readAsDataURL(file)
//   }

//   const handleUploadLogo = async () => {
//     if (!logoFile || !website?._id) {
//       toast.error('No logo file selected')
//       return
//     }

//     try {
//       setUploadingLogo(true)
//       const formDataUpload = new FormData()
//       formDataUpload.append('logo', logoFile)

//       const response = await axios.post(`/websites/${website._id}/logo`, formDataUpload, {
//         headers: {
//           'Content-Type': 'multipart/form-data'
//         }
//       })

//       toast.success('✅ Logo uploaded and saved successfully')
      
//       // Update preview to show the uploaded logo in the modal
//       const returnedWebsite = response.data?.website
//       const returnedUrl = returnedWebsite?.logo?.url
//       if (returnedUrl) {
//         const backendOrigin = axios.defaults.baseURL ? axios.defaults.baseURL.replace(/\/api\/?$/, '') : ''
//         const previewUrl = (/^https?:\/\//i.test(returnedUrl) || returnedUrl.startsWith('data:') || returnedUrl.startsWith('blob:'))
//           ? returnedUrl
//           : (backendOrigin ? `${backendOrigin}${returnedUrl.startsWith('/') ? returnedUrl : `/${returnedUrl}`}` : returnedUrl)
//         setLogoPreview(previewUrl)
//       }
      
//       setLogoFile(null) // Clear file input after successful upload
      
//       // Logo is already saved on server — don't trigger onSubmit yet
//       // User should click "Save Website" to persist other form changes if needed
//     } catch (error) {
//       console.error('[Logo Upload] Error:', error)
//       toast.error(error.response?.data?.message || 'Failed to upload logo')
//     } finally {
//       setUploadingLogo(false)
//     }
//   }

//   const handleDeleteLogo = async () => {
//     if (!website?._id) return

//     if (!window.confirm('Are you sure you want to delete the logo?')) return

//     try {
//       setUploadingLogo(true)
//       await axios.delete(`/websites/${website._id}/logo`)
//       toast.success('Logo deleted successfully')
//       setLogoPreview(null)
//       setLogoFile(null)
//       // Logo is already deleted on server — don't trigger onSubmit yet
//       // User should click "Save Website" to persist other form changes if needed
//     } catch (error) {
//       console.error('[Logo Delete] Error:', error)
//       toast.error(error.response?.data?.message || 'Failed to delete logo')
//     } finally {
//       setUploadingLogo(false)
//     }
//   }

//   const handleSubmit = (e) => {
//     e.preventDefault()
    
//     if (!formData.name.trim()) {
//       toast.error('Website name is required')
//       return
//     }

//     if (!website || !website._id) {
//       toast.error('Invalid website - please select a website to edit')
//       return
//     }

//     const submitData = {
//       name: formData.name.trim(),
//       description: formData.description,
//       seo: {
//         metaTitle: formData.metaTitle,
//         metaDescription: formData.metaDescription,
//         metaKeywords: formData.metaKeywords
//       },
//       colors: {
//         primary: formData.primaryColor,
//         secondary: formData.secondaryColor,
//         accent: formData.accentColor
//       },
//       fonts: {
//         primary: formData.primaryFont,
//         secondary: formData.secondaryFont
//       }
//     }

//     onSubmit(submitData)
//   }

//   if (!isOpen) return null

//   return (
//     <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
//       <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
//         {/* Header */}
//         <div className="flex justify-between items-center p-6 border-b border-gray-200 sticky top-0 bg-white">
//           <h2 className="text-2xl font-bold text-gray-900">Edit Website</h2>
//           <button
//             onClick={onClose}
//             disabled={isSaving}
//             className="p-1 hover:bg-gray-100 rounded-lg transition-colors disabled:opacity-50"
//           >
//             <FiX className="w-6 h-6" />
//           </button>
//         </div>

//         {/* Content */}
//         <form onSubmit={handleSubmit} className="p-6 space-y-6">
//           {/* Basic Information */}
//           <div className="space-y-4">
//             <h3 className="text-lg font-semibold text-gray-900">Basic Information</h3>
            
//             <div>
//               <label className="block text-sm font-medium text-gray-700 mb-2">
//                 Website Name *
//               </label>
//               <input
//                 type="text"
//                 name="name"
//                 value={formData.name}
//                 onChange={handleInputChange}
//                 placeholder="Enter website name"
//                 className="input-field w-full"
//                 required
//               />
//             </div>

//             <div>
//               <label className="block text-sm font-medium text-gray-700 mb-2">
//                 Description
//               </label>
//               <textarea
//                 name="description"
//                 value={formData.description}
//                 onChange={handleInputChange}
//                 placeholder="Brief description of your website"
//                 rows="3"
//                 className="input-field w-full resize-none"
//               />
//             </div>

//             {/* Website Logo */}
//             <div className="mt-6 p-4 bg-gray-50 rounded-lg border border-gray-200">
//               <label className="block text-sm font-medium text-gray-700 mb-3">
//                 Website Logo
//               </label>
//               <p className="text-xs text-gray-600 mb-3">
//                 Upload a logo to replace the website name on your public site. Recommended: square image (PNG, JPG, GIF, WebP, or SVG). Max 5MB.
//               </p>

//               {/* Logo Preview */}
//               {logoPreview && (
//                 <div className="mb-4">
//                   <div className="relative inline-block">
//                     <img
//                       src={logoPreview}
//                       alt="Logo preview"
//                       className="max-w-xs max-h-24 rounded-lg border border-gray-300"
//                     />
//                   </div>
//                 </div>
//               )}

//               {/* File Input */}
//               <div className="flex gap-2">
//                 <label className="flex-1">
//                   <input
//                     type="file"
//                     accept="image/*"
//                     onChange={handleLogoChange}
//                     disabled={uploadingLogo}
//                     className="hidden"
//                   />
//                   <span className="inline-flex items-center gap-2 px-4 py-2 bg-blue-600 text-white hover:bg-blue-700 disabled:bg-gray-400 rounded-lg font-medium transition-colors cursor-pointer w-full justify-center">
//                     <FiUpload className="w-4 h-4" />
//                     Choose File
//                   </span>
//                 </label>

//                 {logoFile && !uploadingLogo && (
//                   <button
//                     type="button"
//                     onClick={handleUploadLogo}
//                     disabled={uploadingLogo}
//                     className="px-4 py-2 bg-green-600 text-white hover:bg-green-700 disabled:bg-gray-400 rounded-lg font-medium transition-colors flex items-center gap-2"
//                   >
//                     Upload
//                   </button>
//                 )}

//                 {logoPreview && (
//                   <button
//                     type="button"
//                     onClick={handleDeleteLogo}
//                     disabled={uploadingLogo}
//                     className="px-4 py-2 bg-red-600 text-white hover:bg-red-700 disabled:bg-gray-400 rounded-lg font-medium transition-colors flex items-center gap-2"
//                   >
//                     <FiTrash2 className="w-4 h-4" />
//                     Remove
//                   </button>
//                 )}

//                 {uploadingLogo && (
//                   <button
//                     type="button"
//                     disabled
//                     className="px-4 py-2 bg-gray-400 text-white rounded-lg font-medium flex items-center gap-2"
//                   >
//                     <FiLoader className="w-4 h-4 animate-spin" />
//                     Uploading...
//                   </button>
//                 )}
//               </div>

//               {logoFile && (
//                 <p className="text-xs text-gray-600 mt-2">
//                   Selected: {logoFile.name}
//                 </p>
//               )}
//             </div>
//           </div>

//           {/* SEO Settings */}
//           <div className="space-y-4 pt-6 border-t border-gray-200">
//             <h3 className="text-lg font-semibold text-gray-900">SEO Settings</h3>
            
//             <div>
//               <label className="block text-sm font-medium text-gray-700 mb-2">
//                 Meta Title
//               </label>
//               <input
//                 type="text"
//                 name="metaTitle"
//                 value={formData.metaTitle}
//                 onChange={handleInputChange}
//                 placeholder="Enter SEO title (60 characters)"
//                 maxLength="60"
//                 className="input-field w-full"
//               />
//               <p className="text-xs text-gray-500 mt-1">{formData.metaTitle.length}/60</p>
//             </div>

//             <div>
//               <label className="block text-sm font-medium text-gray-700 mb-2">
//                 Meta Description
//               </label>
//               <textarea
//                 name="metaDescription"
//                 value={formData.metaDescription}
//                 onChange={handleInputChange}
//                 placeholder="Enter SEO description (160 characters)"
//                 maxLength="160"
//                 rows="2"
//                 className="input-field w-full resize-none"
//               />
//               <p className="text-xs text-gray-500 mt-1">{formData.metaDescription.length}/160</p>
//             </div>

//             <div>
//               <label className="block text-sm font-medium text-gray-700 mb-2">
//                 Keywords
//               </label>
//               <input
//                 type="text"
//                 name="metaKeywords"
//                 value={formData.metaKeywords}
//                 onChange={handleInputChange}
//                 placeholder="Comma-separated keywords"
//                 className="input-field w-full"
//               />
//             </div>
//           </div>

//           {/* Design Settings */}
//           <div className="space-y-4 pt-6 border-t border-gray-200">
//             <h3 className="text-lg font-semibold text-gray-900">Design Settings</h3>

//             {/* Colors */}
//             <div className="space-y-4">
//               <p className="text-sm text-gray-600">Colors</p>
//               <div className="grid grid-cols-3 gap-4">
//                 <div>
//                   <label className="block text-xs font-medium text-gray-700 mb-2">
//                     Primary Color
//                   </label>
//                   <div className="flex items-center gap-2">
//                     <input
//                       type="color"
//                       value={formData.primaryColor}
//                       onChange={(e) => handleColorChange('primaryColor', e.target.value)}
//                       className="w-12 h-10 rounded cursor-pointer border border-gray-300"
//                     />
//                     <input
//                       type="text"
//                       value={formData.primaryColor}
//                       onChange={(e) => handleColorChange('primaryColor', e.target.value)}
//                       className="input-field flex-1 font-mono text-sm"
//                       maxLength="7"
//                     />
//                   </div>
//                 </div>

//                 <div>
//                   <label className="block text-xs font-medium text-gray-700 mb-2">
//                     Secondary Color
//                   </label>
//                   <div className="flex items-center gap-2">
//                     <input
//                       type="color"
//                       value={formData.secondaryColor}
//                       onChange={(e) => handleColorChange('secondaryColor', e.target.value)}
//                       className="w-12 h-10 rounded cursor-pointer border border-gray-300"
//                     />
//                     <input
//                       type="text"
//                       value={formData.secondaryColor}
//                       onChange={(e) => handleColorChange('secondaryColor', e.target.value)}
//                       className="input-field flex-1 font-mono text-sm"
//                       maxLength="7"
//                     />
//                   </div>
//                 </div>

//                 <div>
//                   <label className="block text-xs font-medium text-gray-700 mb-2">
//                     Accent Color
//                   </label>
//                   <div className="flex items-center gap-2">
//                     <input
//                       type="color"
//                       value={formData.accentColor}
//                       onChange={(e) => handleColorChange('accentColor', e.target.value)}
//                       className="w-12 h-10 rounded cursor-pointer border border-gray-300"
//                     />
//                     <input
//                       type="text"
//                       value={formData.accentColor}
//                       onChange={(e) => handleColorChange('accentColor', e.target.value)}
//                       className="input-field flex-1 font-mono text-sm"
//                       maxLength="7"
//                     />
//                   </div>
//                 </div>
//               </div>
//             </div>

//             {/* Fonts */}
//             <div className="space-y-3">
//               <p className="text-sm text-gray-600">Fonts</p>
//               <div className="grid grid-cols-2 gap-4">
//                 <div>
//                   <label className="block text-xs font-medium text-gray-700 mb-2">
//                     Primary Font
//                   </label>
//                   <select
//                     name="primaryFont"
//                     value={formData.primaryFont}
//                     onChange={handleInputChange}
//                     className="input-field w-full"
//                   >
//                     <option value="Inter">Inter</option>
//                     <option value="Arial">Arial</option>
//                     <option value="Georgia">Georgia</option>
//                     <option value="Times New Roman">Times New Roman</option>
//                     <option value="Verdana">Verdana</option>
//                   </select>
//                 </div>
//                 <div>
//                   <label className="block text-xs font-medium text-gray-700 mb-2">
//                     Secondary Font
//                   </label>
//                   <select
//                     name="secondaryFont"
//                     value={formData.secondaryFont}
//                     onChange={handleInputChange}
//                     className="input-field w-full"
//                   >
//                     <option value="Inter">Inter</option>
//                     <option value="Arial">Arial</option>
//                     <option value="Georgia">Georgia</option>
//                     <option value="Times New Roman">Times New Roman</option>
//                     <option value="Verdana">Verdana</option>
//                   </select>
//                 </div>
//               </div>
//             </div>
//           </div>

//           {/* Action Buttons */}
//           <div className="flex gap-3 pt-6 border-t border-gray-200">
//             <button
//               type="button"
//               onClick={onClose}
//               disabled={isSaving}
//               className="flex-1 py-2 px-4 bg-gray-100 text-gray-700 hover:bg-gray-200 rounded-lg font-medium transition-colors disabled:opacity-50"
//             >
//               Cancel
//             </button>
//             <button
//               type="submit"
//               disabled={isSaving}
//               className="flex-1 py-2 px-4 bg-primary-600 text-white hover:bg-primary-700 disabled:bg-gray-400 rounded-lg font-medium transition-colors flex items-center justify-center gap-2 disabled:cursor-not-allowed"
//             >
//               {isSaving ? (
//                 <>
//                   <FiLoader className="w-4 h-4 animate-spin" /> Saving...
//                 </>
//               ) : (
//                 <>
//                   <FiSave className="w-4 h-4" /> Save Website
//                 </>
//               )}
//             </button>
//           </div>
//         </form>
//       </div>
//     </div>
//   )
// }
















// import { useState, useEffect } from 'react'
// import { FiX, FiLoader, FiSave, FiTrash2, FiUpload } from 'react-icons/fi'
// import toast from 'react-hot-toast'
// import axios from 'axios'

// export default function WebsiteEditorModal({ isOpen, onClose, onSubmit, website, categories, isSaving, mode }) {
//   const [formData, setFormData] = useState({
//     name: '',
//     description: '',
//     metaTitle: '',
//     metaDescription: '',
//     metaKeywords: '',
//     primaryColor: '#3B82F6',
//     secondaryColor: '#1F2937',
//     accentColor: '#F97316',
//     primaryFont: 'Inter',
//     secondaryFont: 'Inter'
//   })
//   const [logoFile, setLogoFile] = useState(null)
//   const [logoPreview, setLogoPreview] = useState(null)
//   const [uploadingLogo, setUploadingLogo] = useState(false)

//   useEffect(() => {
//     if (website && isOpen) {
//       setFormData({
//         name: website.name || '',
//         description: website.description || '',
//         metaTitle: website.seo?.metaTitle || '',
//         metaDescription: website.seo?.metaDescription || '',
//         metaKeywords: website.seo?.metaKeywords || '',
//         primaryColor: website.colors?.primary || '#3B82F6',
//         secondaryColor: website.colors?.secondary || '#1F2937',
//         accentColor: website.colors?.accent || '#F97316',
//         primaryFont: website.fonts?.primary || 'Inter',
//         secondaryFont: website.fonts?.secondary || 'Inter'
//       })
//       // Don't show any saved logo preview in the modal
//       // Only local file preview (before upload) will be shown
//       setLogoPreview(null)
//       setLogoFile(null)
//     }
//   }, [website, isOpen])

//   const handleInputChange = (e) => {
//     const { name, value } = e.target
//     setFormData(prev => ({
//       ...prev,
//       [name]: value
//     }))
//   }

//   const handleColorChange = (colorField, value) => {
//     setFormData(prev => ({
//       ...prev,
//       [colorField]: value
//     }))
//   }

//   const handleLogoChange = (e) => {
//     const file = e.target.files?.[0]
//     if (!file) return

//     if (!file.type.startsWith('image/')) {
//       toast.error('Please select an image file')
//       return
//     }

//     if (file.size > 5 * 1024 * 1024) {
//       toast.error('File size must be less than 5MB')
//       return
//     }

//     setLogoFile(file)
//     const reader = new FileReader()
//     reader.onload = (evt) => {
//       setLogoPreview(evt.target?.result)
//     }
//     reader.readAsDataURL(file)
//   }

//   const handleUploadLogo = async () => {
//     if (!logoFile || !website?._id) {
//       toast.error('No logo file selected')
//       return
//     }

//     try {
//       setUploadingLogo(true)
//       const formDataUpload = new FormData()
//       formDataUpload.append('logo', logoFile)

//       const response = await axios.post(`/websites/${website._id}/logo`, formDataUpload, {
//         headers: {
//           'Content-Type': 'multipart/form-data'
//         }
//       })

//       toast.success('✅ Logo uploaded and saved successfully')
      
//       setLogoFile(null) // Clear file input after successful upload
//       setLogoPreview(null) // Clear preview from modal - logo now on public site
      
//       // Logo is already saved on server — don't trigger onSubmit yet
//       // User should click "Save Website" to persist other form changes if needed
//     } catch (error) {
//       console.error('[Logo Upload] Error:', error)
//       toast.error(error.response?.data?.message || 'Failed to upload logo')
//     } finally {
//       setUploadingLogo(false)
//     }
//   }

//   const handleDeleteLogo = async () => {
//     if (!website?._id) return

//     if (!window.confirm('Are you sure you want to delete the logo?')) return

//     try {
//       setUploadingLogo(true)
//       await axios.delete(`/websites/${website._id}/logo`)
//       toast.success('Logo deleted successfully')
//       setLogoPreview(null)
//       setLogoFile(null)
//       // Logo is already deleted on server — don't trigger onSubmit yet
//       // User should click "Save Website" to persist other form changes if needed
//     } catch (error) {
//       console.error('[Logo Delete] Error:', error)
//       toast.error(error.response?.data?.message || 'Failed to delete logo')
//     } finally {
//       setUploadingLogo(false)
//     }
//   }

//   const handleSubmit = (e) => {
//     e.preventDefault()
    
//     if (!formData.name.trim()) {
//       toast.error('Website name is required')
//       return
//     }

//     if (!website || !website._id) {
//       toast.error('Invalid website - please select a website to edit')
//       return
//     }

//     const submitData = {
//       name: formData.name.trim(),
//       description: formData.description,
//       seo: {
//         metaTitle: formData.metaTitle,
//         metaDescription: formData.metaDescription,
//         metaKeywords: formData.metaKeywords
//       },
//       colors: {
//         primary: formData.primaryColor,
//         secondary: formData.secondaryColor,
//         accent: formData.accentColor
//       },
//       fonts: {
//         primary: formData.primaryFont,
//         secondary: formData.secondaryFont
//       }
//     }

//     onSubmit(submitData)
//   }

//   if (!isOpen) return null

//   return (
//     <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
//       <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
//         {/* Header */}
//         <div className="flex justify-between items-center p-6 border-b border-gray-200 sticky top-0 bg-white">
//           <h2 className="text-2xl font-bold text-gray-900">Edit Website</h2>
//           <button
//             onClick={onClose}
//             disabled={isSaving}
//             className="p-1 hover:bg-gray-100 rounded-lg transition-colors disabled:opacity-50"
//           >
//             <FiX className="w-6 h-6" />
//           </button>
//         </div>

//         {/* Content */}
//         <form onSubmit={handleSubmit} className="p-6 space-y-6">
//           {/* Basic Information */}
//           <div className="space-y-4">
//             <h3 className="text-lg font-semibold text-gray-900">Basic Information</h3>
            
//             <div>
//               <label className="block text-sm font-medium text-gray-700 mb-2">
//                 Website Name *
//               </label>
//               <input
//                 type="text"
//                 name="name"
//                 value={formData.name}
//                 onChange={handleInputChange}
//                 placeholder="Enter website name"
//                 className="input-field w-full"
//                 required
//               />
//             </div>

//             <div>
//               <label className="block text-sm font-medium text-gray-700 mb-2">
//                 Description
//               </label>
//               <textarea
//                 name="description"
//                 value={formData.description}
//                 onChange={handleInputChange}
//                 placeholder="Brief description of your website"
//                 rows="3"
//                 className="input-field w-full resize-none"
//               />
//             </div>

//             {/* Website Logo */}
//             <div className="mt-6 p-4 bg-gray-50 rounded-lg border border-gray-200">
//               <label className="block text-sm font-medium text-gray-700 mb-3">
//                 Website Logo
//               </label>
//               <p className="text-xs text-gray-600 mb-3">
//                 Upload a logo to replace the website name on your public site. Recommended: square image (PNG, JPG, GIF, WebP, or SVG). Max 5MB.
//               </p>

//               {/* Logo Preview */}
//               {logoPreview && (
//                 <div className="mb-4">
//                   <div className="relative inline-block">
//                     <img
//                       src={logoPreview}
//                       alt="Logo preview"
//                       className="max-w-xs max-h-24 rounded-lg border border-gray-300"
//                     />
//                   </div>
//                 </div>
//               )}

//               {/* File Input */}
//               <div className="flex gap-2">
//                 <label className="flex-1">
//                   <input
//                     type="file"
//                     accept="image/*"
//                     onChange={handleLogoChange}
//                     disabled={uploadingLogo}
//                     className="hidden"
//                   />
//                   <span className="inline-flex items-center gap-2 px-4 py-2 bg-blue-600 text-white hover:bg-blue-700 disabled:bg-gray-400 rounded-lg font-medium transition-colors cursor-pointer w-full justify-center">
//                     <FiUpload className="w-4 h-4" />
//                     Choose File
//                   </span>
//                 </label>

//                 {logoFile && !uploadingLogo && (
//                   <button
//                     type="button"
//                     onClick={handleUploadLogo}
//                     disabled={uploadingLogo}
//                     className="px-4 py-2 bg-green-600 text-white hover:bg-green-700 disabled:bg-gray-400 rounded-lg font-medium transition-colors flex items-center gap-2"
//                   >
//                     Upload
//                   </button>
//                 )}

//                 {logoPreview && (
//                   <button
//                     type="button"
//                     onClick={handleDeleteLogo}
//                     disabled={uploadingLogo}
//                     className="px-4 py-2 bg-red-600 text-white hover:bg-red-700 disabled:bg-gray-400 rounded-lg font-medium transition-colors flex items-center gap-2"
//                   >
//                     <FiTrash2 className="w-4 h-4" />
//                     Remove
//                   </button>
//                 )}

//                 {uploadingLogo && (
//                   <button
//                     type="button"
//                     disabled
//                     className="px-4 py-2 bg-gray-400 text-white rounded-lg font-medium flex items-center gap-2"
//                   >
//                     <FiLoader className="w-4 h-4 animate-spin" />
//                     Uploading...
//                   </button>
//                 )}
//               </div>

//               {logoFile && (
//                 <p className="text-xs text-gray-600 mt-2">
//                   Selected: {logoFile.name}
//                 </p>
//               )}
//             </div>
//           </div>

//           {/* SEO Settings */}
//           <div className="space-y-4 pt-6 border-t border-gray-200">
//             <h3 className="text-lg font-semibold text-gray-900">SEO Settings</h3>
            
//             <div>
//               <label className="block text-sm font-medium text-gray-700 mb-2">
//                 Meta Title
//               </label>
//               <input
//                 type="text"
//                 name="metaTitle"
//                 value={formData.metaTitle}
//                 onChange={handleInputChange}
//                 placeholder="Enter SEO title (60 characters)"
//                 maxLength="60"
//                 className="input-field w-full"
//               />
//               <p className="text-xs text-gray-500 mt-1">{formData.metaTitle.length}/60</p>
//             </div>

//             <div>
//               <label className="block text-sm font-medium text-gray-700 mb-2">
//                 Meta Description
//               </label>
//               <textarea
//                 name="metaDescription"
//                 value={formData.metaDescription}
//                 onChange={handleInputChange}
//                 placeholder="Enter SEO description (160 characters)"
//                 maxLength="160"
//                 rows="2"
//                 className="input-field w-full resize-none"
//               />
//               <p className="text-xs text-gray-500 mt-1">{formData.metaDescription.length}/160</p>
//             </div>

//             <div>
//               <label className="block text-sm font-medium text-gray-700 mb-2">
//                 Keywords
//               </label>
//               <input
//                 type="text"
//                 name="metaKeywords"
//                 value={formData.metaKeywords}
//                 onChange={handleInputChange}
//                 placeholder="Comma-separated keywords"
//                 className="input-field w-full"
//               />
//             </div>
//           </div>

//           {/* Design Settings */}
//           <div className="space-y-4 pt-6 border-t border-gray-200">
//             <h3 className="text-lg font-semibold text-gray-900">Design Settings</h3>

//             {/* Colors */}
//             <div className="space-y-4">
//               <p className="text-sm text-gray-600">Colors</p>
//               <div className="grid grid-cols-3 gap-4">
//                 <div>
//                   <label className="block text-xs font-medium text-gray-700 mb-2">
//                     Primary Color
//                   </label>
//                   <div className="flex items-center gap-2">
//                     <input
//                       type="color"
//                       value={formData.primaryColor}
//                       onChange={(e) => handleColorChange('primaryColor', e.target.value)}
//                       className="w-12 h-10 rounded cursor-pointer border border-gray-300"
//                     />
//                     <input
//                       type="text"
//                       value={formData.primaryColor}
//                       onChange={(e) => handleColorChange('primaryColor', e.target.value)}
//                       className="input-field flex-1 font-mono text-sm"
//                       maxLength="7"
//                     />
//                   </div>
//                 </div>

//                 <div>
//                   <label className="block text-xs font-medium text-gray-700 mb-2">
//                     Secondary Color
//                   </label>
//                   <div className="flex items-center gap-2">
//                     <input
//                       type="color"
//                       value={formData.secondaryColor}
//                       onChange={(e) => handleColorChange('secondaryColor', e.target.value)}
//                       className="w-12 h-10 rounded cursor-pointer border border-gray-300"
//                     />
//                     <input
//                       type="text"
//                       value={formData.secondaryColor}
//                       onChange={(e) => handleColorChange('secondaryColor', e.target.value)}
//                       className="input-field flex-1 font-mono text-sm"
//                       maxLength="7"
//                     />
//                   </div>
//                 </div>

//                 <div>
//                   <label className="block text-xs font-medium text-gray-700 mb-2">
//                     Accent Color
//                   </label>
//                   <div className="flex items-center gap-2">
//                     <input
//                       type="color"
//                       value={formData.accentColor}
//                       onChange={(e) => handleColorChange('accentColor', e.target.value)}
//                       className="w-12 h-10 rounded cursor-pointer border border-gray-300"
//                     />
//                     <input
//                       type="text"
//                       value={formData.accentColor}
//                       onChange={(e) => handleColorChange('accentColor', e.target.value)}
//                       className="input-field flex-1 font-mono text-sm"
//                       maxLength="7"
//                     />
//                   </div>
//                 </div>
//               </div>
//             </div>

//             {/* Fonts */}
//             <div className="space-y-3">
//               <p className="text-sm text-gray-600">Fonts</p>
//               <div className="grid grid-cols-2 gap-4">
//                 <div>
//                   <label className="block text-xs font-medium text-gray-700 mb-2">
//                     Primary Font
//                   </label>
//                   <select
//                     name="primaryFont"
//                     value={formData.primaryFont}
//                     onChange={handleInputChange}
//                     className="input-field w-full"
//                   >
//                     <option value="Inter">Inter</option>
//                     <option value="Arial">Arial</option>
//                     <option value="Georgia">Georgia</option>
//                     <option value="Times New Roman">Times New Roman</option>
//                     <option value="Verdana">Verdana</option>
//                   </select>
//                 </div>
//                 <div>
//                   <label className="block text-xs font-medium text-gray-700 mb-2">
//                     Secondary Font
//                   </label>
//                   <select
//                     name="secondaryFont"
//                     value={formData.secondaryFont}
//                     onChange={handleInputChange}
//                     className="input-field w-full"
//                   >
//                     <option value="Inter">Inter</option>
//                     <option value="Arial">Arial</option>
//                     <option value="Georgia">Georgia</option>
//                     <option value="Times New Roman">Times New Roman</option>
//                     <option value="Verdana">Verdana</option>
//                   </select>
//                 </div>
//               </div>
//             </div>
//           </div>

//           {/* Action Buttons */}
//           <div className="flex gap-3 pt-6 border-t border-gray-200">
//             <button
//               type="button"
//               onClick={onClose}
//               disabled={isSaving}
//               className="flex-1 py-2 px-4 bg-gray-100 text-gray-700 hover:bg-gray-200 rounded-lg font-medium transition-colors disabled:opacity-50"
//             >
//               Cancel
//             </button>
//             <button
//               type="submit"
//               disabled={isSaving}
//               className="flex-1 py-2 px-4 bg-primary-600 text-white hover:bg-primary-700 disabled:bg-gray-400 rounded-lg font-medium transition-colors flex items-center justify-center gap-2 disabled:cursor-not-allowed"
//             >
//               {isSaving ? (
//                 <>
//                   <FiLoader className="w-4 h-4 animate-spin" /> Saving...
//                 </>
//               ) : (
//                 <>
//                   <FiSave className="w-4 h-4" /> Save Website
//                 </>
//               )}
//             </button>
//           </div>
//         </form>
//       </div>
//     </div>
//   )
// }




// import { useState, useEffect } from 'react'
// import { FiX, FiLoader, FiSave, FiTrash2, FiUpload } from 'react-icons/fi'
// import toast from 'react-hot-toast'
// import axios from 'axios'

// export default function WebsiteEditorModal({ isOpen, onClose, onSubmit, website, categories, isSaving, mode }) {
//   const [formData, setFormData] = useState({
//     name: '',
//     description: '',
//     metaTitle: '',
//     metaDescription: '',
//     metaKeywords: '',
//     primaryColor: '#3B82F6',
//     secondaryColor: '#1F2937',
//     accentColor: '#F97316',
//     primaryFont: 'Inter',
//     secondaryFont: 'Inter'
//   })
//   const [logoFile, setLogoFile] = useState(null)
//   const [logoPreview, setLogoPreview] = useState(null)
//   const [uploadingLogo, setUploadingLogo] = useState(false)

//   useEffect(() => {
//     if (website && isOpen) {
//       setFormData({
//         name: website.name || '',
//         description: website.description || '',
//         metaTitle: website.seo?.metaTitle || '',
//         metaDescription: website.seo?.metaDescription || '',
//         metaKeywords: website.seo?.metaKeywords || '',
//         primaryColor: website.colors?.primary || '#3B82F6',
//         secondaryColor: website.colors?.secondary || '#1F2937',
//         accentColor: website.colors?.accent || '#F97316',
//         primaryFont: website.fonts?.primary || 'Inter',
//         secondaryFont: website.fonts?.secondary || 'Inter'
//       })
//       // Only show logo preview if website already has a saved logo (after successful upload)
//       // Do NOT show a default preview on first open
//       if (website.logo?.url) {
//         const backendOrigin = axios.defaults.baseURL ? axios.defaults.baseURL.replace(/\/api\/?$/, '') : ''
//         const storedUrl = website.logo.url
//         const previewUrl = (/^https?:\/\//i.test(storedUrl) || storedUrl.startsWith('data:') || storedUrl.startsWith('blob:'))
//           ? storedUrl
//           : (backendOrigin ? `${backendOrigin}${storedUrl.startsWith('/') ? storedUrl : `/${storedUrl}`}` : storedUrl)
//         setLogoPreview(previewUrl)
//       } else {
//         setLogoPreview(null)
//       }
//       setLogoFile(null)
//     }
//   }, [website, isOpen])

//   const handleInputChange = (e) => {
//     const { name, value } = e.target
//     setFormData(prev => ({
//       ...prev,
//       [name]: value
//     }))
//   }

//   const handleColorChange = (colorField, value) => {
//     setFormData(prev => ({
//       ...prev,
//       [colorField]: value
//     }))
//   }

//   const handleLogoChange = (e) => {
//     const file = e.target.files?.[0]
//     if (!file) return

//     if (!file.type.startsWith('image/')) {
//       toast.error('Please select an image file')
//       return
//     }

//     if (file.size > 5 * 1024 * 1024) {
//       toast.error('File size must be less than 5MB')
//       return
//     }

//     setLogoFile(file)
//     const reader = new FileReader()
//     reader.onload = (evt) => {
//       setLogoPreview(evt.target?.result)
//     }
//     reader.readAsDataURL(file)
//   }

//   const handleUploadLogo = async () => {
//     if (!logoFile || !website?._id) {
//       toast.error('No logo file selected')
//       return
//     }

//     try {
//       setUploadingLogo(true)
//       const formDataUpload = new FormData()
//       formDataUpload.append('logo', logoFile)

//       const response = await axios.post(`/websites/${website._id}/logo`, formDataUpload, {
//         headers: {
//           'Content-Type': 'multipart/form-data'
//         }
//       })

//       toast.success('✅ Logo uploaded successfully')
      
//       // Update preview to the stored URL returned by server so dashboard reflects uploaded logo
//       const returnedWebsite = response.data?.website
//       const returnedUrl = returnedWebsite?.logo?.url
//       if (returnedUrl) {
//         const backendOrigin = axios.defaults.baseURL ? axios.defaults.baseURL.replace(/\/api\/?$/, '') : ''
//         const previewUrl = (/^https?:\/\//i.test(returnedUrl) || returnedUrl.startsWith('data:') || returnedUrl.startsWith('blob:'))
//           ? returnedUrl
//           : (backendOrigin ? `${backendOrigin}${returnedUrl.startsWith('/') ? returnedUrl : `/${returnedUrl}`}` : returnedUrl)
//         setLogoPreview(previewUrl)
//       }
      
//       setLogoFile(null) // Clear file input after successful upload
      
//       // Call onSubmit if provided to refresh website data in parent
//       if (onSubmit) {
//         onSubmit(formData)
//       }
//     } catch (error) {
//       console.error('[Logo Upload] Error:', error)
//       toast.error(error.response?.data?.message || 'Failed to upload logo')
//     } finally {
//       setUploadingLogo(false)
//     }
//   }

//   const handleDeleteLogo = async () => {
//     if (!website?._id) return

//     if (!window.confirm('Are you sure you want to delete the logo?')) return

//     try {
//       setUploadingLogo(true)
//       await axios.delete(`/websites/${website._id}/logo`)
//       toast.success('Logo deleted successfully')
//       setLogoPreview(null)
//       setLogoFile(null)
//       // Call onSubmit if provided to refresh website data
//       if (onSubmit) {
//         onSubmit(formData)
//       }
//     } catch (error) {
//       console.error('[Logo Delete] Error:', error)
//       toast.error(error.response?.data?.message || 'Failed to delete logo')
//     } finally {
//       setUploadingLogo(false)
//     }
//   }

//   const handleSubmit = (e) => {
//     e.preventDefault()
    
//     if (!formData.name.trim()) {
//       toast.error('Website name is required')
//       return
//     }

//     if (!website || !website._id) {
//       toast.error('Invalid website - please select a website to edit')
//       return
//     }

//     const submitData = {
//       name: formData.name.trim(),
//       description: formData.description,
//       seo: {
//         metaTitle: formData.metaTitle,
//         metaDescription: formData.metaDescription,
//         metaKeywords: formData.metaKeywords
//       },
//       colors: {
//         primary: formData.primaryColor,
//         secondary: formData.secondaryColor,
//         accent: formData.accentColor
//       },
//       fonts: {
//         primary: formData.primaryFont,
//         secondary: formData.secondaryFont
//       }
//     }

//     onSubmit(submitData)
//   }

//   if (!isOpen) return null

//   return (
//     <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
//       <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
//         {/* Header */}
//         <div className="flex justify-between items-center p-6 border-b border-gray-200 sticky top-0 bg-white">
//           <h2 className="text-2xl font-bold text-gray-900">Edit Website</h2>
//           <button
//             onClick={onClose}
//             disabled={isSaving}
//             className="p-1 hover:bg-gray-100 rounded-lg transition-colors disabled:opacity-50"
//           >
//             <FiX className="w-6 h-6" />
//           </button>
//         </div>

//         {/* Content */}
//         <form onSubmit={handleSubmit} className="p-6 space-y-6">
//           {/* Basic Information */}
//           <div className="space-y-4">
//             <h3 className="text-lg font-semibold text-gray-900">Basic Information</h3>
            
//             <div>
//               <label className="block text-sm font-medium text-gray-700 mb-2">
//                 Website Name *
//               </label>
//               <input
//                 type="text"
//                 name="name"
//                 value={formData.name}
//                 onChange={handleInputChange}
//                 placeholder="Enter website name"
//                 className="input-field w-full"
//                 required
//               />
//             </div>

//             <div>
//               <label className="block text-sm font-medium text-gray-700 mb-2">
//                 Description
//               </label>
//               <textarea
//                 name="description"
//                 value={formData.description}
//                 onChange={handleInputChange}
//                 placeholder="Brief description of your website"
//                 rows="3"
//                 className="input-field w-full resize-none"
//               />
//             </div>

//             {/* Website Logo */}
//             <div className="mt-6 p-4 bg-gray-50 rounded-lg border border-gray-200">
//               <label className="block text-sm font-medium text-gray-700 mb-3">
//                 Website Logo
//               </label>
//               <p className="text-xs text-gray-600 mb-3">
//                 Upload a logo to replace the website name on your public site. Recommended: square image (PNG, JPG, GIF, WebP, or SVG). Max 5MB.
//               </p>

//               {/* Logo Preview */}
//               {logoPreview && (
//                 <div className="mb-4">
//                   <div className="relative inline-block">
//                     <img
//                       src={logoPreview}
//                       alt="Logo preview"
//                       className="max-w-xs max-h-24 rounded-lg border border-gray-300"
//                     />
//                   </div>
//                 </div>
//               )}

//               {/* File Input */}
//               <div className="flex gap-2">
//                 <label className="flex-1">
//                   <input
//                     type="file"
//                     accept="image/*"
//                     onChange={handleLogoChange}
//                     disabled={uploadingLogo}
//                     className="hidden"
//                   />
//                   <span className="inline-flex items-center gap-2 px-4 py-2 bg-blue-600 text-white hover:bg-blue-700 disabled:bg-gray-400 rounded-lg font-medium transition-colors cursor-pointer w-full justify-center">
//                     <FiUpload className="w-4 h-4" />
//                     Choose File
//                   </span>
//                 </label>

//                 {logoFile && !uploadingLogo && (
//                   <button
//                     type="button"
//                     onClick={handleUploadLogo}
//                     disabled={uploadingLogo}
//                     className="px-4 py-2 bg-green-600 text-white hover:bg-green-700 disabled:bg-gray-400 rounded-lg font-medium transition-colors flex items-center gap-2"
//                   >
//                     Upload
//                   </button>
//                 )}

//                 {logoPreview && (
//                   <button
//                     type="button"
//                     onClick={handleDeleteLogo}
//                     disabled={uploadingLogo}
//                     className="px-4 py-2 bg-red-600 text-white hover:bg-red-700 disabled:bg-gray-400 rounded-lg font-medium transition-colors flex items-center gap-2"
//                   >
//                     <FiTrash2 className="w-4 h-4" />
//                     Remove
//                   </button>
//                 )}

//                 {uploadingLogo && (
//                   <button
//                     type="button"
//                     disabled
//                     className="px-4 py-2 bg-gray-400 text-white rounded-lg font-medium flex items-center gap-2"
//                   >
//                     <FiLoader className="w-4 h-4 animate-spin" />
//                     Uploading...
//                   </button>
//                 )}
//               </div>

//               {logoFile && (
//                 <p className="text-xs text-gray-600 mt-2">
//                   Selected: {logoFile.name}
//                 </p>
//               )}
//             </div>
//           </div>

//           {/* SEO Settings */}
//           <div className="space-y-4 pt-6 border-t border-gray-200">
//             <h3 className="text-lg font-semibold text-gray-900">SEO Settings</h3>
            
//             <div>
//               <label className="block text-sm font-medium text-gray-700 mb-2">
//                 Meta Title
//               </label>
//               <input
//                 type="text"
//                 name="metaTitle"
//                 value={formData.metaTitle}
//                 onChange={handleInputChange}
//                 placeholder="Enter SEO title (60 characters)"
//                 maxLength="60"
//                 className="input-field w-full"
//               />
//               <p className="text-xs text-gray-500 mt-1">{formData.metaTitle.length}/60</p>
//             </div>

//             <div>
//               <label className="block text-sm font-medium text-gray-700 mb-2">
//                 Meta Description
//               </label>
//               <textarea
//                 name="metaDescription"
//                 value={formData.metaDescription}
//                 onChange={handleInputChange}
//                 placeholder="Enter SEO description (160 characters)"
//                 maxLength="160"
//                 rows="2"
//                 className="input-field w-full resize-none"
//               />
//               <p className="text-xs text-gray-500 mt-1">{formData.metaDescription.length}/160</p>
//             </div>

//             <div>
//               <label className="block text-sm font-medium text-gray-700 mb-2">
//                 Keywords
//               </label>
//               <input
//                 type="text"
//                 name="metaKeywords"
//                 value={formData.metaKeywords}
//                 onChange={handleInputChange}
//                 placeholder="Comma-separated keywords"
//                 className="input-field w-full"
//               />
//             </div>
//           </div>

//           {/* Design Settings */}
//           <div className="space-y-4 pt-6 border-t border-gray-200">
//             <h3 className="text-lg font-semibold text-gray-900">Design Settings</h3>

//             {/* Colors */}
//             <div className="space-y-4">
//               <p className="text-sm text-gray-600">Colors</p>
//               <div className="grid grid-cols-3 gap-4">
//                 <div>
//                   <label className="block text-xs font-medium text-gray-700 mb-2">
//                     Primary Color
//                   </label>
//                   <div className="flex items-center gap-2">
//                     <input
//                       type="color"
//                       value={formData.primaryColor}
//                       onChange={(e) => handleColorChange('primaryColor', e.target.value)}
//                       className="w-12 h-10 rounded cursor-pointer border border-gray-300"
//                     />
//                     <input
//                       type="text"
//                       value={formData.primaryColor}
//                       onChange={(e) => handleColorChange('primaryColor', e.target.value)}
//                       className="input-field flex-1 font-mono text-sm"
//                       maxLength="7"
//                     />
//                   </div>
//                 </div>

//                 <div>
//                   <label className="block text-xs font-medium text-gray-700 mb-2">
//                     Secondary Color
//                   </label>
//                   <div className="flex items-center gap-2">
//                     <input
//                       type="color"
//                       value={formData.secondaryColor}
//                       onChange={(e) => handleColorChange('secondaryColor', e.target.value)}
//                       className="w-12 h-10 rounded cursor-pointer border border-gray-300"
//                     />
//                     <input
//                       type="text"
//                       value={formData.secondaryColor}
//                       onChange={(e) => handleColorChange('secondaryColor', e.target.value)}
//                       className="input-field flex-1 font-mono text-sm"
//                       maxLength="7"
//                     />
//                   </div>
//                 </div>

//                 <div>
//                   <label className="block text-xs font-medium text-gray-700 mb-2">
//                     Accent Color
//                   </label>
//                   <div className="flex items-center gap-2">
//                     <input
//                       type="color"
//                       value={formData.accentColor}
//                       onChange={(e) => handleColorChange('accentColor', e.target.value)}
//                       className="w-12 h-10 rounded cursor-pointer border border-gray-300"
//                     />
//                     <input
//                       type="text"
//                       value={formData.accentColor}
//                       onChange={(e) => handleColorChange('accentColor', e.target.value)}
//                       className="input-field flex-1 font-mono text-sm"
//                       maxLength="7"
//                     />
//                   </div>
//                 </div>
//               </div>
//             </div>

//             {/* Fonts */}
//             <div className="space-y-3">
//               <p className="text-sm text-gray-600">Fonts</p>
//               <div className="grid grid-cols-2 gap-4">
//                 <div>
//                   <label className="block text-xs font-medium text-gray-700 mb-2">
//                     Primary Font
//                   </label>
//                   <select
//                     name="primaryFont"
//                     value={formData.primaryFont}
//                     onChange={handleInputChange}
//                     className="input-field w-full"
//                   >
//                     <option value="Inter">Inter</option>
//                     <option value="Arial">Arial</option>
//                     <option value="Georgia">Georgia</option>
//                     <option value="Times New Roman">Times New Roman</option>
//                     <option value="Verdana">Verdana</option>
//                   </select>
//                 </div>
//                 <div>
//                   <label className="block text-xs font-medium text-gray-700 mb-2">
//                     Secondary Font
//                   </label>
//                   <select
//                     name="secondaryFont"
//                     value={formData.secondaryFont}
//                     onChange={handleInputChange}
//                     className="input-field w-full"
//                   >
//                     <option value="Inter">Inter</option>
//                     <option value="Arial">Arial</option>
//                     <option value="Georgia">Georgia</option>
//                     <option value="Times New Roman">Times New Roman</option>
//                     <option value="Verdana">Verdana</option>
//                   </select>
//                 </div>
//               </div>
//             </div>
//           </div>

//           {/* Action Buttons */}
//           <div className="flex gap-3 pt-6 border-t border-gray-200">
//             <button
//               type="button"
//               onClick={onClose}
//               disabled={isSaving}
//               className="flex-1 py-2 px-4 bg-gray-100 text-gray-700 hover:bg-gray-200 rounded-lg font-medium transition-colors disabled:opacity-50"
//             >
//               Cancel
//             </button>
//             <button
//               type="submit"
//               disabled={isSaving}
//               className="flex-1 py-2 px-4 bg-primary-600 text-white hover:bg-primary-700 disabled:bg-gray-400 rounded-lg font-medium transition-colors flex items-center justify-center gap-2 disabled:cursor-not-allowed"
//             >
//               {isSaving ? (
//                 <>
//                   <FiLoader className="w-4 h-4 animate-spin" /> Saving...
//                 </>
//               ) : (
//                 <>
//                   <FiSave className="w-4 h-4" /> Save Website
//                 </>
//               )}
//             </button>
//           </div>
//         </form>
//       </div>
//     </div>
//   )
// }





// import { useState, useEffect } from 'react'
// import { FiX, FiLoader, FiSave, FiTrash2, FiUpload } from 'react-icons/fi'
// import toast from 'react-hot-toast'
// import axios from 'axios'

// export default function WebsiteEditorModal({ isOpen, onClose, onSubmit, website, categories, isSaving, mode }) {
//   const [formData, setFormData] = useState({
//     name: '',
//     description: '',
//     metaTitle: '',
//     metaDescription: '',
//     metaKeywords: '',
//     primaryColor: '#3B82F6',
//     secondaryColor: '#1F2937',
//     accentColor: '#F97316',
//     primaryFont: 'Inter',
//     secondaryFont: 'Inter'
//   })
//   const [logoFile, setLogoFile] = useState(null)
//   const [logoPreview, setLogoPreview] = useState(null)
//   const [uploadingLogo, setUploadingLogo] = useState(false)

//   useEffect(() => {
//     if (website && isOpen) {
//       setFormData({
//         name: website.name || '',
//         description: website.description || '',
//         metaTitle: website.seo?.metaTitle || '',
//         metaDescription: website.seo?.metaDescription || '',
//         metaKeywords: website.seo?.metaKeywords || '',
//         primaryColor: website.colors?.primary || '#3B82F6',
//         secondaryColor: website.colors?.secondary || '#1F2937',
//         accentColor: website.colors?.accent || '#F97316',
//         primaryFont: website.fonts?.primary || 'Inter',
//         secondaryFont: website.fonts?.secondary || 'Inter'
//       })
//       setLogoPreview(website.logo?.url || null)
//       setLogoFile(null)
//     }
//   }, [website, isOpen])

//   const handleInputChange = (e) => {
//     const { name, value } = e.target
//     setFormData(prev => ({
//       ...prev,
//       [name]: value
//     }))
//   }

//   const handleColorChange = (colorField, value) => {
//     setFormData(prev => ({
//       ...prev,
//       [colorField]: value
//     }))
//   }

//   const handleLogoChange = (e) => {
//     const file = e.target.files?.[0]
//     if (!file) return

//     if (!file.type.startsWith('image/')) {
//       toast.error('Please select an image file')
//       return
//     }

//     if (file.size > 5 * 1024 * 1024) {
//       toast.error('File size must be less than 5MB')
//       return
//     }

//     setLogoFile(file)
//     const reader = new FileReader()
//     reader.onload = (evt) => {
//       setLogoPreview(evt.target?.result)
//     }
//     reader.readAsDataURL(file)
//   }

//   const handleUploadLogo = async () => {
//     if (!logoFile || !website?._id) {
//       toast.error('No logo file selected')
//       return
//     }

//     try {
//       setUploadingLogo(true)
//       const formDataUpload = new FormData()
//       formDataUpload.append('logo', logoFile)

//       const response = await axios.post(`/websites/${website._id}/logo`, formDataUpload, {
//         headers: {
//           'Content-Type': 'multipart/form-data'
//         }
//       })

//       toast.success('✅ Logo uploaded successfully')
//       setLogoFile(null)
//       // Update preview to the stored URL returned by server so dashboard reflects uploaded logo
//       const returnedWebsite = response.data?.website
//       const returnedUrl = returnedWebsite?.logo?.url
//       if (returnedUrl) {
//         const backendOrigin = axios.defaults.baseURL ? axios.defaults.baseURL.replace(/\/api\/?$/, '') : ''
//         const previewUrl = (/^https?:\/\//i.test(returnedUrl) || returnedUrl.startsWith('data:') || returnedUrl.startsWith('blob:'))
//           ? returnedUrl
//           : (backendOrigin ? `${backendOrigin}${returnedUrl.startsWith('/') ? returnedUrl : `/${returnedUrl}`}` : returnedUrl)
//         setLogoPreview(previewUrl)
//       }
//       // Call onSubmit if provided to refresh website data
//       if (onSubmit) {
//         onSubmit(formData)
//       }
//     } catch (error) {
//       console.error('[Logo Upload] Error:', error)
//       toast.error(error.response?.data?.message || 'Failed to upload logo')
//     } finally {
//       setUploadingLogo(false)
//     }
//   }

//   const handleDeleteLogo = async () => {
//     if (!website?._id) return

//     if (!window.confirm('Are you sure you want to delete the logo?')) return

//     try {
//       setUploadingLogo(true)
//       await axios.delete(`/websites/${website._id}/logo`)
//       toast.success('Logo deleted successfully')
//       setLogoPreview(null)
//       setLogoFile(null)
//       // Call onSubmit if provided to refresh website data
//       if (onSubmit) {
//         onSubmit(formData)
//       }
//     } catch (error) {
//       console.error('[Logo Delete] Error:', error)
//       toast.error(error.response?.data?.message || 'Failed to delete logo')
//     } finally {
//       setUploadingLogo(false)
//     }
//   }

//   const handleSubmit = (e) => {
//     e.preventDefault()
    
//     if (!formData.name.trim()) {
//       toast.error('Website name is required')
//       return
//     }

//     if (!website || !website._id) {
//       toast.error('Invalid website - please select a website to edit')
//       return
//     }

//     const submitData = {
//       name: formData.name.trim(),
//       description: formData.description,
//       seo: {
//         metaTitle: formData.metaTitle,
//         metaDescription: formData.metaDescription,
//         metaKeywords: formData.metaKeywords
//       },
//       colors: {
//         primary: formData.primaryColor,
//         secondary: formData.secondaryColor,
//         accent: formData.accentColor
//       },
//       fonts: {
//         primary: formData.primaryFont,
//         secondary: formData.secondaryFont
//       }
//     }

//     onSubmit(submitData)
//   }

//   if (!isOpen) return null

//   return (
//     <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
//       <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
//         {/* Header */}
//         <div className="flex justify-between items-center p-6 border-b border-gray-200 sticky top-0 bg-white">
//           <h2 className="text-2xl font-bold text-gray-900">Edit Website</h2>
//           <button
//             onClick={onClose}
//             disabled={isSaving}
//             className="p-1 hover:bg-gray-100 rounded-lg transition-colors disabled:opacity-50"
//           >
//             <FiX className="w-6 h-6" />
//           </button>
//         </div>

//         {/* Content */}
//         <form onSubmit={handleSubmit} className="p-6 space-y-6">
//           {/* Basic Information */}
//           <div className="space-y-4">
//             <h3 className="text-lg font-semibold text-gray-900">Basic Information</h3>
            
//             <div>
//               <label className="block text-sm font-medium text-gray-700 mb-2">
//                 Website Name *
//               </label>
//               <input
//                 type="text"
//                 name="name"
//                 value={formData.name}
//                 onChange={handleInputChange}
//                 placeholder="Enter website name"
//                 className="input-field w-full"
//                 required
//               />
//             </div>

//             <div>
//               <label className="block text-sm font-medium text-gray-700 mb-2">
//                 Description
//               </label>
//               <textarea
//                 name="description"
//                 value={formData.description}
//                 onChange={handleInputChange}
//                 placeholder="Brief description of your website"
//                 rows="3"
//                 className="input-field w-full resize-none"
//               />
//             </div>

//             {/* Website Logo */}
//             <div className="mt-6 p-4 bg-gray-50 rounded-lg border border-gray-200">
//               <label className="block text-sm font-medium text-gray-700 mb-3">
//                 Website Logo
//               </label>
//               <p className="text-xs text-gray-600 mb-3">
//                 Upload a logo to replace the website name on your public site. Recommended: square image (PNG, JPG, GIF, WebP, or SVG). Max 5MB.
//               </p>

//               {/* Logo Preview */}
//               {logoPreview && (
//                 <div className="mb-4">
//                   <div className="relative inline-block">
//                     <img
//                       src={logoPreview}
//                       alt="Logo preview"
//                       className="max-w-xs max-h-24 rounded-lg border border-gray-300"
//                     />
//                   </div>
//                 </div>
//               )}

//               {/* File Input */}
//               <div className="flex gap-2">
//                 <label className="flex-1">
//                   <input
//                     type="file"
//                     accept="image/*"
//                     onChange={handleLogoChange}
//                     disabled={uploadingLogo}
//                     className="hidden"
//                   />
//                   <span className="inline-flex items-center gap-2 px-4 py-2 bg-blue-600 text-white hover:bg-blue-700 disabled:bg-gray-400 rounded-lg font-medium transition-colors cursor-pointer w-full justify-center">
//                     <FiUpload className="w-4 h-4" />
//                     Choose File
//                   </span>
//                 </label>

//                 {logoFile && !uploadingLogo && (
//                   <button
//                     type="button"
//                     onClick={handleUploadLogo}
//                     disabled={uploadingLogo}
//                     className="px-4 py-2 bg-green-600 text-white hover:bg-green-700 disabled:bg-gray-400 rounded-lg font-medium transition-colors flex items-center gap-2"
//                   >
//                     Upload
//                   </button>
//                 )}

//                 {logoPreview && (
//                   <button
//                     type="button"
//                     onClick={handleDeleteLogo}
//                     disabled={uploadingLogo}
//                     className="px-4 py-2 bg-red-600 text-white hover:bg-red-700 disabled:bg-gray-400 rounded-lg font-medium transition-colors flex items-center gap-2"
//                   >
//                     <FiTrash2 className="w-4 h-4" />
//                     Remove
//                   </button>
//                 )}

//                 {uploadingLogo && (
//                   <button
//                     type="button"
//                     disabled
//                     className="px-4 py-2 bg-gray-400 text-white rounded-lg font-medium flex items-center gap-2"
//                   >
//                     <FiLoader className="w-4 h-4 animate-spin" />
//                     Uploading...
//                   </button>
//                 )}
//               </div>

//               {logoFile && (
//                 <p className="text-xs text-gray-600 mt-2">
//                   Selected: {logoFile.name}
//                 </p>
//               )}
//             </div>
//           </div>

//           {/* SEO Settings */}
//           <div className="space-y-4 pt-6 border-t border-gray-200">
//             <h3 className="text-lg font-semibold text-gray-900">SEO Settings</h3>
            
//             <div>
//               <label className="block text-sm font-medium text-gray-700 mb-2">
//                 Meta Title
//               </label>
//               <input
//                 type="text"
//                 name="metaTitle"
//                 value={formData.metaTitle}
//                 onChange={handleInputChange}
//                 placeholder="Enter SEO title (60 characters)"
//                 maxLength="60"
//                 className="input-field w-full"
//               />
//               <p className="text-xs text-gray-500 mt-1">{formData.metaTitle.length}/60</p>
//             </div>

//             <div>
//               <label className="block text-sm font-medium text-gray-700 mb-2">
//                 Meta Description
//               </label>
//               <textarea
//                 name="metaDescription"
//                 value={formData.metaDescription}
//                 onChange={handleInputChange}
//                 placeholder="Enter SEO description (160 characters)"
//                 maxLength="160"
//                 rows="2"
//                 className="input-field w-full resize-none"
//               />
//               <p className="text-xs text-gray-500 mt-1">{formData.metaDescription.length}/160</p>
//             </div>

//             <div>
//               <label className="block text-sm font-medium text-gray-700 mb-2">
//                 Keywords
//               </label>
//               <input
//                 type="text"
//                 name="metaKeywords"
//                 value={formData.metaKeywords}
//                 onChange={handleInputChange}
//                 placeholder="Comma-separated keywords"
//                 className="input-field w-full"
//               />
//             </div>
//           </div>

//           {/* Design Settings */}
//           <div className="space-y-4 pt-6 border-t border-gray-200">
//             <h3 className="text-lg font-semibold text-gray-900">Design Settings</h3>

//             {/* Colors */}
//             <div className="space-y-4">
//               <p className="text-sm text-gray-600">Colors</p>
//               <div className="grid grid-cols-3 gap-4">
//                 <div>
//                   <label className="block text-xs font-medium text-gray-700 mb-2">
//                     Primary Color
//                   </label>
//                   <div className="flex items-center gap-2">
//                     <input
//                       type="color"
//                       value={formData.primaryColor}
//                       onChange={(e) => handleColorChange('primaryColor', e.target.value)}
//                       className="w-12 h-10 rounded cursor-pointer border border-gray-300"
//                     />
//                     <input
//                       type="text"
//                       value={formData.primaryColor}
//                       onChange={(e) => handleColorChange('primaryColor', e.target.value)}
//                       className="input-field flex-1 font-mono text-sm"
//                       maxLength="7"
//                     />
//                   </div>
//                 </div>

//                 <div>
//                   <label className="block text-xs font-medium text-gray-700 mb-2">
//                     Secondary Color
//                   </label>
//                   <div className="flex items-center gap-2">
//                     <input
//                       type="color"
//                       value={formData.secondaryColor}
//                       onChange={(e) => handleColorChange('secondaryColor', e.target.value)}
//                       className="w-12 h-10 rounded cursor-pointer border border-gray-300"
//                     />
//                     <input
//                       type="text"
//                       value={formData.secondaryColor}
//                       onChange={(e) => handleColorChange('secondaryColor', e.target.value)}
//                       className="input-field flex-1 font-mono text-sm"
//                       maxLength="7"
//                     />
//                   </div>
//                 </div>

//                 <div>
//                   <label className="block text-xs font-medium text-gray-700 mb-2">
//                     Accent Color
//                   </label>
//                   <div className="flex items-center gap-2">
//                     <input
//                       type="color"
//                       value={formData.accentColor}
//                       onChange={(e) => handleColorChange('accentColor', e.target.value)}
//                       className="w-12 h-10 rounded cursor-pointer border border-gray-300"
//                     />
//                     <input
//                       type="text"
//                       value={formData.accentColor}
//                       onChange={(e) => handleColorChange('accentColor', e.target.value)}
//                       className="input-field flex-1 font-mono text-sm"
//                       maxLength="7"
//                     />
//                   </div>
//                 </div>
//               </div>
//             </div>

//             {/* Fonts */}
//             <div className="space-y-3">
//               <p className="text-sm text-gray-600">Fonts</p>
//               <div className="grid grid-cols-2 gap-4">
//                 <div>
//                   <label className="block text-xs font-medium text-gray-700 mb-2">
//                     Primary Font
//                   </label>
//                   <select
//                     name="primaryFont"
//                     value={formData.primaryFont}
//                     onChange={handleInputChange}
//                     className="input-field w-full"
//                   >
//                     <option value="Inter">Inter</option>
//                     <option value="Arial">Arial</option>
//                     <option value="Georgia">Georgia</option>
//                     <option value="Times New Roman">Times New Roman</option>
//                     <option value="Verdana">Verdana</option>
//                   </select>
//                 </div>
//                 <div>
//                   <label className="block text-xs font-medium text-gray-700 mb-2">
//                     Secondary Font
//                   </label>
//                   <select
//                     name="secondaryFont"
//                     value={formData.secondaryFont}
//                     onChange={handleInputChange}
//                     className="input-field w-full"
//                   >
//                     <option value="Inter">Inter</option>
//                     <option value="Arial">Arial</option>
//                     <option value="Georgia">Georgia</option>
//                     <option value="Times New Roman">Times New Roman</option>
//                     <option value="Verdana">Verdana</option>
//                   </select>
//                 </div>
//               </div>
//             </div>
//           </div>

//           {/* Action Buttons */}
//           <div className="flex gap-3 pt-6 border-t border-gray-200">
//             <button
//               type="button"
//               onClick={onClose}
//               disabled={isSaving}
//               className="flex-1 py-2 px-4 bg-gray-100 text-gray-700 hover:bg-gray-200 rounded-lg font-medium transition-colors disabled:opacity-50"
//             >
//               Cancel
//             </button>
//             <button
//               type="submit"
//               disabled={isSaving}
//               className="flex-1 py-2 px-4 bg-primary-600 text-white hover:bg-primary-700 disabled:bg-gray-400 rounded-lg font-medium transition-colors flex items-center justify-center gap-2 disabled:cursor-not-allowed"
//             >
//               {isSaving ? (
//                 <>
//                   <FiLoader className="w-4 h-4 animate-spin" /> Saving...
//                 </>
//               ) : (
//                 <>
//                   <FiSave className="w-4 h-4" /> Save Website
//                 </>
//               )}
//             </button>
//           </div>
//         </form>
//       </div>
//     </div>
//   )
// }












// import { useState, useEffect } from 'react'
// import { FiX, FiLoader, FiSave, FiTrash2, FiUpload } from 'react-icons/fi'
// import toast from 'react-hot-toast'
// import axios from 'axios'

// export default function WebsiteEditorModal({ isOpen, onClose, onSubmit, website, categories, isSaving, mode }) {
//   const [formData, setFormData] = useState({
//     name: '',
//     description: '',
//     metaTitle: '',
//     metaDescription: '',
//     metaKeywords: '',
//     primaryColor: '#3B82F6',
//     secondaryColor: '#1F2937',
//     accentColor: '#F97316',
//     primaryFont: 'Inter',
//     secondaryFont: 'Inter'
//   })
//   const [logoFile, setLogoFile] = useState(null)
//   const [logoPreview, setLogoPreview] = useState(null)
//   const [uploadingLogo, setUploadingLogo] = useState(false)

//   useEffect(() => {
//     if (website && isOpen) {
//       setFormData({
//         name: website.name || '',
//         description: website.description || '',
//         metaTitle: website.seo?.metaTitle || '',
//         metaDescription: website.seo?.metaDescription || '',
//         metaKeywords: website.seo?.metaKeywords || '',
//         primaryColor: website.colors?.primary || '#3B82F6',
//         secondaryColor: website.colors?.secondary || '#1F2937',
//         accentColor: website.colors?.accent || '#F97316',
//         primaryFont: website.fonts?.primary || 'Inter',
//         secondaryFont: website.fonts?.secondary || 'Inter'
//       })
//       setLogoPreview(website.logo?.url || null)
//       setLogoFile(null)
//     }
//   }, [website, isOpen])

//   const handleInputChange = (e) => {
//     const { name, value } = e.target
//     setFormData(prev => ({
//       ...prev,
//       [name]: value
//     }))
//   }

//   const handleColorChange = (colorField, value) => {
//     setFormData(prev => ({
//       ...prev,
//       [colorField]: value
//     }))
//   }

//   const handleLogoChange = (e) => {
//     const file = e.target.files?.[0]
//     if (!file) return

//     if (!file.type.startsWith('image/')) {
//       toast.error('Please select an image file')
//       return
//     }

//     if (file.size > 5 * 1024 * 1024) {
//       toast.error('File size must be less than 5MB')
//       return
//     }

//     setLogoFile(file)
//     const reader = new FileReader()
//     reader.onload = (evt) => {
//       setLogoPreview(evt.target?.result)
//     }
//     reader.readAsDataURL(file)
//   }

//   const handleUploadLogo = async () => {
//     if (!logoFile || !website?._id) {
//       toast.error('No logo file selected')
//       return
//     }

//     try {
//       setUploadingLogo(true)
//       const formDataUpload = new FormData()
//       formDataUpload.append('logo', logoFile)

//       const response = await axios.post(`/websites/${website._id}/logo`, formDataUpload, {
//         headers: {
//           'Content-Type': 'multipart/form-data'
//         }
//       })

//       toast.success('✅ Logo uploaded successfully')
//       setLogoFile(null)
//       // Call onSubmit if provided to refresh website data
//       if (onSubmit) {
//         onSubmit(formData)
//       }
//     } catch (error) {
//       console.error('[Logo Upload] Error:', error)
//       toast.error(error.response?.data?.message || 'Failed to upload logo')
//     } finally {
//       setUploadingLogo(false)
//     }
//   }

//   const handleDeleteLogo = async () => {
//     if (!website?._id) return

//     if (!window.confirm('Are you sure you want to delete the logo?')) return

//     try {
//       setUploadingLogo(true)
//       await axios.delete(`/websites/${website._id}/logo`)
//       toast.success('Logo deleted successfully')
//       setLogoPreview(null)
//       setLogoFile(null)
//       // Call onSubmit if provided to refresh website data
//       if (onSubmit) {
//         onSubmit(formData)
//       }
//     } catch (error) {
//       console.error('[Logo Delete] Error:', error)
//       toast.error(error.response?.data?.message || 'Failed to delete logo')
//     } finally {
//       setUploadingLogo(false)
//     }
//   }

//   const handleSubmit = (e) => {
//     e.preventDefault()
    
//     if (!formData.name.trim()) {
//       toast.error('Website name is required')
//       return
//     }

//     if (!website || !website._id) {
//       toast.error('Invalid website - please select a website to edit')
//       return
//     }

//     const submitData = {
//       name: formData.name.trim(),
//       description: formData.description,
//       seo: {
//         metaTitle: formData.metaTitle,
//         metaDescription: formData.metaDescription,
//         metaKeywords: formData.metaKeywords
//       },
//       colors: {
//         primary: formData.primaryColor,
//         secondary: formData.secondaryColor,
//         accent: formData.accentColor
//       },
//       fonts: {
//         primary: formData.primaryFont,
//         secondary: formData.secondaryFont
//       }
//     }

//     onSubmit(submitData)
//   }

//   if (!isOpen) return null

//   return (
//     <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
//       <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
//         {/* Header */}
//         <div className="flex justify-between items-center p-6 border-b border-gray-200 sticky top-0 bg-white">
//           <h2 className="text-2xl font-bold text-gray-900">Edit Website</h2>
//           <button
//             onClick={onClose}
//             disabled={isSaving}
//             className="p-1 hover:bg-gray-100 rounded-lg transition-colors disabled:opacity-50"
//           >
//             <FiX className="w-6 h-6" />
//           </button>
//         </div>

//         {/* Content */}
//         <form onSubmit={handleSubmit} className="p-6 space-y-6">
//           {/* Basic Information */}
//           <div className="space-y-4">
//             <h3 className="text-lg font-semibold text-gray-900">Basic Information</h3>
            
//             <div>
//               <label className="block text-sm font-medium text-gray-700 mb-2">
//                 Website Name *
//               </label>
//               <input
//                 type="text"
//                 name="name"
//                 value={formData.name}
//                 onChange={handleInputChange}
//                 placeholder="Enter website name"
//                 className="input-field w-full"
//                 required
//               />
//             </div>

//             <div>
//               <label className="block text-sm font-medium text-gray-700 mb-2">
//                 Description
//               </label>
//               <textarea
//                 name="description"
//                 value={formData.description}
//                 onChange={handleInputChange}
//                 placeholder="Brief description of your website"
//                 rows="3"
//                 className="input-field w-full resize-none"
//               />
//             </div>

//             {/* Website Logo */}
//             <div className="mt-6 p-4 bg-gray-50 rounded-lg border border-gray-200">
//               <label className="block text-sm font-medium text-gray-700 mb-3">
//                 Website Logo
//               </label>
//               <p className="text-xs text-gray-600 mb-3">
//                 Upload a logo to replace the website name on your public site. Recommended: square image (PNG, JPG, GIF, WebP, or SVG). Max 5MB.
//               </p>

//               {/* Logo Preview */}
//               {logoPreview && (
//                 <div className="mb-4">
//                   <div className="relative inline-block">
//                     <img
//                       src={logoPreview}
//                       alt="Logo preview"
//                       className="max-w-xs max-h-24 rounded-lg border border-gray-300"
//                     />
//                   </div>
//                 </div>
//               )}

//               {/* File Input */}
//               <div className="flex gap-2">
//                 <label className="flex-1">
//                   <input
//                     type="file"
//                     accept="image/*"
//                     onChange={handleLogoChange}
//                     disabled={uploadingLogo}
//                     className="hidden"
//                   />
//                   <span className="inline-flex items-center gap-2 px-4 py-2 bg-blue-600 text-white hover:bg-blue-700 disabled:bg-gray-400 rounded-lg font-medium transition-colors cursor-pointer w-full justify-center">
//                     <FiUpload className="w-4 h-4" />
//                     Choose File
//                   </span>
//                 </label>

//                 {logoFile && !uploadingLogo && (
//                   <button
//                     type="button"
//                     onClick={handleUploadLogo}
//                     disabled={uploadingLogo}
//                     className="px-4 py-2 bg-green-600 text-white hover:bg-green-700 disabled:bg-gray-400 rounded-lg font-medium transition-colors flex items-center gap-2"
//                   >
//                     Upload
//                   </button>
//                 )}

//                 {logoPreview && (
//                   <button
//                     type="button"
//                     onClick={handleDeleteLogo}
//                     disabled={uploadingLogo}
//                     className="px-4 py-2 bg-red-600 text-white hover:bg-red-700 disabled:bg-gray-400 rounded-lg font-medium transition-colors flex items-center gap-2"
//                   >
//                     <FiTrash2 className="w-4 h-4" />
//                     Remove
//                   </button>
//                 )}

//                 {uploadingLogo && (
//                   <button
//                     type="button"
//                     disabled
//                     className="px-4 py-2 bg-gray-400 text-white rounded-lg font-medium flex items-center gap-2"
//                   >
//                     <FiLoader className="w-4 h-4 animate-spin" />
//                     Uploading...
//                   </button>
//                 )}
//               </div>

//               {logoFile && (
//                 <p className="text-xs text-gray-600 mt-2">
//                   Selected: {logoFile.name}
//                 </p>
//               )}
//             </div>
//           </div>

//           {/* SEO Settings */}
//           <div className="space-y-4 pt-6 border-t border-gray-200">
//             <h3 className="text-lg font-semibold text-gray-900">SEO Settings</h3>
            
//             <div>
//               <label className="block text-sm font-medium text-gray-700 mb-2">
//                 Meta Title
//               </label>
//               <input
//                 type="text"
//                 name="metaTitle"
//                 value={formData.metaTitle}
//                 onChange={handleInputChange}
//                 placeholder="Enter SEO title (60 characters)"
//                 maxLength="60"
//                 className="input-field w-full"
//               />
//               <p className="text-xs text-gray-500 mt-1">{formData.metaTitle.length}/60</p>
//             </div>

//             <div>
//               <label className="block text-sm font-medium text-gray-700 mb-2">
//                 Meta Description
//               </label>
//               <textarea
//                 name="metaDescription"
//                 value={formData.metaDescription}
//                 onChange={handleInputChange}
//                 placeholder="Enter SEO description (160 characters)"
//                 maxLength="160"
//                 rows="2"
//                 className="input-field w-full resize-none"
//               />
//               <p className="text-xs text-gray-500 mt-1">{formData.metaDescription.length}/160</p>
//             </div>

//             <div>
//               <label className="block text-sm font-medium text-gray-700 mb-2">
//                 Keywords
//               </label>
//               <input
//                 type="text"
//                 name="metaKeywords"
//                 value={formData.metaKeywords}
//                 onChange={handleInputChange}
//                 placeholder="Comma-separated keywords"
//                 className="input-field w-full"
//               />
//             </div>
//           </div>

//           {/* Design Settings */}
//           <div className="space-y-4 pt-6 border-t border-gray-200">
//             <h3 className="text-lg font-semibold text-gray-900">Design Settings</h3>

//             {/* Colors */}
//             <div className="space-y-4">
//               <p className="text-sm text-gray-600">Colors</p>
//               <div className="grid grid-cols-3 gap-4">
//                 <div>
//                   <label className="block text-xs font-medium text-gray-700 mb-2">
//                     Primary Color
//                   </label>
//                   <div className="flex items-center gap-2">
//                     <input
//                       type="color"
//                       value={formData.primaryColor}
//                       onChange={(e) => handleColorChange('primaryColor', e.target.value)}
//                       className="w-12 h-10 rounded cursor-pointer border border-gray-300"
//                     />
//                     <input
//                       type="text"
//                       value={formData.primaryColor}
//                       onChange={(e) => handleColorChange('primaryColor', e.target.value)}
//                       className="input-field flex-1 font-mono text-sm"
//                       maxLength="7"
//                     />
//                   </div>
//                 </div>

//                 <div>
//                   <label className="block text-xs font-medium text-gray-700 mb-2">
//                     Secondary Color
//                   </label>
//                   <div className="flex items-center gap-2">
//                     <input
//                       type="color"
//                       value={formData.secondaryColor}
//                       onChange={(e) => handleColorChange('secondaryColor', e.target.value)}
//                       className="w-12 h-10 rounded cursor-pointer border border-gray-300"
//                     />
//                     <input
//                       type="text"
//                       value={formData.secondaryColor}
//                       onChange={(e) => handleColorChange('secondaryColor', e.target.value)}
//                       className="input-field flex-1 font-mono text-sm"
//                       maxLength="7"
//                     />
//                   </div>
//                 </div>

//                 <div>
//                   <label className="block text-xs font-medium text-gray-700 mb-2">
//                     Accent Color
//                   </label>
//                   <div className="flex items-center gap-2">
//                     <input
//                       type="color"
//                       value={formData.accentColor}
//                       onChange={(e) => handleColorChange('accentColor', e.target.value)}
//                       className="w-12 h-10 rounded cursor-pointer border border-gray-300"
//                     />
//                     <input
//                       type="text"
//                       value={formData.accentColor}
//                       onChange={(e) => handleColorChange('accentColor', e.target.value)}
//                       className="input-field flex-1 font-mono text-sm"
//                       maxLength="7"
//                     />
//                   </div>
//                 </div>
//               </div>
//             </div>

//             {/* Fonts */}
//             <div className="space-y-3">
//               <p className="text-sm text-gray-600">Fonts</p>
//               <div className="grid grid-cols-2 gap-4">
//                 <div>
//                   <label className="block text-xs font-medium text-gray-700 mb-2">
//                     Primary Font
//                   </label>
//                   <select
//                     name="primaryFont"
//                     value={formData.primaryFont}
//                     onChange={handleInputChange}
//                     className="input-field w-full"
//                   >
//                     <option value="Inter">Inter</option>
//                     <option value="Arial">Arial</option>
//                     <option value="Georgia">Georgia</option>
//                     <option value="Times New Roman">Times New Roman</option>
//                     <option value="Verdana">Verdana</option>
//                   </select>
//                 </div>
//                 <div>
//                   <label className="block text-xs font-medium text-gray-700 mb-2">
//                     Secondary Font
//                   </label>
//                   <select
//                     name="secondaryFont"
//                     value={formData.secondaryFont}
//                     onChange={handleInputChange}
//                     className="input-field w-full"
//                   >
//                     <option value="Inter">Inter</option>
//                     <option value="Arial">Arial</option>
//                     <option value="Georgia">Georgia</option>
//                     <option value="Times New Roman">Times New Roman</option>
//                     <option value="Verdana">Verdana</option>
//                   </select>
//                 </div>
//               </div>
//             </div>
//           </div>

//           {/* Action Buttons */}
//           <div className="flex gap-3 pt-6 border-t border-gray-200">
//             <button
//               type="button"
//               onClick={onClose}
//               disabled={isSaving}
//               className="flex-1 py-2 px-4 bg-gray-100 text-gray-700 hover:bg-gray-200 rounded-lg font-medium transition-colors disabled:opacity-50"
//             >
//               Cancel
//             </button>
//             <button
//               type="submit"
//               disabled={isSaving}
//               className="flex-1 py-2 px-4 bg-primary-600 text-white hover:bg-primary-700 disabled:bg-gray-400 rounded-lg font-medium transition-colors flex items-center justify-center gap-2 disabled:cursor-not-allowed"
//             >
//               {isSaving ? (
//                 <>
//                   <FiLoader className="w-4 h-4 animate-spin" /> Saving...
//                 </>
//               ) : (
//                 <>
//                   <FiSave className="w-4 h-4" /> Save Website
//                 </>
//               )}
//             </button>
//           </div>
//         </form>
//       </div>
//     </div>
//   )
// }
